import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Row,
  Col,
  message,
  Statistic,
  DatePicker,
  Icon,
  Switch,
  Tag,
  Divider,
  Modal,
  Spin,
  Input,
  Checkbox,
  List,
  Alert,
  Tooltip
} from 'antd';
import moment from 'moment';
import { apiGet, apiPost } from './utils/apiService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchDailyFuelDataReport } from './utils/fuelService';
import { getUserData } from './utils/authService';
import { generateEmailHTML, generateEmailSubject, formatFiltersForEmail } from './utils/emailTemplate';
import { 
  fetchOrganizationUsers, 
  imageToBase64, 
  pdfBlobToBase64, 
  sendEmailWithAttachment,
  checkEmailServerHealth
} from './utils/emailService';

const { Option } = Select;

// Utility function to convert table data to CSV
const convertTableToCSV = (columns, dataSource, filename) => {
  // Extract headers from columns (exclude Action column)
  const headers = columns
    .filter(col => col.key !== 'action')
    .map(col => col.title || col.key || '')
    .filter(h => h);
  
  // Convert data rows
  const rows = dataSource.map((row, rowIndex) => {
    return columns
      .filter(col => col.key !== 'action')
      .map((col, colIndex) => {
        if (!col.dataIndex && !col.key && !col.render) return '';
        
        let value = '';
        
        // If there's a render function, try to extract the text value
        if (col.render && typeof col.render === 'function') {
          try {
            const dataIndex = col.dataIndex || col.key;
            const rawValue = dataIndex ? row[dataIndex] : row;
            const rendered = col.render(rawValue, row, rowIndex);
            
            // Extract text from rendered React elements or use raw value
            if (typeof rendered === 'string' || typeof rendered === 'number') {
              value = rendered;
            } else if (rendered && rendered.props && rendered.props.children) {
              // Try to extract text from React element (e.g., Tag components)
              const children = rendered.props.children;
              if (typeof children === 'string' || typeof children === 'number') {
                value = children;
              } else if (Array.isArray(children)) {
                value = children.map(c => {
                  if (typeof c === 'string' || typeof c === 'number') {
                    return c;
                  } else if (c && c.props && c.props.children) {
                    const nestedChildren = c.props.children;
                    if (typeof nestedChildren === 'string' || typeof nestedChildren === 'number') {
                      return nestedChildren;
                    } else if (Array.isArray(nestedChildren)) {
                      return nestedChildren.map(nc => typeof nc === 'string' || typeof nc === 'number' ? nc : '').join(' ');
                    }
                  }
                  return '';
                }).filter(v => v).join(' ');
              } else {
                value = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
              }
            } else {
              // Fallback to raw value
              const dataIndex = col.dataIndex || col.key;
              value = dataIndex ? row[dataIndex] : '';
            }
          } catch (e) {
            // If render fails, use raw value
            const dataIndex = col.dataIndex || col.key;
            value = dataIndex ? (row[dataIndex] !== null && row[dataIndex] !== undefined ? row[dataIndex] : '') : '';
          }
        } else {
          // No render function, use raw value
          const dataIndex = col.dataIndex || col.key;
          value = dataIndex ? row[dataIndex] : '';
        }
        
        // Format the value
        if (value === null || value === undefined || value === '') {
          value = '';
        } else if (typeof value === 'number') {
          value = value.toFixed(2);
        } else if (moment.isMoment(value)) {
          value = value.format('YYYY-MM-DD HH:mm:ss');
        } else if (typeof value === 'object') {
          value = JSON.stringify(value);
        } else {
          value = String(value);
        }
        
        // Escape CSV special characters
        if (String(value).includes(',') || String(value).includes('"') || String(value).includes('\n')) {
          value = `"${String(value).replace(/"/g, '""')}"`;
        }
        
        return value;
      });
  });
  
  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename || 'monthly_full_tank_report'}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Helper function to extract calculated reading from mileage
// Prefers calculatedReading from meterReadingRecord (cumulative, always increasing)
// Falls back to reading (physical meter reading, may reset on meter change)
const getCalculatedReading = (mileage) => {
  if (!mileage) return null;
  // Check for calculatedReading in meterReadingRecord first
  if (mileage.meterReadingRecord && 
      mileage.meterReadingRecord.calculatedReading !== null && 
      mileage.meterReadingRecord.calculatedReading !== undefined) {
    return mileage.meterReadingRecord.calculatedReading;
  }
  // Fall back to regular reading
  return mileage.reading;
};

// Helper function to get both readings for display purposes
const getMeterReadings = (mileage) => {
  if (!mileage) return { reading: null, calculatedReading: null };
  const reading = mileage.reading;
  const calculatedReading = mileage.meterReadingRecord?.calculatedReading ?? null;
  return { reading, calculatedReading };
};

// Helper to get the meter entity ID for a mileage reading.
// A meter replacement creates a new meter entity, so the ID differs before vs.
// after the change. This is the EXACT-IDENTIFIER signal used to detect a meter
// change between two consecutive full-tank events (see meter-change exclusion below).
// Returns null when the ID is absent — callers must treat null as "cannot confirm a
// change" (fail safe: never exclude on missing data).
const getMeterId = (mileage) => {
  if (!mileage) return null;
  return mileage.meter || mileage.meterReadingRecord?.meter || null;
};

// Detect whether a meter change occurred between the previous and current full-tank
// event for a given meter NAME (e.g. "HOURS"). The meter name is stable across a
// replacement; only the underlying meter entity ID changes. Returns true ONLY when
// both IDs are present and differ. Missing ID on either side => false (no guessing).
const detectMeterChange = (currentMileage, previousMileage) => {
  const cid = getMeterId(currentMileage);
  const pid = getMeterId(previousMileage);
  if (!cid || !pid) return false;
  return cid !== pid;
};

const formatFuelEntryForBreakdown = (entry, getLocation, getSubLocation) => {
  let meterReadings = [];
  if (entry?.mileage && Array.isArray(entry.mileage)) {
    meterReadings = entry.mileage.map(m => {
      const readings = getMeterReadings(m);
      return {
        name: m.name || '-',
        reading: readings.reading,
        calculatedReading: readings.calculatedReading,
        hasCalculatedReading: readings.calculatedReading !== null
      };
    }).filter(m => m.reading !== null || m.calculatedReading !== null);
  }

  const meterReadingStr = meterReadings.length > 0
    ? meterReadings.map(m => {
        if (m.hasCalculatedReading && m.reading !== m.calculatedReading) {
          return `${m.name}: ${m.reading} (Calc: ${m.calculatedReading})`;
        }
        return `${m.name}: ${m.reading}`;
      }).join(', ')
    : '-';

  return {
    date: moment(entry?.date || entry?.datetime).format('YYYY-MM-DD HH:mm'),
    voucherNo: entry?.voucherNo || '-',
    quantity: entry?.quantity || 0,
    isFullTank: entry?.isFullTank === true,
    location: getLocation ? getLocation(entry) : '-',
    subLocation: getSubLocation ? getSubLocation(entry) : '-',
    fuelSource: entry?.fuelSource?.name || entry?.fuelSource?.code || '-',
    meterReadings,
    meterReadingStr
  };
};

const normalizeConsumptionUnit = (unitLabel) => {
  const value = (unitLabel || '').toUpperCase();
  if (value.includes('KWH')) return 'KWH/L';
  if (value.includes('KM') || value.includes('KMS')) return 'KM/L';
  if (value.includes('HOUR') || value.includes('HRS')) return 'L/HOURS';
  return null;
};

const getUnitPriority = (unitLabel) => {
  const normalized = normalizeConsumptionUnit(unitLabel);
  if (normalized === 'KWH/L') return 3;
  if (normalized === 'KM/L') return 2;
  if (normalized === 'L/HOURS') return 1;
  return 0;
};

const shouldPreferRateData = (nextRate, currentRate) => {
  if (!nextRate) return false;
  if (!currentRate) return true;
  return getUnitPriority(nextRate.unit) > getUnitPriority(currentRate.unit);
};

const getMeterPatternsForUnit = (unitLabel) => {
  const normalized = normalizeConsumptionUnit(unitLabel);
  if (normalized === 'KWH/L') return ['KWH'];
  if (normalized === 'KM/L') return ['KM', 'ODO', 'DISTANCE'];
  if (normalized === 'L/HOURS') return ['HOUR', 'HRS'];
  return [];
};

const buildMeterPriorityPatterns = (preferredUnit) => {
  const orderedGroups = [
    getMeterPatternsForUnit(preferredUnit),
    ['KWH'],
    ['KM', 'ODO', 'DISTANCE'],
    ['HOUR', 'HRS'],
  ];

  const seen = new Set();
  const patterns = [];
  orderedGroups.flat().forEach(pattern => {
    if (!pattern || seen.has(pattern)) return;
    seen.add(pattern);
    patterns.push(pattern);
  });

  return patterns;
};

const getConsumptionUnitFromMeterName = (meterName) => {
  const value = (meterName || '').toUpperCase();
  if (value.includes('KWH')) return 'KWH/L';
  if (value.includes('KM') || value.includes('ODO') || value.includes('DISTANCE')) return 'KM/L';
  if (value.includes('HOUR') || value.includes('HRS')) return 'L/HOURS';
  return null;
};

// Comparison Table Component
const ComparisonTable = ({ data, selectedPeriods, loading, filters, columns, selectedMonth, showIoTData, hideZeroRefills }) => {
  // Filter data based on filters
  let filtered = data;
  
  // Hide assets with 0 refills in ALL periods (show if ANY period has > 0 refills)
  if (hideZeroRefills) {
    filtered = filtered.filter(item => {
      // Check if any period has > 0 refills
      return selectedPeriods.some(period => {
        const periodKey = period.format('YYYY-MM');
        const periodData = item.periods[periodKey];
        return periodData && periodData.totalRefills > 0;
      });
    });
  }
  
  if (filters.assetCode) {
    const searchTerm = filters.assetCode.toLowerCase();
    filtered = filtered.filter(item => {
      const matchCode = item.assetCode && item.assetCode.toLowerCase().includes(searchTerm);
      const matchAltname = item.altname && item.altname.toLowerCase().includes(searchTerm);
      const matchOwnercode = item.ownercode && item.ownercode.toLowerCase().includes(searchTerm);
      return matchCode || matchAltname || matchOwnercode;
    });
  }
  if (filters.excludedAssetCodes && filters.excludedAssetCodes.length > 0) {
    const excludeSet = new Set(
      filters.excludedAssetCodes.map(c => String(c).trim().toLowerCase()).filter(Boolean)
    );
    filtered = filtered.filter(item => {
      const code = (item.assetCode || '').toLowerCase();
      const alt = (item.altname || '').toLowerCase();
      const own = (item.ownercode || '').toLowerCase();
      return !(excludeSet.has(code) || excludeSet.has(alt) || excludeSet.has(own));
    });
  }
  if (filters.assetType) {
    filtered = filtered.filter(item => item.assetType === filters.assetType);
  }
  if (filters.assetSubType) {
    filtered = filtered.filter(item => item.assetSubType === filters.assetSubType);
  }
  if (filters.assetModel) {
    filtered = filtered.filter(item => item.assetModel === filters.assetModel);
  }
  if (filters.location) {
    filtered = filtered.filter(item => {
      return selectedPeriods.some(period => {
        const periodKey = period.format('YYYY-MM');
        const periodData = item.periods[periodKey];
        return periodData && periodData.location === filters.location;
      });
    });
  }
  if (filters.subLocation) {
    filtered = filtered.filter(item => {
      return selectedPeriods.some(period => {
        const periodKey = period.format('YYYY-MM');
        const periodData = item.periods[periodKey];
        return periodData && periodData.subLocation === filters.subLocation;
      });
    });
  }

  // Sort filtered data by: Severity, Variance (abs desc), Sub Location, Location, Asset Model, Asset Sub Type, Asset Type
  const firstPeriodKey = selectedPeriods[0]?.format('YYYY-MM');
  filtered.sort((a, b) => {
    // Severity first (ascending order: 1=Critical, 2=High, 3=Medium, 4=Low, 5=OK, 999=null)
    const severityA = (a.periods?.[firstPeriodKey]?.severity || a.severity)?.order || 999;
    const severityB = (b.periods?.[firstPeriodKey]?.severity || b.severity)?.order || 999;
    if (severityA !== severityB) return severityA - severityB;
    
    // Variance second (positive before negative, descending - worse first; null sorts last)
    const vA = a.periods?.[firstPeriodKey]?.variancePercent ?? a.variancePercent;
    const vB = b.periods?.[firstPeriodKey]?.variancePercent ?? b.variancePercent;
    const varA = (vA != null) ? vA : -Infinity;
    const varB = (vB != null) ? vB : -Infinity;
    if (varA !== varB) return varB - varA; // descending: positive first, then negative
    
    const subLocA = (a.subLocation || '').toLowerCase();
    const subLocB = (b.subLocation || '').toLowerCase();
    if (subLocA !== subLocB) return subLocA.localeCompare(subLocB);
    
    const locA = (a.location || '').toLowerCase();
    const locB = (b.location || '').toLowerCase();
    if (locA !== locB) return locA.localeCompare(locB);
    
    const modelA = (a.assetModel || '').toLowerCase();
    const modelB = (b.assetModel || '').toLowerCase();
    if (modelA !== modelB) return modelA.localeCompare(modelB);
    
    const subTypeA = (a.assetSubType || '').toLowerCase();
    const subTypeB = (b.assetSubType || '').toLowerCase();
    if (subTypeA !== subTypeB) return subTypeA.localeCompare(subTypeB);
    
    const typeA = (a.assetType || '').toLowerCase();
    const typeB = (b.assetType || '').toLowerCase();
    return typeA.localeCompare(typeB);
  });

  // Transform data to show periods as rows
  const comparisonRows = [];
  filtered.forEach(asset => {
    selectedPeriods.forEach(period => {
      const periodKey = period.format('YYYY-MM');
      const periodData = asset.periods[periodKey];
      
      if (periodData) {
        comparisonRows.push({
          ...periodData,
          periodLabel: period.format('MMM YYYY'),
          periodKey: periodKey,
          periodMoment: period,
          assetGroupKey: `${asset.assetCode}_${asset.assetModel}_${asset.location || ''}_${asset.subLocation || ''}`,
          _assetCode: asset.assetCode,
          _assetType: asset.assetType,
          _assetSubType: asset.assetSubType,
          _assetModel: asset.assetModel,
          _location: asset.location,
          _subLocation: asset.subLocation
        });
      }
    });
  });

  // Add period column at the beginning
  const comparisonColumns = [
    {
      title: 'Period',
      dataIndex: 'periodLabel',
      key: 'periodLabel',
      width: 100,
      fixed: 'left',
      render: (text, record) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>{text}</Tag>
      )
    },
    ...columns.filter(col => 
      col.key !== 'snNo' && col.dataIndex !== 'snNo'
    ).map(col => ({
      ...col,
      fixed: col.fixed === 'left' ? 'left' : undefined
    }))
  ];

  // Add row grouping styling
  const getRowClassName = (record, index) => {
    if (index > 0) {
      const prevRecord = comparisonRows[index - 1];
      if (prevRecord && prevRecord.assetGroupKey === record.assetGroupKey) {
        return 'comparison-row-grouped';
      }
    }
    return 'comparison-row-first';
  };

  return (
    <div>
      <style>{`
        .comparison-row-grouped {
          background-color: #fafafa;
        }
        .comparison-row-first {
          border-top: 2px solid #1890ff;
        }
        .comparison-row-grouped td:first-child {
          border-left: 3px solid #1890ff;
        }
      `}</style>
      <Table
        columns={comparisonColumns}
        dataSource={comparisonRows}
        rowKey={(record) => `${record.assetGroupKey}_${record.periodKey}`}
        loading={loading}
        scroll={{ x: showIoTData ? 4000 : 2200, y: 600 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} rows`
        }}
        rowClassName={getRowClassName}
      />
    </div>
  );
};

function MonthlyFullTankReport() {
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const [assetModels, setAssetModels] = useState([]);
  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [selectedAssetSubType, setSelectedAssetSubType] = useState(null);
  const [selectedAssetModel, setSelectedAssetModel] = useState(null);
  const [selectedAssetCode, setSelectedAssetCode] = useState(null);
  const [excludedAssetCodes, setExcludedAssetCodes] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSubLocation, setSelectedSubLocation] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, 'month'));
  const [standardRatesMap, setStandardRatesMap] = useState({});
  const [varianceThresholdsMap, setVarianceThresholdsMap] = useState({});
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedPeriods, setSelectedPeriods] = useState([moment().subtract(1, 'month')]);
  const [comparisonData, setComparisonData] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecordDetails, setSelectedRecordDetails] = useState(null);
  const [fuelBowserMap, setFuelBowserMap] = useState({}); // Map of asset codes to isFuelBowser status
  const [rawFuelEntries, setRawFuelEntries] = useState([]); // Store raw fuel entries for accurate totals
  const [rawFuelEntriesPerPeriod, setRawFuelEntriesPerPeriod] = useState({}); // For comparison mode: periodKey -> entries
  const [dayWiseModalVisible, setDayWiseModalVisible] = useState(false);
  const [dayWiseModalTitle, setDayWiseModalTitle] = useState('');
  const [dayWiseData, setDayWiseData] = useState([]);

  // Helpers for the "Exclude Asset Codes" filter — exact (case-insensitive) match
  // against assetCode / altname / ownercode so partial codes don't accidentally
  // exclude unrelated assets.
  const buildExcludeSet = () => new Set(
    excludedAssetCodes.map(c => String(c).trim().toLowerCase()).filter(Boolean)
  );
  const isExcludedAggregatedItem = (item, excludeSet) => {
    if (!excludeSet || excludeSet.size === 0) return false;
    const code = (item.assetCode || '').toLowerCase();
    const alt = (item.altname || '').toLowerCase();
    const own = (item.ownercode || '').toLowerCase();
    return excludeSet.has(code) || excludeSet.has(alt) || excludeSet.has(own);
  };
  const isExcludedRawEntry = (entry, excludeSet) => {
    if (!excludeSet || excludeSet.size === 0) return false;
    let assetCode = null;
    let altname = null;
    let ownercode = null;
    if (entry.asset && typeof entry.asset === 'object') {
      assetCode = entry.asset.assetCode;
      altname = entry.asset.altname || entry.asset.altName;
      ownercode = entry.asset.ownercode || entry.asset.ownerCode;
    }
    if (!assetCode && entry.assetCode) assetCode = entry.assetCode;
    const code = (assetCode || '').toLowerCase();
    const alt = (altname || '').toLowerCase();
    const own = (ownercode || '').toLowerCase();
    return excludeSet.has(code) || excludeSet.has(alt) || excludeSet.has(own);
  };

  // IoT data state
  const [showIoTData, setShowIoTData] = useState(false); // Toggle to show/hide IoT data
  const [hideZeroRefills, setHideZeroRefills] = useState(true); // Hide assets with 0 refills (default: true)
  const [iotDataPerAsset, setIotDataPerAsset] = useState({}); // Map: assetCode -> IoT aggregated data
  const [iotDataPerPeriod, setIotDataPerPeriod] = useState({}); // For comparison: periodKey -> { assetCode -> IoT data }
  const [dailyIoTData, setDailyIoTData] = useState([]); // Daily IoT records for breakdown
  const [dailyIoTDataPerPeriod, setDailyIoTDataPerPeriod] = useState({}); // For comparison: periodKey -> daily records
  const [iotBreakdownModalVisible, setIotBreakdownModalVisible] = useState(false);
  const [iotBreakdownData, setIotBreakdownData] = useState([]);
  const [iotBreakdownTitle, setIotBreakdownTitle] = useState('');
  
  // Refuel entries breakdown modal state
  const [refuelBreakdownModalVisible, setRefuelBreakdownModalVisible] = useState(false);
  const [refuelBreakdownData, setRefuelBreakdownData] = useState([]);
  const [refuelBreakdownTitle, setRefuelBreakdownTitle] = useState('');

  // Per-cycle refuel breakup modal state (the entries behind one full-tank cycle's
  // Fuel Consumed / Meter Run).
  const [cycleBreakupVisible, setCycleBreakupVisible] = useState(false);
  const [cycleBreakupData, setCycleBreakupData] = useState([]);
  const [cycleBreakupInfo, setCycleBreakupInfo] = useState(null);

  // Email report modal state
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchText, setUserSearchText] = useState('');
  const [emailServerOnline, setEmailServerOnline] = useState(false);

  // Load standard rates and fuel bowser map on mount
  useEffect(() => {
    loadStandardRates();
    loadFuelBowserMap();
  }, []);

  // Fetch asset list and build fuel bowser map (to filter out fuel storage assets)
  const loadFuelBowserMap = async () => {
    try {
      console.log('Fetching asset list to build fuel bowser map...');
      const result = await apiGet('/asset/asset-all', {
        headers: { 'Accept': '*/*' },
      });

      if (!result.success) {
        console.warn('Failed to fetch asset list for fuel bowser filtering:', result.error);
        return {};
      }

      const assets = result.data || [];
      const bowserMap = {};
      
      assets.forEach(asset => {
        if (asset.assetCode) {
          // Check if asset has equipmentSubType with assetType that has isBowser
          // Primary path: asset.equipmentSubType.assetType.isBowser
          let isBowser = false;
          
          // Check equipmentSubType.assetType.isBowser (primary path based on API structure)
          if (asset.equipmentSubType && typeof asset.equipmentSubType === 'object' && asset.equipmentSubType !== null) {
            const assetType = asset.equipmentSubType.assetType;
            if (assetType && typeof assetType === 'object' && assetType !== null) {
              isBowser = assetType.isBowser === true || assetType.isBowser === 'true';
            }
          }
          
          // Fallback: Check asset.assetType directly (for backward compatibility)
          if (!isBowser && asset.assetType) {
            if (typeof asset.assetType === 'object' && asset.assetType !== null) {
              isBowser = asset.assetType.isBowser === true || 
                        asset.assetType.isBowser === 'true' ||
                        asset.assetType.isFuelBowser === true ||
                        asset.assetType.isAFuelBowser === true ||
                        asset.assetType['Is a fuel Bowser'] === true ||
                        false;
            }
          }
          
          if (isBowser) {
            bowserMap[asset.assetCode] = true;
            console.log(`Found fuel storage/bowser asset (isBowser=true): ${asset.assetCode}`);
          }
        }
      });
      
      console.log(`Built fuel bowser map with ${Object.keys(bowserMap).length} fuel storage assets to exclude`);
      setFuelBowserMap(bowserMap);
      return bowserMap;
    } catch (error) {
      console.error('Error loading fuel bowser map:', error);
      return {};
    }
  };

  // Filter out entries where fuel was ENTERED IN a bowser (recipient is bowser).
  // Do NOT filter when fuel was FILLED FROM a bowser (entry.isBowser = source was bowser, recipient is vehicle).
  const filterOutBowserEntries = (entries, bowserMap = {}) => {
    return entries.filter(entry => {
      let assetCode = null;
      if (entry.asset && typeof entry.asset === 'object' && entry.asset.assetCode) {
        assetCode = entry.asset.assetCode;
      }
      if (!assetCode && entry.assetCode) {
        assetCode = entry.assetCode;
      }

      // Only filter when the ASSET (recipient being refueled) is a bowser.
      // entry.isBowser = fuel source was bowser (vehicle refueled FROM bowser) - INCLUDE these.
      let assetIsBowser = false;

      // 1. Check fuelBowserMap (pre-loaded from asset list API - asset type is bowser)
      if (assetCode && bowserMap[assetCode] === true) {
        assetIsBowser = true;
      }

      // 2. Check entry.assetType.isBowser (asset's type is bowser)
      if (!assetIsBowser && entry.assetType) {
        if (typeof entry.assetType === 'object' && entry.assetType !== null) {
          assetIsBowser = entry.assetType.isBowser === true || entry.assetType.isBowser === 'true';
        }
      }

      // 3. Check entry.asset.assetType.isBowser (asset's type is bowser)
      if (!assetIsBowser && entry.asset && entry.asset.assetType) {
        if (typeof entry.asset.assetType === 'object' && entry.asset.assetType !== null) {
          assetIsBowser = entry.asset.assetType.isBowser === true ||
                    entry.asset.assetType.isBowser === 'true' ||
                    entry.asset.assetType.isFuelBowser === true ||
                    entry.asset.assetType.isAFuelBowser === true;
        }
      }

      // 4. Check entry.asset.equipmentSubType.assetType.isBowser (asset's type is bowser)
      if (!assetIsBowser && entry.asset && entry.asset.equipmentSubType) {
        const equipmentSubType = entry.asset.equipmentSubType;
        if (typeof equipmentSubType === 'object' && equipmentSubType !== null && equipmentSubType.assetType) {
          const assetTypeObj = equipmentSubType.assetType;
          if (typeof assetTypeObj === 'object' && assetTypeObj !== null) {
            assetIsBowser = assetTypeObj.isBowser === true || assetTypeObj.isBowser === 'true';
          }
        }
      }

      // 5. Check entry.assetSubType.assetType.isBowser (asset's type is bowser)
      if (!assetIsBowser && entry.assetSubType) {
        if (typeof entry.assetSubType === 'object' && entry.assetSubType !== null && entry.assetSubType.assetType) {
          const assetTypeObj = entry.assetSubType.assetType;
          if (typeof assetTypeObj === 'object' && assetTypeObj !== null) {
            assetIsBowser = assetTypeObj.isBowser === true || assetTypeObj.isBowser === 'true';
          }
        }
      }

      // Do NOT filter by entry.isBowser - that indicates fuel SOURCE was bowser (vehicle refueled from bowser).

      return !assetIsBowser;
    });
  };

  // Transform IoT API data to a usable format (similar to transformApiResponseToCsvFormat in IoTSummaryReport)
  const transformIoTApiData = (apiData, bowserMap = {}) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    
    // Filter out bowser assets
    const filteredData = apiData.filter(record => {
      const asset = record.asset || {};
      const assetCode = asset.assetCode || '';
      
      if (bowserMap[assetCode] === true) return false;
      
      let isBowser = false;
      if (asset.equipmentSubType?.assetType) {
        const assetType = asset.equipmentSubType.assetType;
        isBowser = assetType.isBowser === true || assetType.isBowser === 'true';
      }
      if (!isBowser && asset.assetType) {
        isBowser = asset.assetType.isBowser === true || asset.assetType.isBowser === 'true';
      }
      return !isBowser;
    });
    
    return filteredData.map(record => {
      const asset = record.asset || {};
      const locationObj = asset.location || {};
      const subLocationObj = asset.subLocation || {};
      const assetModelObj = asset.assetModel || {};
      const equipmentSubTypeObj = asset.equipmentSubType || {};
      
      // Determine unit of measurement based on asset sub type
      const subTypeName = equipmentSubTypeObj.name || '';
      const subTypeLower = subTypeName.toLowerCase();
      let unitOfMeasurement = 'KMS';
      if (subTypeLower.includes('excavator') || subTypeLower.includes('loader') || 
          subTypeLower.includes('generator') || subTypeLower.includes('crane') ||
          subTypeLower.includes('compressor') || subTypeLower.includes('roller') ||
          subTypeLower.includes('forklift') || subTypeLower.includes('pump')) {
        unitOfMeasurement = 'HOURS';
      }
      
      return {
        assetCode: asset.assetCode || '',
        ownerCode: asset.ownerCode || '',
        altName: asset.altName || '',
        location: typeof locationObj === 'string' ? locationObj : (locationObj.name || locationObj.code || ''),
        subLocation: typeof subLocationObj === 'string' ? subLocationObj : (subLocationObj.name || subLocationObj.code || ''),
        assetModel: typeof assetModelObj === 'string' ? assetModelObj : (assetModelObj.name || ''),
        assetSubType: equipmentSubTypeObj.name || '',
        assetType: equipmentSubTypeObj.assetType?.name || '',
        date: record.date_timestamp || record.date,
        dateFormatted: moment(record.date_timestamp || record.date).format('DD/MM/YYYY'),
        ignitionTime: (record.ignition_time || 0) / 60000, // Convert ms to minutes
        idleTime: (record.ideal_time || record.idle_time || 0) / 60000,
        movingTime: (record.moving_time || 0) / 60000,
        distanceTravelled: (record.distance_travelled || 0) / 1000, // Convert m to km
        fuelConsumed: record.fuel_consumed || record.fuel_consumed_analog || 0,
        possibleRefuel: Math.abs(record.total_refuel || 0), // total_refuel is negative
        possibleDrain: record.total_theft || 0,
        unitOfMeasurement,
        dataFrom: record.dataFromTimestamp || record.startOfDayEpoch,
        dataTo: record.dataToTimestamp || record.endOfDayEpoch
      };
    });
  };

  // Aggregate IoT data per asset for a given period
  // Uses standardRatesMap to determine correct unit (priority: KWH > KMS > HOURS)
  const aggregateIoTDataPerAsset = (dailyData, period, ratesMap = {}) => {
    // Use IST timezone (UTC+5:30) for date range
    const monthStart = period.clone().startOf('month').utcOffset('+05:30', true);
    const monthEnd = period.clone().endOf('month').utcOffset('+05:30', true);
    
    // Filter data for the month
    const monthData = dailyData.filter(row => {
      const date = moment(row.date);
      return date.isBetween(monthStart, monthEnd, 'day', '[]');
    });
    
    // Group by assetCode + location (to match full tank grouping)
    const assetMap = {};
    monthData.forEach(row => {
      const key = `${row.assetCode}_${row.location || '-'}_${row.subLocation || '-'}`;
      
      if (!assetMap[key]) {
        assetMap[key] = {
          assetCode: row.assetCode,
          location: row.location || '-',
          subLocation: row.subLocation || '-',
          assetModel: row.assetModel,
          assetType: row.assetType,
          assetSubType: row.assetSubType,
          unitOfMeasurement: null, // Will be determined from standard rate
          totalFuelConsumedIoT: 0,
          totalFuelAddedIoT: 0,
          totalUnitsIoT: 0,
          totalIgnitionMinutes: 0,
          totalDistanceKm: 0,
          daysWithData: 0,
          dailyRecords: []
        };
      }
      
      assetMap[key].totalFuelConsumedIoT += row.fuelConsumed || 0;
      assetMap[key].totalFuelAddedIoT += row.possibleRefuel || 0;
      assetMap[key].totalIgnitionMinutes += row.ignitionTime || 0;
      assetMap[key].totalDistanceKm += row.distanceTravelled || 0;
      assetMap[key].daysWithData += 1;
      assetMap[key].dailyRecords.push(row);
    });
    
    // Calculate total units and consumption rate based on standard rate unit
    Object.values(assetMap).forEach(asset => {
      // Determine unit from standard rate (priority: KWH > KMS > HOURS)
      let unitOfMeasurement = null;
      let standardRateUnit = null;
      
      if (asset.assetModel && ratesMap) {
        const modelName = asset.assetModel;
        const normalizedModelName = modelName ? modelName.trim().toLowerCase() : '';
        
        // Look for standard rate - check various key formats
        const stdRate = ratesMap[modelName] || ratesMap[normalizedModelName];
        
        if (stdRate) {
          // Check unit from the standard rate
          const rateUnit = stdRate.unit || '';
          const rateUnitUpper = rateUnit.toUpperCase();
          
          // Determine unit based on standard rate unit (priority: KWH > KMS > HOURS)
          if (rateUnitUpper.includes('KWH')) {
            unitOfMeasurement = 'KWH';
            standardRateUnit = rateUnit;
          } else if (rateUnitUpper.includes('KM')) {
            unitOfMeasurement = 'KMS';
            standardRateUnit = rateUnit;
          } else if (rateUnitUpper.includes('HOUR') || rateUnitUpper.includes('HRS')) {
            unitOfMeasurement = 'HOURS';
            standardRateUnit = rateUnit;
          }
        }
      }
      
      // Fallback to asset type if no standard rate found
      if (!unitOfMeasurement) {
        const subTypeLower = (asset.assetSubType || '').toLowerCase();
        const typeLower = (asset.assetType || '').toLowerCase();
        const combinedType = subTypeLower + ' ' + typeLower;
        
        const isEquipment = combinedType.includes('excavator') || combinedType.includes('loader') ||
                          combinedType.includes('generator') || combinedType.includes('crane') ||
                          combinedType.includes('compressor') || combinedType.includes('roller') ||
                          combinedType.includes('forklift') || combinedType.includes('pump') ||
                          combinedType.includes('grader') || combinedType.includes('backhoe');
        
        unitOfMeasurement = isEquipment ? 'HOURS' : 'KMS';
      }
      
      asset.unitOfMeasurement = unitOfMeasurement;
      
      // Calculate total units and consumption rate based on determined unit
      if (unitOfMeasurement === 'KWH') {
        // For KWH, we don't have KWH data from IoT sensors directly
        // Fall back to HOURS for equipment
        asset.totalUnitsIoT = asset.totalIgnitionMinutes / 60;
        asset.iotConsumptionRate = asset.totalUnitsIoT > 0 
          ? asset.totalFuelConsumedIoT / asset.totalUnitsIoT 
          : 0;
        asset.iotConsumptionRateUnit = 'Ltr/Hr';
        asset.iotUnitLabel = 'Hrs';
      } else if (unitOfMeasurement === 'HOURS') {
        asset.totalUnitsIoT = asset.totalIgnitionMinutes / 60; // Convert to hours
        asset.iotConsumptionRate = asset.totalUnitsIoT > 0 
          ? asset.totalFuelConsumedIoT / asset.totalUnitsIoT // Ltr/Hr
          : 0;
        asset.iotConsumptionRateUnit = 'Ltr/Hr';
        asset.iotUnitLabel = 'Hrs';
      } else {
        // KMS
        asset.totalUnitsIoT = asset.totalDistanceKm;
        asset.iotConsumptionRate = asset.totalFuelConsumedIoT > 0 
          ? asset.totalUnitsIoT / asset.totalFuelConsumedIoT // KM/Ltr
          : 0;
        asset.iotConsumptionRateUnit = 'KM/Ltr';
        asset.iotUnitLabel = 'KM';
      }
    });
    
    return assetMap;
  };

  // Handle click on IoT values to show daily breakdown
  const handleIotBreakdownClick = (assetCode, location, subLocation, title, period = null) => {
    const periodMoment = period || selectedMonth;
    const periodKey = periodMoment.format('YYYY-MM');
    
    // Get daily records from appropriate source
    let dailyRecords = [];
    if (comparisonMode && dailyIoTDataPerPeriod[periodKey]) {
      dailyRecords = dailyIoTDataPerPeriod[periodKey];
    } else {
      dailyRecords = dailyIoTData;
    }
    
    // Filter by asset code and location
    // Use IST timezone (UTC+5:30) for date range
    const monthStart = periodMoment.clone().startOf('month').utcOffset('+05:30', true);
    const monthEnd = periodMoment.clone().endOf('month').utcOffset('+05:30', true);
    const filtered = dailyRecords.filter(row => {
      const matchAsset = row.assetCode === assetCode;
      const matchLocation = (row.location || '-') === (location || '-');
      const matchSubLocation = !subLocation || subLocation === '-' || (row.subLocation || '-') === subLocation;
      const date = moment(row.date);
      const matchDate = date.isBetween(monthStart, monthEnd, 'day', '[]');
      return matchAsset && matchLocation && matchSubLocation && matchDate;
    }).sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
    
    setIotBreakdownData(filtered);
    setIotBreakdownTitle(`${title} - ${assetCode} (${periodMoment.format('MMM YYYY')})`);
    setIotBreakdownModalVisible(true);
  };

  // Calculate day-wise breakdown from raw entries (with filters applied)
  // periodMoment: optional - for comparison mode, use this period; otherwise selectedMonth
  const calculateDayWiseBreakdown = (entries, metricType, periodMoment = null) => {
    const period = periodMoment || selectedMonth;
    // Use IST timezone (UTC+5:30) for date range
    const startOfMonth = period.clone().startOf('month').utcOffset('+05:30', true);
    const endOfMonth = period.clone().endOf('month').utcOffset('+05:30', true);
    
    // Apply the same filters as the main table
    let filteredEntries = entries;
    
    // Filter by asset code (search across assetCode, altname, ownercode)
    if (selectedAssetCode) {
      const searchTerm = selectedAssetCode.toLowerCase();
      filteredEntries = filteredEntries.filter(entry => {
        let assetCode = null;
        let altname = null;
        let ownercode = null;
        if (entry.asset && typeof entry.asset === 'object') {
          assetCode = entry.asset.assetCode;
          altname = entry.asset.altname || entry.asset.altName;
          ownercode = entry.asset.ownercode || entry.asset.ownerCode;
        }
        if (!assetCode && entry.assetCode) {
          assetCode = entry.assetCode;
        }
        const matchCode = assetCode && assetCode.toLowerCase().includes(searchTerm);
        const matchAltname = altname && altname.toLowerCase().includes(searchTerm);
        const matchOwnercode = ownercode && ownercode.toLowerCase().includes(searchTerm);
        return matchCode || matchAltname || matchOwnercode;
      });
    }

    if (excludedAssetCodes.length > 0) {
      const excludeSet = buildExcludeSet();
      filteredEntries = filteredEntries.filter(entry => !isExcludedRawEntry(entry, excludeSet));
    }

    // Filter by asset type
    if (selectedAssetType) {
      filteredEntries = filteredEntries.filter(entry => {
        const assetType = extractName(entry.assetType);
        return assetType === selectedAssetType;
      });
    }

    // Filter by asset sub type
    if (selectedAssetSubType) {
      filteredEntries = filteredEntries.filter(entry => {
        const assetSubType = extractName(entry.assetSubType);
        return assetSubType === selectedAssetSubType;
      });
    }

    // Filter by asset model
    if (selectedAssetModel) {
      filteredEntries = filteredEntries.filter(entry => {
        const assetModel = extractName(entry.assetModel);
        return assetModel === selectedAssetModel;
      });
    }

    // Filter by location (use same helper as aggregation for consistency)
    if (selectedLocation) {
      filteredEntries = filteredEntries.filter(entry => {
        const location = getLocationFromEntry(entry);
        return location === selectedLocation;
      });
    }

    // Filter by sub location
    if (selectedSubLocation) {
      filteredEntries = filteredEntries.filter(entry => {
        const subLocation = getSubLocationFromEntry(entry);
        return subLocation === selectedSubLocation;
      });
    }

    // Group entries by date
    const dayMap = {};
    
    filteredEntries.forEach(entry => {
      const entryDate = moment(entry.date || entry.datetime);
      if (!entryDate.isBetween(startOfMonth, endOfMonth, null, '[]')) return;
      
      const dateKey = entryDate.format('YYYY-MM-DD');
      
      if (!dayMap[dateKey]) {
        dayMap[dateKey] = {
          date: dateKey,
          dateDisplay: entryDate.format('DD MMM YYYY (ddd)'),
          totalRefills: 0,
          totalFuelAdded: 0,
          fullTankEvents: 0,
          entries: []
        };
      }
      
      dayMap[dateKey].totalRefills += 1;
      dayMap[dateKey].totalFuelAdded += (entry.quantity || 0);
      if (entry.isFullTank === true) {
        dayMap[dateKey].fullTankEvents += 1;
      }
      dayMap[dateKey].entries.push(entry);
    });
    
    // Convert to array and sort by date
    const dayWiseArray = Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date));
    
    // Calculate running totals
    let runningRefills = 0;
    let runningFuelAdded = 0;
    let runningFullTank = 0;
    
    dayWiseArray.forEach(day => {
      runningRefills += day.totalRefills;
      runningFuelAdded += day.totalFuelAdded;
      runningFullTank += day.fullTankEvents;
      day.runningRefills = runningRefills;
      day.runningFuelAdded = runningFuelAdded;
      day.runningFullTank = runningFullTank;
    });
    
    return dayWiseArray;
  };

  // Handle click on summary statistics (single month mode)
  const handleSummaryClick = (metricType, title) => {
    const filterParts = [];
    if (selectedAssetCode) filterParts.push(`Asset: ${selectedAssetCode}`);
    if (selectedAssetType) filterParts.push(`Type: ${selectedAssetType}`);
    if (selectedAssetSubType) filterParts.push(`Sub Type: ${selectedAssetSubType}`);
    if (selectedAssetModel) filterParts.push(`Model: ${selectedAssetModel}`);
    if (selectedLocation) filterParts.push(`Location: ${selectedLocation}`);
    if (selectedSubLocation) filterParts.push(`Sub Location: ${selectedSubLocation}`);
    const filterSuffix = filterParts.length > 0 ? ` (${filterParts.join(', ')})` : '';
    
    const dayWise = calculateDayWiseBreakdown(rawFuelEntries, metricType);
    setDayWiseData(dayWise);
    setDayWiseModalTitle(title + filterSuffix);
    setDayWiseModalVisible(true);
  };

  // Handle click on summary statistics (comparison mode - per period)
  const handleSummaryClickForPeriod = (metricType, title, period, periodLabel) => {
    const periodKey = period.format('YYYY-MM');
    const entries = rawFuelEntriesPerPeriod[periodKey] || [];
    
    const filterParts = [];
    if (selectedAssetCode) filterParts.push(`Asset: ${selectedAssetCode}`);
    if (selectedAssetType) filterParts.push(`Type: ${selectedAssetType}`);
    if (selectedAssetSubType) filterParts.push(`Sub Type: ${selectedAssetSubType}`);
    if (selectedAssetModel) filterParts.push(`Model: ${selectedAssetModel}`);
    if (selectedLocation) filterParts.push(`Location: ${selectedLocation}`);
    if (selectedSubLocation) filterParts.push(`Sub Location: ${selectedSubLocation}`);
    filterParts.push(`Period: ${periodLabel}`);
    const filterSuffix = filterParts.length > 0 ? ` (${filterParts.join(', ')})` : '';
    
    const dayWise = calculateDayWiseBreakdown(entries, metricType, period);
    setDayWiseData(dayWise);
    setDayWiseModalTitle(title + filterSuffix);
    setDayWiseModalVisible(true);
  };

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [summaryData, selectedAssetType, selectedAssetSubType, selectedAssetModel, selectedAssetCode, excludedAssetCodes, selectedLocation, selectedSubLocation, hideZeroRefills]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
      return undefined;
    }

    window.__monthlyFullTankDebug = {
      selectedMonth: selectedMonth ? selectedMonth.format('YYYY-MM') : null,
      filters: {
        assetType: selectedAssetType,
        assetSubType: selectedAssetSubType,
        assetModel: selectedAssetModel,
        assetCode: selectedAssetCode,
        location: selectedLocation,
        subLocation: selectedSubLocation,
        hideZeroRefills,
      },
      rawFuelEntries,
      summaryData,
      filteredData,
      comparisonMode,
      comparisonData,
      rawFuelEntriesPerPeriod,
    };

    return () => {
      delete window.__monthlyFullTankDebug;
    };
  }, [
    selectedMonth,
    selectedAssetType,
    selectedAssetSubType,
    selectedAssetModel,
    selectedAssetCode,
    selectedLocation,
    selectedSubLocation,
    hideZeroRefills,
    rawFuelEntries,
    summaryData,
    filteredData,
    comparisonMode,
    comparisonData,
    rawFuelEntriesPerPeriod,
  ]);

  // Update filter options based on selections
  useEffect(() => {
    if (summaryData.length > 0) {
      let filtered = summaryData;
      
      if (selectedAssetType) {
        filtered = filtered.filter(item => item.assetType === selectedAssetType);
      }
      
      const subTypes = [...new Set(filtered.map(item => item.assetSubType).filter(Boolean))].sort();
      setAssetSubTypes(subTypes);
      
      if (selectedAssetSubType) {
        filtered = filtered.filter(item => item.assetSubType === selectedAssetSubType);
      }
      
      const models = [...new Set(filtered.map(item => item.assetModel).filter(Boolean))].sort();
      setAssetModels(models);
      
      if (selectedLocation) {
        filtered = filtered.filter(item => item.location === selectedLocation);
      }
      
      const subLocs = [...new Set(filtered.map(item => item.subLocation).filter(Boolean))].sort();
      setSubLocations(subLocs);
    }
  }, [summaryData, selectedAssetType, selectedAssetSubType, selectedLocation]);

  const loadStandardRates = async () => {
    try {
      console.log('Loading standard consumption rates from API...');
      
      const modelsResult = await apiGet('/asset-model', {
        headers: { 'Accept': '*/*' },
      });

      if (!modelsResult.success) {
        console.error(`Failed to load asset models: ${modelsResult.error}`);
        return;
      }

      // Handle different response structures
      let modelsData = [];
      if (Array.isArray(modelsResult.data)) {
        modelsData = modelsResult.data;
      } else if (modelsResult.data && Array.isArray(modelsResult.data.data)) {
        modelsData = modelsResult.data.data;
      } else if (modelsResult.data && Array.isArray(modelsResult.data.models)) {
        modelsData = modelsResult.data.models;
      } else if (modelsResult.data && typeof modelsResult.data === 'object') {
        for (const key of Object.keys(modelsResult.data)) {
          if (Array.isArray(modelsResult.data[key])) {
            modelsData = modelsResult.data[key];
            break;
          }
        }
      }

      const ratesMap = {};
      const thresholdsMap = {};

      modelsData.forEach(model => {
        if (!model._id) return;

        const modelName = (model.name || '').trim();
        const normalizedName = modelName.toLowerCase();

        // Extract standard consumption rates
        if (model.standardFuelConsumptionRate && Array.isArray(model.standardFuelConsumptionRate) && model.standardFuelConsumptionRate.length > 0) {
          model.standardFuelConsumptionRate.forEach(rateObj => {
            const fuelQty = rateObj.fuelQty;
            if (fuelQty === null || fuelQty === undefined || isNaN(fuelQty) || fuelQty <= 0) return;

            if (rateObj.perType && rateObj.perType.name) {
              const unitName = rateObj.perType.name.toUpperCase();
              let rate = null;
              let unit = '';

              if (unitName === 'KMS' || unitName === 'KM' || unitName.includes('KM')) {
                rate = 1 / fuelQty;
                unit = 'KM/L';
              } else if (unitName === 'HOURS' || unitName === 'HOUR' || unitName === 'HRS' || unitName.includes('HOUR')) {
                rate = fuelQty;
                unit = 'L/HOURS';
              } else if (unitName === 'KWH' || unitName.includes('KWH')) {
                rate = 1 / fuelQty;
                unit = 'KWH/L';
              }

              if (rate !== null) {
                const keyById = `${model._id}_${unit}`;
                const keyByName = `${normalizedName}_${unit}`;
                
                const rateData = { rate, unit, modelId: model._id, modelName };
                
                if (!ratesMap[keyById]) ratesMap[keyById] = rateData;
                if (shouldPreferRateData(rateData, ratesMap[model._id])) ratesMap[model._id] = rateData;
                if (!ratesMap[keyByName]) ratesMap[keyByName] = rateData;
                if (shouldPreferRateData(rateData, ratesMap[normalizedName])) ratesMap[normalizedName] = rateData;
                if (modelName && shouldPreferRateData(rateData, ratesMap[modelName])) ratesMap[modelName] = rateData;
              }
            }
          });
        }

        // Extract variance thresholds
        if (model.fuelMileageThreshold && Array.isArray(model.fuelMileageThreshold) && model.fuelMileageThreshold.length > 0) {
          const firstThreshold = model.fuelMileageThreshold[0];
          if (firstThreshold.value !== null && firstThreshold.value !== undefined && !isNaN(firstThreshold.value) && firstThreshold.value > 0) {
            const thresholdData = { threshold: firstThreshold.value, modelId: model._id, modelName };
            thresholdsMap[model._id] = thresholdData;
            thresholdsMap[normalizedName] = thresholdData;
            if (modelName) thresholdsMap[modelName] = thresholdData;
          }
        }
      });

      console.log(`Loaded ${Object.keys(ratesMap).length} standard rates and ${Object.keys(thresholdsMap).length} thresholds`);
      setStandardRatesMap(ratesMap);
      setVarianceThresholdsMap(thresholdsMap);
    } catch (error) {
      console.error('Error loading standard rates:', error);
    }
  };

  const loadSummaryData = async () => {
    setLoading(true);
    setSummaryData([]);
    setFilteredData([]);

    try {
      // Use IST timezone (UTC+5:30) for date range to ensure correct boundary handling
      // keepLocalTime=true interprets the date values in IST
      const startIST = selectedMonth.clone().startOf('month').utcOffset('+05:30', true);
      const endIST = selectedMonth.clone().endOf('month').utcOffset('+05:30', true);
      const startTimestamp = startIST.valueOf();
      const endTimestamp = endIST.valueOf();
      const startISO = startIST.toISOString();
      const endISO = endIST.toISOString();

      console.log(`Loading monthly full tank data for ${selectedMonth.format('MMMM YYYY')}`);

      // Ensure fuel bowser map is loaded
      let currentBowserMap = fuelBowserMap;
      if (Object.keys(currentBowserMap).length === 0) {
        console.log('Loading fuel bowser map before fetching data...');
        currentBowserMap = await loadFuelBowserMap();
      }

      // Fetch all fuel entries
      const allFuelEntriesResult = await apiPost('/fuel/mileage-report', {
        asset: null,
        location: 'all',
        subLocation: 'all',
        periodFrom: startISO,
        periodTo: endISO,
      });

      if (!allFuelEntriesResult.success) {
        message.error(`Failed to fetch fuel entries: ${allFuelEntriesResult.error}`);
        setLoading(false);
        return;
      }

      const allFuelEntries = allFuelEntriesResult.data || [];
      console.log(`Fetched ${allFuelEntries.length} fuel entries`);

      // Filter bowsers ONCE - use same filtered set for both raw totals and aggregation
      // so that per-asset table rows sum correctly to the location total
      const bowserFilteredEntries = filterOutBowserEntries(allFuelEntries, currentBowserMap);
      setRawFuelEntries(bowserFilteredEntries);

      // Process and aggregate - pass already-filtered entries (bowser filter already applied)
      const aggregatedData = aggregateMonthlyData(bowserFilteredEntries, startTimestamp, endTimestamp, {});
      
      // Fetch IoT data for the same period (only if showIoTData is enabled)
      let iotAggregatedMap = {};
      let transformedIotData = [];
      
      if (showIoTData) {
        console.log('Fetching IoT data for the period...');
        try {
          const iotResult = await fetchDailyFuelDataReport(startTimestamp, endTimestamp, {
            assets: null,
            locations: ['all'],
            subLocations: ['all'],
            assetTypes: [],
            assetSubTypes: [],
            assetModels: [],
            frequency: 'Daily'
          });
          
          if (iotResult.success && iotResult.data && iotResult.data.length > 0) {
            console.log(`Fetched ${iotResult.data.length} IoT daily records`);
            transformedIotData = transformIoTApiData(iotResult.data, currentBowserMap);
            setDailyIoTData(transformedIotData);
            iotAggregatedMap = aggregateIoTDataPerAsset(transformedIotData, selectedMonth, standardRatesMap);
            setIotDataPerAsset(iotAggregatedMap);
          } else {
            console.log('No IoT data available for this period');
            setDailyIoTData([]);
            setIotDataPerAsset({});
          }
        } catch (iotError) {
          console.warn('Failed to fetch IoT data:', iotError);
          setDailyIoTData([]);
          setIotDataPerAsset({});
        }
      } else {
        // Clear IoT data when not enabled
        setDailyIoTData([]);
        setIotDataPerAsset({});
      }
      
      // Merge IoT data into aggregatedData (with difference calculations)
      aggregatedData.forEach(item => {
        const key = `${item.assetCode}_${item.location || '-'}_${item.subLocation || '-'}`;
        const iotData = iotAggregatedMap[key];
        if (iotData && showIoTData) {
          item.iotFuelAdded = iotData.totalFuelAddedIoT || 0;
          item.iotFuelConsumed = iotData.totalFuelConsumedIoT || 0;
          item.iotUnitsRun = iotData.totalUnitsIoT || 0;
          item.iotConsumptionRate = iotData.iotConsumptionRate || 0;
          item.iotConsumptionRateUnit = iotData.iotConsumptionRateUnit || '';
          item.iotUnitOfMeasurement = iotData.unitOfMeasurement || '';
          item.iotUnitLabel = iotData.iotUnitLabel || '';
          item.hasIoTData = true;
          
          // Calculate differences between IoT and Full Tank data
          // Fuel Added difference: IoT - Manual (Full Tank Total Fuel Added)
          item.fuelAddedDiff = item.iotFuelAdded - item.totalFuelAdded;
          item.fuelAddedDiffPercent = item.totalFuelAdded > 0 
            ? ((item.fuelAddedDiff / item.totalFuelAdded) * 100) 
            : (item.iotFuelAdded > 0 ? 100 : 0);
          
          // Fuel Consumed difference: IoT - Full Tank basis
          item.fuelConsumedDiff = item.iotFuelConsumed - item.totalFuelConsumed;
          item.fuelConsumedDiffPercent = item.totalFuelConsumed > 0 
            ? ((item.fuelConsumedDiff / item.totalFuelConsumed) * 100) 
            : (item.iotFuelConsumed > 0 ? 100 : 0);
          
          // Units Run difference: IoT - Full Tank meter run
          item.unitsRunDiff = item.iotUnitsRun - item.totalMeterRun;
          item.unitsRunDiffPercent = item.totalMeterRun > 0 
            ? ((item.unitsRunDiff / item.totalMeterRun) * 100) 
            : (item.iotUnitsRun > 0 ? 100 : 0);
          
          // Consumption Rate difference (only if same unit type)
          if (item.avgConsumptionRate && item.iotConsumptionRate) {
            item.consumptionRateDiff = item.iotConsumptionRate - item.avgConsumptionRate;
            item.consumptionRateDiffPercent = item.avgConsumptionRate > 0 
              ? ((item.consumptionRateDiff / item.avgConsumptionRate) * 100) 
              : 0;
          } else {
            item.consumptionRateDiff = null;
            item.consumptionRateDiffPercent = null;
          }
        } else {
          item.iotFuelAdded = 0;
          item.iotFuelConsumed = 0;
          item.iotUnitsRun = 0;
          item.iotConsumptionRate = 0;
          item.iotConsumptionRateUnit = '';
          item.iotUnitOfMeasurement = '';
          item.iotUnitLabel = '';
          item.hasIoTData = false;
          item.fuelAddedDiff = null;
          item.fuelAddedDiffPercent = null;
          item.fuelConsumedDiff = null;
          item.fuelConsumedDiffPercent = null;
          item.unitsRunDiff = null;
          item.unitsRunDiffPercent = null;
          item.consumptionRateDiff = null;
          item.consumptionRateDiffPercent = null;
        }
      });
      
      // Populate filter options
      const types = [...new Set(aggregatedData.map(a => a.assetType).filter(Boolean).filter(v => v !== '-'))].sort();
      const subTypes = [...new Set(aggregatedData.map(a => a.assetSubType).filter(Boolean).filter(v => v !== '-'))].sort();
      const models = [...new Set(aggregatedData.map(a => a.assetModel).filter(Boolean).filter(v => v !== '-'))].sort();
      const locs = [...new Set(aggregatedData.map(a => a.location).filter(Boolean).filter(v => v !== '-'))].sort();
      const subLocs = [...new Set(aggregatedData.map(a => a.subLocation).filter(Boolean).filter(v => v !== '-'))].sort();

      setAssetTypes(types);
      setAssetSubTypes(subTypes);
      setAssetModels(models);
      setLocations(locs);
      setSubLocations(subLocs);

      setSummaryData(aggregatedData);
      message.success(`Loaded ${aggregatedData.length} asset-location entries for ${selectedMonth.format('MMMM YYYY')}`);
    } catch (error) {
      console.error('Error loading summary data:', error);
      message.error('Failed to load summary data');
    } finally {
      setLoading(false);
    }
  };

  const loadComparisonData = async () => {
    if (selectedPeriods.length === 0) {
      message.warning('Please select at least one period to compare');
      return;
    }

    setLoading(true);
    setComparisonData([]);
    setRawFuelEntriesPerPeriod({});

    try {
      // Ensure fuel bowser map is loaded
      let currentBowserMap = fuelBowserMap;
      if (Object.keys(currentBowserMap).length === 0) {
        console.log('Loading fuel bowser map before fetching comparison data...');
        currentBowserMap = await loadFuelBowserMap();
      }

      const allPeriodsData = {};
      const rawEntriesByPeriod = {};
      const iotDataByPeriod = {};
      const dailyIotByPeriod = {};

      for (const period of selectedPeriods) {
        // Use IST timezone (UTC+5:30) for date range
        const startIST = period.clone().startOf('month').utcOffset('+05:30', true);
        const endIST = period.clone().endOf('month').utcOffset('+05:30', true);
        const startTimestamp = startIST.valueOf();
        const endTimestamp = endIST.valueOf();
        const startISO = startIST.toISOString();
        const endISO = endIST.toISOString();
        const periodKey = period.format('YYYY-MM');

        console.log(`Loading data for ${period.format('MMMM YYYY')}`);

        const result = await apiPost('/fuel/mileage-report', {
          asset: null,
          location: 'all',
          subLocation: 'all',
          periodFrom: startISO,
          periodTo: endISO,
        });

        // Fetch IoT data for this period (only if showIoTData is enabled)
        let iotAggregatedMap = {};
        if (showIoTData) {
          try {
            const iotResult = await fetchDailyFuelDataReport(startTimestamp, endTimestamp, {
              assets: null,
              locations: ['all'],
              subLocations: ['all'],
              assetTypes: [],
              assetSubTypes: [],
              assetModels: [],
              frequency: 'Daily'
            });
            
            if (iotResult.success && iotResult.data && iotResult.data.length > 0) {
              const transformedIotData = transformIoTApiData(iotResult.data, currentBowserMap);
              dailyIotByPeriod[periodKey] = transformedIotData;
              iotAggregatedMap = aggregateIoTDataPerAsset(transformedIotData, period, standardRatesMap);
              iotDataByPeriod[periodKey] = iotAggregatedMap;
            }
          } catch (iotError) {
            console.warn(`Failed to fetch IoT data for ${periodKey}:`, iotError);
          }
        }

        if (result.success) {
          const bowserFiltered = filterOutBowserEntries(result.data || [], currentBowserMap);
          rawEntriesByPeriod[periodKey] = bowserFiltered;
          const aggregated = aggregateMonthlyData(bowserFiltered, startTimestamp, endTimestamp, {});
          
          // Merge IoT data into aggregated items (with difference calculations)
          aggregated.forEach(item => {
            const iotKey = `${item.assetCode}_${item.location || '-'}_${item.subLocation || '-'}`;
            const iotData = iotAggregatedMap[iotKey];
            if (iotData && showIoTData) {
              item.iotFuelAdded = iotData.totalFuelAddedIoT || 0;
              item.iotFuelConsumed = iotData.totalFuelConsumedIoT || 0;
              item.iotUnitsRun = iotData.totalUnitsIoT || 0;
              item.iotConsumptionRate = iotData.iotConsumptionRate || 0;
              item.iotConsumptionRateUnit = iotData.iotConsumptionRateUnit || '';
              item.iotUnitOfMeasurement = iotData.unitOfMeasurement || '';
              item.iotUnitLabel = iotData.iotUnitLabel || '';
              item.hasIoTData = true;
              
              // Calculate differences between IoT and Full Tank data
              item.fuelAddedDiff = item.iotFuelAdded - item.totalFuelAdded;
              item.fuelAddedDiffPercent = item.totalFuelAdded > 0 
                ? ((item.fuelAddedDiff / item.totalFuelAdded) * 100) 
                : (item.iotFuelAdded > 0 ? 100 : 0);
              
              item.fuelConsumedDiff = item.iotFuelConsumed - item.totalFuelConsumed;
              item.fuelConsumedDiffPercent = item.totalFuelConsumed > 0 
                ? ((item.fuelConsumedDiff / item.totalFuelConsumed) * 100) 
                : (item.iotFuelConsumed > 0 ? 100 : 0);
              
              item.unitsRunDiff = item.iotUnitsRun - item.totalMeterRun;
              item.unitsRunDiffPercent = item.totalMeterRun > 0 
                ? ((item.unitsRunDiff / item.totalMeterRun) * 100) 
                : (item.iotUnitsRun > 0 ? 100 : 0);
              
              if (item.avgConsumptionRate && item.iotConsumptionRate) {
                item.consumptionRateDiff = item.iotConsumptionRate - item.avgConsumptionRate;
                item.consumptionRateDiffPercent = item.avgConsumptionRate > 0 
                  ? ((item.consumptionRateDiff / item.avgConsumptionRate) * 100) 
                  : 0;
              } else {
                item.consumptionRateDiff = null;
                item.consumptionRateDiffPercent = null;
              }
            } else {
              item.iotFuelAdded = 0;
              item.iotFuelConsumed = 0;
              item.iotUnitsRun = 0;
              item.iotConsumptionRate = 0;
              item.iotConsumptionRateUnit = '';
              item.iotUnitOfMeasurement = '';
              item.iotUnitLabel = '';
              item.hasIoTData = false;
              item.fuelAddedDiff = null;
              item.fuelAddedDiffPercent = null;
              item.fuelConsumedDiff = null;
              item.fuelConsumedDiffPercent = null;
              item.unitsRunDiff = null;
              item.unitsRunDiffPercent = null;
              item.consumptionRateDiff = null;
              item.consumptionRateDiffPercent = null;
            }
            
            // Key includes location to handle assets at multiple locations
            const key = `${item.assetCode}_${item.assetModel}_${item.location}_${item.subLocation}`;
            if (!allPeriodsData[key]) {
              allPeriodsData[key] = {
                assetCode: item.assetCode,
                assetType: item.assetType,
                assetSubType: item.assetSubType,
                assetModel: item.assetModel,
                location: item.location,
                subLocation: item.subLocation,
                periods: {}
              };
            }
            allPeriodsData[key].periods[periodKey] = item;
          });
        }
      }

      const comparisonItems = Object.values(allPeriodsData);
      setComparisonData(comparisonItems);
      setIotDataPerPeriod(iotDataByPeriod);
      setDailyIoTDataPerPeriod(dailyIotByPeriod);

      // Populate filter options from comparison data
      const types = [...new Set(comparisonItems.map(a => a.assetType).filter(Boolean).filter(v => v !== '-'))].sort();
      const subTypes = [...new Set(comparisonItems.map(a => a.assetSubType).filter(Boolean).filter(v => v !== '-'))].sort();
      const models = [...new Set(comparisonItems.map(a => a.assetModel).filter(Boolean).filter(v => v !== '-'))].sort();
      const locs = [...new Set(comparisonItems.map(a => a.location).filter(Boolean).filter(v => v !== '-'))].sort();
      const subLocs = [...new Set(comparisonItems.map(a => a.subLocation).filter(Boolean).filter(v => v !== '-'))].sort();
      setAssetTypes(types);
      setAssetSubTypes(subTypes);
      setAssetModels(models);
      setLocations(locs);
      setSubLocations(subLocs);

      setRawFuelEntriesPerPeriod(rawEntriesByPeriod);
      message.success(`Loaded comparison data for ${selectedPeriods.length} periods`);
    } catch (error) {
      console.error('Error loading comparison data:', error);
      message.error('Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  const extractName = (field) => {
    if (!field) return null;
    
    if (typeof field === 'string') {
      const isObjectId = /^[0-9a-fA-F]{24}$/i.test(field.trim());
      const isObjectIdPattern = /^[0-9a-fA-F]{12,24}\s+[0-9a-fA-F]{12,24}$/i.test(field.trim());
      
      if (isObjectId || isObjectIdPattern) return null;
      if (field.length > 20 && /^[0-9a-fA-F\s]+$/i.test(field)) return null;
      return field.trim();
    }
    
    if (typeof field === 'object') {
      const val = field.name || field.code || field.title || field.label || null;
      return val && typeof val === 'string' ? val.trim() : val;
    }
    
    return null;
  };

  // Get location from entry - check root first, fallback to asset (for consistent grouping)
  const getLocationFromEntry = (entry) => {
    const loc = extractName(entry.location) || extractName(entry.asset?.location);
    return (loc != null && String(loc).trim()) || '-';
  };

  const getSubLocationFromEntry = (entry) => {
    const sub = extractName(entry.subLocation) || extractName(entry.asset?.subLocation);
    return (sub != null && String(sub).trim()) || '-';
  };

  // Calculate severity based on variance and threshold
  // D = signed deviation (variancePercent), T = threshold
  // D > 0 means worse (consuming more than expected)
  // D < 0 means better (consuming less than expected)
  const calculateSeverity = (variancePercent, varianceThreshold) => {
    // If no variance data, return null (no severity)
    if (variancePercent === null || variancePercent === undefined) {
      return { level: null, order: 999 };
    }
    
    // Default threshold if not defined
    const T = varianceThreshold !== null && varianceThreshold !== undefined ? varianceThreshold : 10;
    const D = variancePercent; // Signed deviation
    const A = Math.abs(D); // Absolute deviation
    
    // If within threshold, no severity concern
    if (A < T) {
      return { level: 'OK', order: 5, color: '#52c41a', bgColor: '#f6ffed' };
    }
    
    if (D > 0) {
      // WORSE case (consuming more fuel than expected) - tighter bands
      if (A >= T + 30) {
        return { level: 'CRITICAL', order: 1, color: '#fff', bgColor: '#ff4d4f', textColor: '#fff' };
      } else if (A >= T + 20) {
        return { level: 'HIGH', order: 2, color: '#fa541c', bgColor: '#fff2e8' };
      } else if (A >= T + 10) {
        return { level: 'MEDIUM', order: 3, color: '#faad14', bgColor: '#fffbe6' };
      } else {
        return { level: 'LOW', order: 4, color: '#1890ff', bgColor: '#e6f7ff' };
      }
    } else {
      // BETTER case (consuming less fuel than expected) - wider bands
      // "Better" still needs attention as it might indicate logging/fill issues
      // Bands: LOW T to T+20, MEDIUM T+20 to T+30, HIGH T+30 to T+50, CRITICAL >= T+50
      if (A >= T + 50) {
        return { level: 'CRITICAL', order: 1, color: '#fff', bgColor: '#722ed1', textColor: '#fff', isBetter: true };
      } else if (A >= T + 30) {
        return { level: 'HIGH', order: 2, color: '#722ed1', bgColor: '#f9f0ff', isBetter: true };
      } else if (A >= T + 20) {
        return { level: 'MEDIUM', order: 3, color: '#13c2c2', bgColor: '#e6fffb', isBetter: true };
      } else {
        return { level: 'LOW', order: 4, color: '#52c41a', bgColor: '#f6ffed', isBetter: true };
      }
    }
  };

  const aggregateMonthlyData = (allFuelEntries, startTimestamp, endTimestamp, bowserMap = {}) => {
    // First, filter out fuel bowser/storage assets
    // Check isBowser flag in multiple locations as it can appear in different API response structures
    let filteredOutCount = 0;
    const filteredOutAssets = new Set();

    const filteredEntries = allFuelEntries.filter(entry => {
      let assetCode = null;
      if (entry.asset) {
        if (typeof entry.asset === 'object' && entry.asset.assetCode) {
          assetCode = entry.asset.assetCode;
        }
      }
      if (!assetCode && entry.assetCode) {
        assetCode = entry.assetCode;
      }

      // Check isBowser flag in all possible locations
      let isBowser = false;

      // 1. Check fuelBowserMap (pre-loaded from asset list API)
      if (assetCode && bowserMap[assetCode] === true) {
        isBowser = true;
      }

      // 2. Check entry.assetType.isBowser (root level populated object)
      if (!isBowser && entry.assetType) {
        if (typeof entry.assetType === 'object' && entry.assetType !== null) {
          isBowser = entry.assetType.isBowser === true || entry.assetType.isBowser === 'true';
        }
      }

      // 3. Check entry.asset.assetType.isBowser (nested in asset)
      if (!isBowser && entry.asset && entry.asset.assetType) {
        if (typeof entry.asset.assetType === 'object' && entry.asset.assetType !== null) {
          isBowser = entry.asset.assetType.isBowser === true || 
                    entry.asset.assetType.isBowser === 'true' ||
                    entry.asset.assetType.isFuelBowser === true ||
                    entry.asset.assetType.isAFuelBowser === true;
        }
      }

      // 4. Check entry.asset.equipmentSubType.assetType.isBowser (deeply nested)
      if (!isBowser && entry.asset && entry.asset.equipmentSubType) {
        const equipmentSubType = entry.asset.equipmentSubType;
        if (typeof equipmentSubType === 'object' && equipmentSubType !== null && equipmentSubType.assetType) {
          const assetTypeObj = equipmentSubType.assetType;
          if (typeof assetTypeObj === 'object' && assetTypeObj !== null) {
            isBowser = assetTypeObj.isBowser === true || assetTypeObj.isBowser === 'true';
          }
        }
      }

      // 5. Check entry.assetSubType.assetType.isBowser (root level assetSubType with nested assetType)
      if (!isBowser && entry.assetSubType) {
        if (typeof entry.assetSubType === 'object' && entry.assetSubType !== null && entry.assetSubType.assetType) {
          const assetTypeObj = entry.assetSubType.assetType;
          if (typeof assetTypeObj === 'object' && assetTypeObj !== null) {
            isBowser = assetTypeObj.isBowser === true || assetTypeObj.isBowser === 'true';
          }
        }
      }

      // Do NOT filter by entry.isBowser - that indicates fuel SOURCE was bowser (vehicle refueled from bowser).

      if (isBowser) {
        filteredOutCount++;
        if (assetCode) {
          filteredOutAssets.add(assetCode);
        }
        return false;
      }

      return true;
    });

    if (filteredOutCount > 0) {
      console.log(`Filtered out ${filteredOutCount} fuel entries from ${filteredOutAssets.size} fuel storage/bowser assets`);
      console.log(`Fuel bowser assets filtered: ${Array.from(filteredOutAssets).join(', ')}`);
    }

    // Group entries by asset AND location (to handle assets that move between locations)
    // Key: assetCode_location_subLocation
    const assetLocationMap = {};
    const fuelEntriesByAssetLocation = {};
    // Also track all entries per asset (for full tank calculation across locations)
    const fuelEntriesByAsset = {};

    filteredEntries.forEach(entry => {
      let assetCode = null;
      if (entry.asset) {
        if (typeof entry.asset === 'object' && entry.asset.assetCode) {
          assetCode = entry.asset.assetCode;
        }
      }
      if (!assetCode && entry.assetCode) {
        assetCode = entry.assetCode;
      }
      if (!assetCode) return;

      // Extract location from entry (with fallback to asset.location for consistent grouping)
      const location = getLocationFromEntry(entry);
      const subLocation = getSubLocationFromEntry(entry);
      const locationKey = `${assetCode}_${location}_${subLocation}`;

      // Store all entries per asset (needed for full tank calculation)
      if (!fuelEntriesByAsset[assetCode]) {
        fuelEntriesByAsset[assetCode] = [];
      }
      fuelEntriesByAsset[assetCode].push(entry);

      // Store entries per asset-location combination
      if (!fuelEntriesByAssetLocation[locationKey]) {
        fuelEntriesByAssetLocation[locationKey] = [];
      }
      fuelEntriesByAssetLocation[locationKey].push(entry);

      // Extract altname and ownercode from asset
      let altname = null;
      let ownercode = null;
      if (entry.asset && typeof entry.asset === 'object') {
        altname = entry.asset.altname || entry.asset.altName || null;
        ownercode = entry.asset.ownercode || entry.asset.ownerCode || null;
      }

      // Extract asset info from the entry with best populated fields
      if (!assetLocationMap[locationKey]) {
        assetLocationMap[locationKey] = {
          assetCode: assetCode,
          altname: altname,
          ownercode: ownercode,
          assetType: extractName(entry.assetType),
          assetSubType: extractName(entry.assetSubType),
          assetModel: extractName(entry.assetModel),
          location: location,
          subLocation: subLocation,
          _assetModelObj: entry.assetModel
        };
      } else {
        // Update with better populated data if available
        if (!assetLocationMap[locationKey].altname && altname) {
          assetLocationMap[locationKey].altname = altname;
        }
        if (!assetLocationMap[locationKey].ownercode && ownercode) {
          assetLocationMap[locationKey].ownercode = ownercode;
        }
        if ((!assetLocationMap[locationKey].assetType || assetLocationMap[locationKey].assetType === '-') && entry.assetType) {
          assetLocationMap[locationKey].assetType = extractName(entry.assetType);
        }
        if ((!assetLocationMap[locationKey].assetSubType || assetLocationMap[locationKey].assetSubType === '-') && entry.assetSubType) {
          assetLocationMap[locationKey].assetSubType = extractName(entry.assetSubType);
        }
        if ((!assetLocationMap[locationKey].assetModel || assetLocationMap[locationKey].assetModel === '-') && entry.assetModel) {
          assetLocationMap[locationKey].assetModel = extractName(entry.assetModel);
          assetLocationMap[locationKey]._assetModelObj = entry.assetModel;
        }
      }
    });

    console.log(`Grouped entries into ${Object.keys(assetLocationMap).length} asset-location combinations`);

    // Aggregate data for each asset-location combination
    const aggregatedData = [];
    let snNo = 0;

    Object.keys(assetLocationMap).forEach(locationKey => {
      const assetLocation = assetLocationMap[locationKey];
      const assetCode = assetLocation.assetCode;
      
      // Get entries for this specific location
      const locationEntries = fuelEntriesByAssetLocation[locationKey] || [];
      // Get ALL entries for this asset (needed for full tank calculation across locations)
      const allAssetEntries = fuelEntriesByAsset[assetCode] || [];

      if (locationEntries.length === 0) return;

      // Sort all asset entries by date (for full tank calculation)
      allAssetEntries.sort((a, b) => moment(a.date || a.datetime).valueOf() - moment(b.date || b.datetime).valueOf());

      // Get all full tank events for this asset (including those outside the date range for calculation)
      const allFullTankEvents = allAssetEntries
        .filter(entry => entry.isFullTank === true)
        .sort((a, b) => moment(a.date || a.datetime).valueOf() - moment(b.date || b.datetime).valueOf());

      // Filter for full tank events within the date range AND at this location
      const fullTankEventsInRangeAtLocation = locationEntries.filter(entry => {
        if (entry.isFullTank !== true) return false;
        const entryDate = moment(entry.date || entry.datetime);
        return entryDate.isBetween(moment(startTimestamp), moment(endTimestamp), null, '[]');
      });

      // Calculate totals for THIS LOCATION only (for location-specific fuel consumption)
      const locationEntriesInRange = locationEntries.filter(e => {
        const entryDate = moment(e.date || e.datetime);
        return entryDate.isBetween(moment(startTimestamp), moment(endTimestamp), null, '[]');
      });

      const totalRefills = locationEntriesInRange.length;
      const totalFuelAdded = locationEntriesInRange.reduce((sum, e) => sum + (e.quantity || 0), 0);
      
      // Store all fuel entries for breakdown view (both full tank and non-full tank)
      const allFuelEntries = locationEntriesInRange
        .map(entry => formatFuelEntryForBreakdown(entry, getLocationFromEntry, getSubLocationFromEntry))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Get standard rate for this asset model
      let standardRate = null;
      let standardRateUnit = null;
      let varianceThreshold = null;

      if (assetLocation.assetModel || assetLocation._assetModelObj) {
        const modelName = assetLocation.assetModel;
        const modelId = typeof assetLocation._assetModelObj === 'object' ? assetLocation._assetModelObj._id : assetLocation._assetModelObj;
        const normalizedName = modelName ? modelName.trim().toLowerCase() : '';

        // Find standard rate
        const rateData = standardRatesMap[modelId] || 
                        standardRatesMap[modelName] || 
                        standardRatesMap[normalizedName];
        if (rateData) {
          standardRate = rateData.rate;
          standardRateUnit = rateData.unit;
        }

        // Find variance threshold
        const thresholdData = varianceThresholdsMap[modelId] || 
                             varianceThresholdsMap[modelName] || 
                             varianceThresholdsMap[normalizedName];
        if (thresholdData) {
          varianceThreshold = thresholdData.threshold;
        }
      }

      // Calculate consumption metrics from full tank events AT THIS LOCATION
      let totalMeterRun = 0;
      let totalFuelConsumed = 0;
      let meterName = null;
      let consumptionRateUnit = null;
      let varianceNote = null;
      let fullTankEventDetails = [];
      let fullTankDebug = [];
      // Counts meter-change cycles that were dropped BEFORE reaching fullTankEventDetails
      // (e.g. the calculatedReading run came out <= 0 for the cycle spanning the change).
      // Such cycles are already absent from the average; this only lets the footnote/marker
      // still report that a meter change happened during the month.
      let droppedMeterChangeCount = 0;

      if (fullTankEventsInRangeAtLocation.length > 0) {
        for (const currentEvent of fullTankEventsInRangeAtLocation) {
          const currentEventDate = moment(currentEvent.date || currentEvent.datetime);
          const debugEntry = {
            currentDate: currentEventDate.format('YYYY-MM-DD HH:mm'),
            voucherNo: currentEvent.voucherNo || '-',
            location: assetLocation.location,
            subLocation: assetLocation.subLocation,
            status: 'pending',
            reason: null,
            previousDate: null,
            cycleEntryCount: 0,
            fuelConsumed: 0,
            meterRun: null,
            meterName: null,
          };
          
          // Find previous full tank event (from ALL asset entries, not just this location)
          // This is needed because full tank calculation spans across locations
          let previousEvent = null;
          for (let i = allFullTankEvents.length - 1; i >= 0; i--) {
            const event = allFullTankEvents[i];
            const eventDate = moment(event.date || event.datetime);
            if (eventDate.isBefore(currentEventDate)) {
              previousEvent = event;
              break;
            }
          }

          if (!previousEvent) {
            debugEntry.status = 'skipped';
            debugEntry.reason = 'no_previous_full_tank';
            fullTankDebug.push(debugEntry);
            continue;
          }

          const previousEventDate = moment(previousEvent.date || previousEvent.datetime);
          debugEntry.previousDate = previousEventDate.format('YYYY-MM-DD HH:mm');

          // Calculate fuel consumed over the COMPLETE full tank cycle.
          // If the current full tank falls within the selected month, the cycle may
          // legitimately start before the month boundary.
          const cycleEntries = allAssetEntries.filter(entry => {
            const entryDate = moment(entry.date || entry.datetime);
            
            // Check if within the full tank cycle
            // After previous event (exclusive) AND up to current event (inclusive)
            const afterPrevious = entryDate.isAfter(previousEventDate);
            const beforeOrAtCurrent = entryDate.isSameOrBefore(currentEventDate);
            return afterPrevious && beforeOrAtCurrent;
          });
          
          const fuelConsumed = cycleEntries.reduce((sum, entry) => sum + (entry.quantity || 0), 0);
          debugEntry.cycleEntryCount = cycleEntries.length;
          debugEntry.fuelConsumed = fuelConsumed;

          // Build the refuel-event breakup that produced this cycle's Fuel Consumed and Meter Run.
          // Opening = previous full tank (start meter; its fuel belongs to the PRIOR cycle, NOT
          // counted here). Intermediate = non-full-tank refuels in between (counted). Closing =
          // current full tank (end meter; counted). Each row carries date, voucher, meter reading
          // (physical + Calc) and full-tank flag via formatFuelEntryForBreakdown.
          const cycleBreakdown = [
            { ...formatFuelEntryForBreakdown(previousEvent, getLocationFromEntry, getSubLocationFromEntry), role: 'opening', countedInConsumption: false },
            ...cycleEntries
              .slice()
              .sort((a, b) => moment(a.date || a.datetime) - moment(b.date || b.datetime))
              .map(e => ({
                ...formatFuelEntryForBreakdown(e, getLocationFromEntry, getSubLocationFromEntry),
                role: e.isFullTank ? 'closing' : 'intermediate',
                countedInConsumption: true,
              })),
          ];

          // Calculate meter run
          let meterRun = null;
          let currentMeterName = null;
          let currentMeterReadings = null; // { reading, calculatedReading } for display
          let previousMeterReadings = null; // { reading, calculatedReading } for display
          let currentMeterId = null;       // meter entity ID at current event (primary meter)
          let previousMeterId = null;      // meter entity ID at previous event (primary meter)
          let isMeterChangeFirstReading = false; // true if a meter change occurred in this cycle

          if (currentEvent.mileage && Array.isArray(currentEvent.mileage) &&
              previousEvent.mileage && Array.isArray(previousEvent.mileage)) {

            // Search the preferred standard unit first, then fall back to other supported meter types.
            const meterPriority = buildMeterPriorityPatterns(standardRateUnit);

            for (const priorityPattern of meterPriority) {
              for (const currentMeter of currentEvent.mileage) {
                const meterNameUpper = (currentMeter.name || '').toUpperCase();
                if (!meterNameUpper.includes(priorityPattern)) continue;

                const previousMeter = previousEvent.mileage.find(m =>
                  (m.name || '').toUpperCase() === meterNameUpper
                );

                // Use calculatedReading for run calculation (cumulative, always increasing)
                // This handles cases where physical meter was reset (e.g., equipment change)
                const currentCalcReading = getCalculatedReading(currentMeter);
                const previousCalcReading = getCalculatedReading(previousMeter);

                if (currentCalcReading !== null && previousCalcReading !== null) {
                  const run = currentCalcReading - previousCalcReading;
                  if (run > 0) {
                    meterRun = run;
                    currentMeterName = currentMeter.name;
                    debugEntry.meterRun = run;
                    debugEntry.meterName = currentMeter.name;
                    // Store both readings for display in breakup
                    currentMeterReadings = getMeterReadings(currentMeter);
                    previousMeterReadings = getMeterReadings(previousMeter);
                    // Meter-change detection on the SAME meter pair used for the run.
                    // A new meter entity (new ID) after a replacement means this cycle's
                    // run under-counts the faulty-period hours/km -> exclude from average.
                    currentMeterId = getMeterId(currentMeter);
                    previousMeterId = getMeterId(previousMeter);
                    isMeterChangeFirstReading = detectMeterChange(currentMeter, previousMeter);
                    break;
                  }
                }
              }
              if (meterRun !== null) break;
            }

            // Independently of the run sign, check whether the highest-priority meter
            // present in BOTH events changed entity ID. This catches meter-change cycles
            // whose calculatedReading run came out <= 0 (already dropped below) so the
            // footnote/marker can still report the change for the month.
            if (meterRun === null) {
              for (const priorityPattern of meterPriority) {
                let detected = false;
                for (const currentMeter of currentEvent.mileage) {
                  const meterNameUpper = (currentMeter.name || '').toUpperCase();
                  if (!meterNameUpper.includes(priorityPattern)) continue;
                  const previousMeter = previousEvent.mileage.find(m =>
                    (m.name || '').toUpperCase() === meterNameUpper
                  );
                  if (previousMeter && detectMeterChange(currentMeter, previousMeter)) {
                    droppedMeterChangeCount++;
                    detected = true;
                    break;
                  }
                }
                if (detected) break;
              }
            }
          }

          if (!Array.isArray(currentEvent.mileage) || !Array.isArray(previousEvent.mileage)) {
            debugEntry.status = 'skipped';
            debugEntry.reason = 'missing_mileage_array';
            fullTankDebug.push(debugEntry);
            continue;
          }

          if (!meterRun) {
            debugEntry.status = 'skipped';
            debugEntry.reason = 'missing_or_non_positive_meter_run';
            fullTankDebug.push(debugEntry);
            continue;
          }

          if (fuelConsumed <= 0) {
            debugEntry.status = 'skipped';
            debugEntry.reason = 'no_fuel_consumed_in_period';
            fullTankDebug.push(debugEntry);
            continue;
          }

          // Calculate consumption rate using the unit detected from THIS event's meter
          // (don't lock in to the first event's unit — different cycles may use different meters)
          let rate = null;
          const detectedConsumptionUnit = getConsumptionUnitFromMeterName(currentMeterName);

          if (detectedConsumptionUnit === 'KWH/L') {
            rate = meterRun / fuelConsumed;
          } else if (detectedConsumptionUnit === 'KM/L') {
            rate = meterRun / fuelConsumed;
          } else if (detectedConsumptionUnit === 'L/HOURS') {
            rate = fuelConsumed / meterRun;
          }

          // Build per-meter calculations for ALL meters present in this event so the
          // breakdown modal can show every unit, not just the primary one selected above.
          const meterCalculations = [];
          if (Array.isArray(currentEvent.mileage) && Array.isArray(previousEvent.mileage)) {
            for (const cMeter of currentEvent.mileage) {
              const cName = cMeter.name || '';
              const cUnit = getConsumptionUnitFromMeterName(cName);
              if (!cUnit) continue;
              const pMeter = previousEvent.mileage.find(m =>
                (m.name || '').toUpperCase() === cName.toUpperCase()
              );
              if (!pMeter) continue;
              const cRead = getCalculatedReading(cMeter);
              const pRead = getCalculatedReading(pMeter);
              if (cRead === null || pRead === null) continue;
              const run = cRead - pRead;
              if (run <= 0) continue;
              const cReadings = getMeterReadings(cMeter);
              const pReadings = getMeterReadings(pMeter);
              const meterRate = cUnit === 'L/HOURS' ? fuelConsumed / run : run / fuelConsumed;
              meterCalculations.push({
                meterName: cName,
                meterRun: run,
                rate: meterRate,
                rateUnit: cUnit,
                currentMeterReading: cReadings.reading,
                currentMeterCalcReading: cReadings.calculatedReading,
                previousMeterReading: pReadings.reading,
                previousMeterCalcReading: pReadings.calculatedReading,
              });
            }
          }

          debugEntry.status = 'included';
          debugEntry.reason = 'ok';
          fullTankDebug.push(debugEntry);

          // Store event detail with its OWN detected unit (not a locked-in one)
          fullTankEventDetails.push({
            date: currentEventDate.format('YYYY-MM-DD HH:mm'),
            previousDate: previousEventDate.format('YYYY-MM-DD HH:mm'),
            fuelConsumed,
            meterRun,
            meterName: currentMeterName,
            rate,
            rateUnit: detectedConsumptionUnit,
            meterCalculations,
            voucherNo: currentEvent.voucherNo || '-',
            location: assetLocation.location,
            subLocation: assetLocation.subLocation,
            // Include both physical reading and calculatedReading for display
            currentMeterReading: currentMeterReadings?.reading ?? null,
            currentMeterCalcReading: currentMeterReadings?.calculatedReading ?? null,
            previousMeterReading: previousMeterReadings?.reading ?? null,
            previousMeterCalcReading: previousMeterReadings?.calculatedReading ?? null,
            // Meter identity + meter-change flag for this cycle.
            currentMeterId,
            previousMeterId,
            isMeterChangeFirstReading,
            currentEventEntry: formatFuelEntryForBreakdown(currentEvent, getLocationFromEntry, getSubLocationFromEntry),
            previousEventEntry: formatFuelEntryForBreakdown(previousEvent, getLocationFromEntry, getSubLocationFromEntry),
            // Full refuel breakup behind this cycle's Fuel Consumed / Meter Run (opening + intermediates + closing).
            cycleBreakdown,
          });
        }
      }

      // Pick a single primary meter unit, then aggregate only cycles that used that meter.
      // Different cycles can use different meters (e.g. some have KMs, some only HOURS); summing
      // mixed units into totalMeterRun produces nonsense. Pick by KWH > KMS > HOURS priority —
      // the highest-priority unit that has at least one cycle wins, regardless of count.
      const cyclesByUnit = { 'KWH/L': [], 'KM/L': [], 'L/HOURS': [] };
      fullTankEventDetails.forEach(ev => {
        if (ev.rateUnit && cyclesByUnit[ev.rateUnit]) cyclesByUnit[ev.rateUnit].push(ev);
      });

      const unitPriorityOrder = ['KWH/L', 'KM/L', 'L/HOURS'];
      let primaryUnit = null;
      for (const u of unitPriorityOrder) {
        if (cyclesByUnit[u].length > 0) {
          primaryUnit = u;
          break;
        }
      }

      // Meter-change exclusion accumulators (see meter-change rule at top of this file).
      // GROSS = all primary-unit cycles. ADJUSTED = cycles excluding the first reading
      // after a meter change (its run under-counts the faulty-meter period and would
      // distort the month's average). EXCLUDED = the flagged remainder, shown for review.
      let adjustedMeterRun = 0;
      let adjustedFuelConsumed = 0;
      let excludedMeterRun = 0;
      let excludedFuelConsumed = 0;
      let excludedCycleCount = 0;

      if (primaryUnit) {
        const primaryCycles = cyclesByUnit[primaryUnit];
        totalMeterRun = primaryCycles.reduce((sum, ev) => sum + (ev.meterRun || 0), 0);
        totalFuelConsumed = primaryCycles.reduce((sum, ev) => sum + (ev.fuelConsumed || 0), 0);
        meterName = primaryCycles[0]?.meterName || null;
        consumptionRateUnit = primaryUnit;

        primaryCycles.forEach(ev => {
          if (ev.isMeterChangeFirstReading) {
            excludedMeterRun += ev.meterRun || 0;
            excludedFuelConsumed += ev.fuelConsumed || 0;
            excludedCycleCount++;
          } else {
            adjustedMeterRun += ev.meterRun || 0;
            adjustedFuelConsumed += ev.fuelConsumed || 0;
          }
        });
      }

      // A meter change happened this month if any primary-unit cycle was flagged, OR if a
      // meter-change cycle was dropped earlier for a non-positive run (already off the average).
      const hasMeterChange = excludedCycleCount > 0 || droppedMeterChangeCount > 0;
      let meterChangeNote = null;

      // Re-select standard rate to match the meter unit actually used in fuel entries.
      // The default standardRatesMap key picks the highest-priority rate (KWH > KMS > HOURS)
      // regardless of which meter the asset records. If the model defines a rate for the
      // meter unit that was actually used, prefer that to avoid unit mismatches.
      if (consumptionRateUnit && (assetLocation.assetModel || assetLocation._assetModelObj)) {
        const _modelName = assetLocation.assetModel;
        const _modelId = typeof assetLocation._assetModelObj === 'object'
          ? assetLocation._assetModelObj._id
          : assetLocation._assetModelObj;
        const _normalizedName = _modelName ? _modelName.trim().toLowerCase() : '';

        const matchedRate = standardRatesMap[`${_modelId}_${consumptionRateUnit}`] ||
                            standardRatesMap[`${_modelName}_${consumptionRateUnit}`] ||
                            standardRatesMap[`${_normalizedName}_${consumptionRateUnit}`];
        if (matchedRate) {
          standardRate = matchedRate.rate;
          standardRateUnit = matchedRate.unit;
          varianceNote = null; // clear any mismatch note set during the per-event loop
        }
      }

      // Calculate consumption rate from the ADJUSTED TOTALS (not averaging individual rates).
      // Adjusted = meter-change first-reading cycles excluded, so the under-counted run from
      // the faulty-meter period does not distort the month's weighted average.
      // Formula: Total Units Run / Total Fuel Consumed (for KM/L, KWH/L)
      //          Total Fuel Consumed / Total Units Run (for L/HOURS)
      let avgConsumptionRate = null;
      if (adjustedMeterRun > 0 && adjustedFuelConsumed > 0) {
        if (consumptionRateUnit === 'KWH/L' || consumptionRateUnit === 'KM/L') {
          avgConsumptionRate = adjustedMeterRun / adjustedFuelConsumed;
        } else if (consumptionRateUnit === 'L/HOURS') {
          avgConsumptionRate = adjustedFuelConsumed / adjustedMeterRun;
        }
      }

      // No-complete-cycle fallback: if a meter change left no usable cycle this month,
      // do NOT fall back to the distorted figure — leave the rate blank and warn.
      if (avgConsumptionRate === null && hasMeterChange && primaryUnit) {
        meterChangeNote = 'All full-tank cycles this month follow a meter change; no complete cycle remains to average the consumption rate.';
      }

      // Calculate variance - ALWAYS in Litres/Unit basis for standardization
      // Positive variance = consuming MORE fuel than expected = worse
      // Negative variance = consuming LESS fuel than expected = better
      let variance = null;
      let variancePercent = null;
      let totalFuelLoss = null;

      if (standardRate && avgConsumptionRate && standardRateUnit && consumptionRateUnit && standardRateUnit === consumptionRateUnit) {
        if (consumptionRateUnit === 'KM/L' || consumptionRateUnit === 'KWH/L') {
          // For Unit/Litre rates, convert to L/Unit conceptually for variance calculation
          // In L/Unit: actual_lpu = 1/actual, standard_lpu = 1/standard
          // variance_lpu% = (actual_lpu - standard_lpu) / standard_lpu * 100
          //               = ((1/A) - (1/S)) / (1/S) * 100 = (S - A) / A * 100
          variance = standardRate - avgConsumptionRate; // Keep in original units for display
          variancePercent = avgConsumptionRate > 0 ? ((standardRate - avgConsumptionRate) / avgConsumptionRate) * 100 : null;
          // Fuel loss is computed on the SAME (adjusted) basis as the averaged rate so the
          // figures are consistent with the excluded meter-change cycle.
          if (adjustedMeterRun > 0 && standardRate > 0) {
            const expectedFuel = adjustedMeterRun / standardRate;
            totalFuelLoss = adjustedFuelConsumed - expectedFuel;
          }
        } else if (consumptionRateUnit === 'L/HOURS') {
          // Already in L/Unit format, standard percentage change formula
          // variance% = (actual - standard) / standard * 100
          variance = avgConsumptionRate - standardRate;
          variancePercent = standardRate > 0 ? (variance / standardRate) * 100 : null;
          if (adjustedMeterRun > 0 && standardRate > 0) {
            const expectedFuel = standardRate * adjustedMeterRun;
            totalFuelLoss = adjustedFuelConsumed - expectedFuel;
          }
        }
      } else if (standardRate && avgConsumptionRate && standardRateUnit && consumptionRateUnit && standardRateUnit !== consumptionRateUnit) {
        varianceNote = `Consumption unit mismatch (${consumptionRateUnit} vs standard ${standardRateUnit})`;
      }

      snNo++;
      aggregatedData.push({
        key: locationKey,
        snNo,
        assetCode,
        altname: assetLocation.altname || null,
        ownercode: assetLocation.ownercode || null,
        assetType: (assetLocation.assetType && assetLocation.assetType.trim()) || '-',
        assetSubType: (assetLocation.assetSubType && assetLocation.assetSubType.trim()) || '-',
        assetModel: (assetLocation.assetModel && assetLocation.assetModel.trim()) || '-',
        location: assetLocation.location || '-',
        subLocation: assetLocation.subLocation || '-',
        totalRefills,
        totalFuelAdded,
        fullTankCount: fullTankEventsInRangeAtLocation.length,
        totalMeterRun,
        meterName: meterName || '-',
        totalFuelConsumed,
        // After-exclusion totals (meter-change first reading removed). When no meter change
        // occurred these equal the gross totals.
        adjustedMeterRun,
        adjustedFuelConsumed,
        excludedMeterRun,
        excludedFuelConsumed,
        excludedCycleCount,
        hasMeterChange,
        meterChangeNote,
        avgConsumptionRate,
        consumptionRateUnit: consumptionRateUnit || '-',
        standardRate,
        standardRateUnit: standardRateUnit || '-',
        variance,
        variancePercent,
        varianceNote,
        varianceThreshold,
        severity: calculateSeverity(variancePercent, varianceThreshold),
        totalFuelLoss,
        fullTankEventDetails,
        fullTankDebug,
        allFuelEntries,
        _assetModelObj: assetLocation._assetModelObj || null
      });
    });

    return aggregatedData;
  };

  const applyFilters = () => {
    let filtered = [...summaryData];
    
    // Hide assets with 0 refills (default behavior)
    if (hideZeroRefills) {
      filtered = filtered.filter(item => item.totalRefills > 0);
    }
    
    if (selectedAssetCode) {
      const searchTerm = selectedAssetCode.toLowerCase();
      filtered = filtered.filter(item => {
        const matchCode = item.assetCode && item.assetCode.toLowerCase().includes(searchTerm);
        const matchAltname = item.altname && item.altname.toLowerCase().includes(searchTerm);
        const matchOwnercode = item.ownercode && item.ownercode.toLowerCase().includes(searchTerm);
        return matchCode || matchAltname || matchOwnercode;
      });
    }

    if (excludedAssetCodes.length > 0) {
      const excludeSet = buildExcludeSet();
      filtered = filtered.filter(item => !isExcludedAggregatedItem(item, excludeSet));
    }

    if (selectedAssetType) {
      filtered = filtered.filter(item => item.assetType === selectedAssetType);
    }
    
    if (selectedAssetSubType) {
      filtered = filtered.filter(item => item.assetSubType === selectedAssetSubType);
    }
    
    if (selectedAssetModel) {
      filtered = filtered.filter(item => item.assetModel === selectedAssetModel);
    }
    
    if (selectedLocation) {
      filtered = filtered.filter(item => item.location === selectedLocation);
    }
    
    if (selectedSubLocation) {
      filtered = filtered.filter(item => item.subLocation === selectedSubLocation);
    }
    
    // Sort by: Severity, Variance (abs desc), Sub Location, Location, Asset Model, Asset Sub Type, Asset Type
    filtered.sort((a, b) => {
      // Severity first (ascending order: 1=Critical, 2=High, 3=Medium, 4=Low, 5=OK, 999=null)
      const severityA = a.severity?.order || 999;
      const severityB = b.severity?.order || 999;
      if (severityA !== severityB) return severityA - severityB;
      
      // Variance second (positive before negative, descending - worse first; null sorts last)
      const varA = (a.variancePercent != null) ? a.variancePercent : -Infinity;
      const varB = (b.variancePercent != null) ? b.variancePercent : -Infinity;
      if (varA !== varB) return varB - varA; // descending: positive first, then negative
      
      // Sub Location (ascending)
      const subLocA = (a.subLocation || '').toLowerCase();
      const subLocB = (b.subLocation || '').toLowerCase();
      if (subLocA !== subLocB) return subLocA.localeCompare(subLocB);
      
      // Location (ascending)
      const locA = (a.location || '').toLowerCase();
      const locB = (b.location || '').toLowerCase();
      if (locA !== locB) return locA.localeCompare(locB);
      
      // Asset Model (ascending)
      const modelA = (a.assetModel || '').toLowerCase();
      const modelB = (b.assetModel || '').toLowerCase();
      if (modelA !== modelB) return modelA.localeCompare(modelB);
      
      // Asset Sub Type (ascending)
      const subTypeA = (a.assetSubType || '').toLowerCase();
      const subTypeB = (b.assetSubType || '').toLowerCase();
      if (subTypeA !== subTypeB) return subTypeA.localeCompare(subTypeB);
      
      // Asset Type (ascending)
      const typeA = (a.assetType || '').toLowerCase();
      const typeB = (b.assetType || '').toLowerCase();
      return typeA.localeCompare(typeB);
    });
    
    // Re-number SN after sorting
    filtered = filtered.map((item, index) => ({
      ...item,
      snNo: index + 1
    }));
    
    setFilteredData(filtered);
  };

  const handleViewDetails = (record) => {
    setSelectedRecordDetails(record);
    setDetailModalVisible(true);
  };

  const handleOpenFullTankEntryDetails = (eventRecord, entryType) => {
    const entry = entryType === 'previous' ? eventRecord?.previousEventEntry : eventRecord?.currentEventEntry;
    if (!entry) return;

    const label = entryType === 'previous' ? 'Previous Full Tank' : 'Current Full Tank';
    setRefuelBreakdownData([entry]);
    setRefuelBreakdownTitle(`${label} Entry - ${selectedRecordDetails?.assetCode || ''} - ${entry.date}`);
    setRefuelBreakdownModalVisible(true);
  };

  // Opens the refuel-event breakup behind a single full-tank cycle's Fuel Consumed / Meter Run.
  const handleOpenCycleBreakup = (cycleRecord) => {
    if (!cycleRecord || !Array.isArray(cycleRecord.cycleBreakdown) || cycleRecord.cycleBreakdown.length === 0) return;
    setCycleBreakupData(cycleRecord.cycleBreakdown);
    setCycleBreakupInfo({
      assetCode: selectedRecordDetails?.assetCode || '',
      previousDate: cycleRecord.previousDate,
      currentDate: cycleRecord.date,
      fuelConsumed: cycleRecord.fuelConsumed,
      meterRun: cycleRecord.meterRun,
      meterName: cycleRecord.meterName,
      rateUnit: cycleRecord.rateUnit,
      previousMeterCalcReading: cycleRecord.previousMeterCalcReading,
      currentMeterCalcReading: cycleRecord.currentMeterCalcReading,
    });
    setCycleBreakupVisible(true);
  };

  // Format number with thousand separator and 2 decimals for PDF
  const formatNum = (n) => {
    if (n === null || n === undefined || isNaN(n)) return '-';
    return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const drawSeverityGuidePdf = (doc, startY, pageWidth) => {
    const boxX = 20;
    const boxY = startY;
    const boxWidth = pageWidth - 40;
    const boxHeight = 62;

    doc.setFillColor(245, 249, 255);
    doc.setDrawColor(189, 214, 255);
    doc.roundedRect(boxX, boxY, boxWidth, boxHeight, 3, 3, 'FD');

    doc.setFontSize(9.5);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(23, 59, 115);
    doc.text('Severity Logic', boxX + 6, boxY + 8);

    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(79, 95, 122);
    doc.text('T = threshold, D = signed deviation, A = |D|. D > 0 = worse, D < 0 = better, OK when A < T.', boxX + 6, boxY + 17);

    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text('Worse:', boxX + 6, boxY + 29);
    doc.setFont(undefined, 'normal');
    doc.text('LOW T to < T+10 | MED T+10 to < T+20 | HIGH T+20 to < T+30 | CRIT >= T+30', boxX + 28, boxY + 29);

    doc.setFont(undefined, 'bold');
    doc.text('Better:', boxX + 6, boxY + 40);
    doc.setFont(undefined, 'normal');
    doc.text('LOW T to < T+20 | MED T+20 to < T+30 | HIGH T+30 to < T+50 | CRIT >= T+50', boxX + 28, boxY + 40);

    doc.setFont(undefined, 'bold');
    doc.text('Sort:', boxX + 6, boxY + 51);
    doc.setFont(undefined, 'normal');
    doc.text('Severity first, then absolute variance, then Sub Location, Location, Model, Sub Type, Type.', boxX + 22, boxY + 51);

    return boxY + boxHeight + 8;
  };

  const drawSeverityGuidePdfAtEnd = (doc, pageWidth, pageHeight) => {
    doc.addPage();
    doc.setPage(doc.getNumberOfPages());

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Severity Reference', 20, 24);

    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('How severity, colors, and sorting are applied in this report.', 20, 34);

    const endY = drawSeverityGuidePdf(doc, 48, pageWidth);

    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(23, 59, 115);
    doc.text('Color meaning', 20, endY + 6);

    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text('Warm colors indicate worse-than-standard consumption. Green / cyan / purple indicate better-than-standard behaviour.', 20, endY + 16);
    doc.text('Better-side CRITICAL means the result is unusually good and may indicate logging, fill, or data-quality mismatch rather than operational improvement.', 20, endY + 24);

    return endY + 24;
  };

  const loadImageToPdf = async (doc, logoPath = '/Logo-svg copy.png') => {
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';

    await new Promise((resolve) => {
      logoImg.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = logoImg.width;
          canvas.height = logoImg.height;
          ctx.drawImage(logoImg, 0, 0);
          const logoDataUrl = canvas.toDataURL('image/png');

          const aspectRatio = logoImg.width / logoImg.height;
          const logoHeight = 40;
          const logoWidth = logoHeight * aspectRatio;

          doc.addImage(logoDataUrl, 'PNG', 20, 15, logoWidth, logoHeight);
        } catch (error) {
          console.warn('Could not add logo to PDF:', error);
        }
        resolve();
      };

      logoImg.onerror = () => resolve();
      logoImg.src = logoPath;
    });
  };

  const fetchFilteredMonthData = async (month) => {
    const startIST = month.clone().startOf('month').utcOffset('+05:30', true);
    const endIST = month.clone().endOf('month').utcOffset('+05:30', true);
    const startTimestamp = startIST.valueOf();
    const endTimestamp = endIST.valueOf();
    const startISO = startIST.toISOString();
    const endISO = endIST.toISOString();

    try {
      const result = await apiPost('/fuel/mileage-report', {
        asset: null,
        location: 'all',
        subLocation: 'all',
        periodFrom: startISO,
        periodTo: endISO,
      });

      if (!result.success) return [];

      const bowserFiltered = filterOutBowserEntries(result.data || [], fuelBowserMap);
      let aggregated = aggregateMonthlyData(bowserFiltered, startTimestamp, endTimestamp, {});

      if (hideZeroRefills) {
        aggregated = aggregated.filter(item => item.totalRefills > 0);
      }
      if (selectedAssetCode) {
        const searchTerm = selectedAssetCode.toLowerCase();
        aggregated = aggregated.filter(item => {
          const matchCode = item.assetCode && item.assetCode.toLowerCase().includes(searchTerm);
          const matchAltname = item.altname && item.altname.toLowerCase().includes(searchTerm);
          const matchOwnercode = item.ownercode && item.ownercode.toLowerCase().includes(searchTerm);
          return matchCode || matchAltname || matchOwnercode;
        });
      }
      if (excludedAssetCodes.length > 0) {
        const excludeSet = buildExcludeSet();
        aggregated = aggregated.filter(item => !isExcludedAggregatedItem(item, excludeSet));
      }
      if (selectedAssetType) aggregated = aggregated.filter(item => item.assetType === selectedAssetType);
      if (selectedAssetSubType) aggregated = aggregated.filter(item => item.assetSubType === selectedAssetSubType);
      if (selectedAssetModel) aggregated = aggregated.filter(item => item.assetModel === selectedAssetModel);
      if (selectedLocation) aggregated = aggregated.filter(item => item.location === selectedLocation);
      if (selectedSubLocation) aggregated = aggregated.filter(item => item.subLocation === selectedSubLocation);

      return aggregated;
    } catch (error) {
      console.warn(`Failed to fetch data for ${month.format('MMM YYYY')}:`, error);
      return [];
    }
  };

  const buildTrendDataBundle = async () => {
    const currentMonth = selectedMonth.clone();
    const prevMonth = currentMonth.clone().subtract(1, 'month');
    const month2Ago = currentMonth.clone().subtract(2, 'month');
    const month3Ago = currentMonth.clone().subtract(3, 'month');

    const [prevMonthData, month2AgoData, month3AgoData] = await Promise.all([
      fetchFilteredMonthData(prevMonth),
      fetchFilteredMonthData(month2Ago),
      fetchFilteredMonthData(month3Ago)
    ]);

    const createLookupMap = (data) => {
      const map = {};
      data.forEach(item => {
        const key = `${item.assetCode}_${item.location || '-'}_${item.subLocation || '-'}`;
        map[key] = item;
      });
      return map;
    };

    const prevMonthMap = createLookupMap(prevMonthData);
    const month2AgoMap = createLookupMap(month2AgoData);
    const month3AgoMap = createLookupMap(month3AgoData);

    const trendData = filteredData.map(item => {
      const key = `${item.assetCode}_${item.location || '-'}_${item.subLocation || '-'}`;
      const prev = prevMonthMap[key] || {};
      const m2 = month2AgoMap[key] || {};
      const m3 = month3AgoMap[key] || {};

      const currRefills = item.totalRefills || 0;
      const currFuelAdded = item.totalFuelAdded || 0;
      const currFuelConsumed = item.totalFuelConsumed || 0;
      const currFuelLoss = item.totalFuelLoss || 0;
      const currMeterRun = item.totalMeterRun || 0;
      const currAvgRate = item.avgConsumptionRate || 0;

      const prevRefills = prev.totalRefills || 0;
      const prevFuelAdded = prev.totalFuelAdded || 0;
      const prevFuelConsumed = prev.totalFuelConsumed || 0;
      const prevFuelLoss = prev.totalFuelLoss || 0;
      const prevMeterRun = prev.totalMeterRun || 0;
      const prevAvgRate = prev.avgConsumptionRate || 0;

      const avg3Refills = Math.round(((prev.totalRefills || 0) + (m2.totalRefills || 0) + (m3.totalRefills || 0)) / 3);
      const avg3FuelAdded = Math.round((((prev.totalFuelAdded || 0) + (m2.totalFuelAdded || 0) + (m3.totalFuelAdded || 0)) / 3) * 100) / 100;
      const avg3FuelConsumed = Math.round((((prev.totalFuelConsumed || 0) + (m2.totalFuelConsumed || 0) + (m3.totalFuelConsumed || 0)) / 3) * 100) / 100;
      const avg3FuelLoss = Math.round((((prev.totalFuelLoss || 0) + (m2.totalFuelLoss || 0) + (m3.totalFuelLoss || 0)) / 3) * 100) / 100;
      const avg3MeterRun = Math.round((((prev.totalMeterRun || 0) + (m2.totalMeterRun || 0) + (m3.totalMeterRun || 0)) / 3) * 100) / 100;
      const avg3AvgRate = Math.round((((prev.avgConsumptionRate || 0) + (m2.avgConsumptionRate || 0) + (m3.avgConsumptionRate || 0)) / 3) * 100) / 100;

      const momRefills = prevRefills > 0 ? ((currRefills - prevRefills) / prevRefills) * 100 : (currRefills > 0 ? 100 : 0);
      const momFuelAdded = prevFuelAdded > 0 ? ((currFuelAdded - prevFuelAdded) / prevFuelAdded) * 100 : (currFuelAdded > 0 ? 100 : 0);
      const momFuelConsumed = prevFuelConsumed > 0 ? ((currFuelConsumed - prevFuelConsumed) / prevFuelConsumed) * 100 : (currFuelConsumed > 0 ? 100 : 0);
      const momFuelLoss = prevFuelLoss !== 0 ? ((currFuelLoss - prevFuelLoss) / Math.abs(prevFuelLoss)) * 100 : (currFuelLoss !== 0 ? 100 : 0);
      const momMeterRun = prevMeterRun > 0 ? ((currMeterRun - prevMeterRun) / prevMeterRun) * 100 : (currMeterRun > 0 ? 100 : 0);

      let momAvgRate = 0;
      const rateUnit = item.consumptionRateUnit || '';
      if (currAvgRate > 0 && prevAvgRate > 0) {
        if (rateUnit === 'KM/L' || rateUnit === 'KWH/L') {
          const currLPerUnit = 1 / currAvgRate;
          const prevLPerUnit = 1 / prevAvgRate;
          momAvgRate = ((currLPerUnit - prevLPerUnit) / prevLPerUnit) * 100;
        } else {
          momAvgRate = ((currAvgRate - prevAvgRate) / prevAvgRate) * 100;
        }
      } else if (currAvgRate > 0) {
        momAvgRate = 100;
      }

      return {
        ...item,
        prev, m2, m3,
        prevRefills, prevFuelAdded, prevFuelConsumed, prevFuelLoss,
        prevMeterRun, prevAvgRate,
        avg3Refills, avg3FuelAdded, avg3FuelConsumed, avg3FuelLoss,
        avg3MeterRun, avg3AvgRate,
        momRefills, momFuelAdded, momFuelConsumed, momFuelLoss,
        momMeterRun, momAvgRate
      };
    });

    const calcSummaryTotals = (data) => ({
      totalAssets: data.length,
      totalRefills: data.reduce((sum, d) => sum + (d.totalRefills || 0), 0),
      totalFuelAdded: data.reduce((sum, d) => sum + (d.totalFuelAdded || 0), 0),
      totalFuelConsumed: data.reduce((sum, d) => sum + (d.totalFuelConsumed || 0), 0),
      totalFuelLoss: data.reduce((sum, d) => sum + (d.totalFuelLoss || 0), 0),
      fullTankCount: data.reduce((sum, d) => sum + (d.fullTankCount || 0), 0)
    });

    const currentTotals = calcSummaryTotals(filteredData);
    const prevTotals = calcSummaryTotals(prevMonthData);
    const m2Totals = calcSummaryTotals(month2AgoData);
    const m3Totals = calcSummaryTotals(month3AgoData);

    const avg3Totals = {
      totalRefills: (prevTotals.totalRefills + m2Totals.totalRefills + m3Totals.totalRefills) / 3,
      totalFuelAdded: (prevTotals.totalFuelAdded + m2Totals.totalFuelAdded + m3Totals.totalFuelAdded) / 3,
      totalFuelConsumed: (prevTotals.totalFuelConsumed + m2Totals.totalFuelConsumed + m3Totals.totalFuelConsumed) / 3,
      totalFuelLoss: (prevTotals.totalFuelLoss + m2Totals.totalFuelLoss + m3Totals.totalFuelLoss) / 3,
      fullTankCount: (prevTotals.fullTankCount + m2Totals.fullTankCount + m3Totals.fullTankCount) / 3
    };

    const momRefillsChange = prevTotals.totalRefills > 0 ? ((currentTotals.totalRefills - prevTotals.totalRefills) / prevTotals.totalRefills) * 100 : 0;
    const momFuelAddedChange = prevTotals.totalFuelAdded > 0 ? ((currentTotals.totalFuelAdded - prevTotals.totalFuelAdded) / prevTotals.totalFuelAdded) * 100 : 0;
    const momFuelConsumedChange = prevTotals.totalFuelConsumed > 0 ? ((currentTotals.totalFuelConsumed - prevTotals.totalFuelConsumed) / prevTotals.totalFuelConsumed) * 100 : 0;
    const momFullTankChange = prevTotals.fullTankCount > 0 ? ((currentTotals.fullTankCount - prevTotals.fullTankCount) / prevTotals.fullTankCount) * 100 : 0;
    const momFuelLossChange = prevTotals.totalFuelLoss !== 0 ? ((currentTotals.totalFuelLoss - prevTotals.totalFuelLoss) / Math.abs(prevTotals.totalFuelLoss)) * 100 : 0;

    return {
      currentMonth,
      prevMonth,
      month2Ago,
      month3Ago,
      prevMonthData,
      month2AgoData,
      month3AgoData,
      trendData,
      currentTotals,
      prevTotals,
      m2Totals,
      m3Totals,
      avg3Totals,
      momRefillsChange,
      momFuelAddedChange,
      momFuelConsumedChange,
      momFullTankChange,
      momFuelLossChange
    };
  };

  const getExecutiveImpactSummary = (rows) => {
    const loss = rows.reduce((sum, row) => sum + ((row.totalFuelLoss || 0) > 0 ? row.totalFuelLoss : 0), 0);
    const gain = rows.reduce((sum, row) => sum + ((row.totalFuelLoss || 0) < 0 ? Math.abs(row.totalFuelLoss) : 0), 0);
    const net = loss - gain;
    return { loss, gain, net };
  };

  const getExecutiveActionSignal = (row) => {
    const fuelLoss = row.totalFuelLoss || 0;
    const severityLevel = row.severity?.level;
    const isBetter = row.severity?.isBetter;

    if (fuelLoss > 0 && (severityLevel === 'CRITICAL' || severityLevel === 'HIGH')) {
      return 'High fuel loss and high severity';
    }
    if (fuelLoss > 0 && severityLevel === 'MEDIUM') {
      return 'Large fuel loss despite medium severity';
    }
    if (fuelLoss > 0) {
      return 'Fuel loss impact worth review';
    }
    if (fuelLoss < 0 && isBetter && (severityLevel === 'CRITICAL' || severityLevel === 'HIGH')) {
      return 'High fuel gain, validate and replicate';
    }
    if (fuelLoss < 0) {
      return 'High gain may indicate data / fill mismatch';
    }
    return 'Monitor trend';
  };

  const handleDownloadPDF = async () => {
    try {
      // Create PDF in landscape mode with pt units
      const doc = new jsPDF('landscape', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;
      
      // Calculate available width for table
      const tableMargin = 30;
      const availableWidth = pageWidth - tableMargin;
      
      // Load and add logo
      const loadLogo = () => {
        return new Promise((resolve) => {
          const logoPath = '/Logo-svg copy.png';
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          
          logoImg.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = logoImg.width;
              canvas.height = logoImg.height;
              ctx.drawImage(logoImg, 0, 0);
              const logoDataUrl = canvas.toDataURL('image/png');
              
              const aspectRatio = logoImg.width / logoImg.height;
              const logoHeight = 40;
              const logoWidth = logoHeight * aspectRatio;
              
              doc.addImage(logoDataUrl, 'PNG', 20, 15, logoWidth, logoHeight);
              resolve();
            } catch (error) {
              console.warn('Could not add logo to PDF:', error);
              resolve();
            }
          };
          
          logoImg.onerror = () => {
            console.warn('Could not load logo image');
            resolve();
          };
          
          logoImg.src = logoPath;
        });
      };
      
      await loadLogo();
      
      // Header Section
      const headerY = 25;
      
      // Title - Right aligned
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      const title = `Monthly Full Tank Report - ${selectedMonth.format('MMMM YYYY')}`;
      doc.text(title, pageWidth - 20, headerY, { align: 'right' });
      
      // Company Name
      try {
        const userData = getUserData();
        if (userData?.organisation) {
          const companyName = typeof userData.organisation === 'string' 
            ? userData.organisation 
            : (userData.organisation.name || '');
          if (companyName) {
            doc.setFontSize(11);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(companyName, pageWidth - 20, headerY + 18, { align: 'right' });
            doc.setTextColor(0, 0, 0);
          }
        }
      } catch (error) {
        console.warn('Could not retrieve company name:', error);
      }
      
      // Generated date
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text(`Generated: ${moment().format('DD MMM YYYY, HH:mm')}`, pageWidth - 20, headerY + 32, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      
      yPosition = headerY + 50;
      
      // Display Active Filters (if any)
      const pdfActiveFilters = getActiveFilters();
      if (pdfActiveFilters.length > 0) {
        // Filter section background
        doc.setFillColor(240, 245, 255);
        doc.roundedRect(20, yPosition, pageWidth - 40, 22, 3, 3, 'F');
        doc.setDrawColor(214, 228, 255);
        doc.roundedRect(20, yPosition, pageWidth - 40, 22, 3, 3, 'S');
        
        // Filter label
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(24, 144, 255);
        doc.text('Filtered Report:', 28, yPosition + 14);
        
        // Filter values
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
        const filterText = pdfActiveFilters.map(f => `${f.label}: ${f.value}`).join('  |  ');
        doc.text(filterText, 105, yPosition + 14);
        
        yPosition += 30;
      }
      
      // Summary Statistics Cards
      const cardHeight = 55;
      const startX = 20;
      const endX = pageWidth - 20;
      const totalCardArea = endX - startX;
      const cardSpacing = 12;
      const numCards = 6;
      const cardWidth = (totalCardArea - (cardSpacing * (numCards - 1))) / numCards;
      
      // Card colors
      const cardColors = [
        [240, 248, 255], // Light blue - Total Assets
        [255, 250, 240], // Light orange - Total Refills
        [240, 255, 240], // Light green - Fuel Added
        [255, 245, 238], // Light peach - Full Tank Events
        [240, 240, 255], // Light purple - Fuel Consumed
        [255, 240, 245]  // Light pink - Fuel Loss
      ];
      
      // Card 1: Total Assets
      doc.setFillColor(...cardColors[0]);
      doc.roundedRect(startX, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(startX, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Total Assets', startX + 8, yPosition + 14);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(24, 144, 255);
      doc.text(`${filteredData.length}`, startX + 8, yPosition + 38);
      
      // Card 2: Total Refills
      const card2X = startX + cardWidth + cardSpacing;
      doc.setFillColor(...cardColors[1]);
      doc.roundedRect(card2X, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(card2X, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Total Refills', card2X + 8, yPosition + 14);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(250, 140, 22);
      doc.text(`${Math.round(totals.totalRefills)}`, card2X + 8, yPosition + 38);
      
      // Card 3: Total Fuel Added
      const card3X = startX + (cardWidth + cardSpacing) * 2;
      doc.setFillColor(...cardColors[2]);
      doc.roundedRect(card3X, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(card3X, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Total Fuel Added', card3X + 8, yPosition + 14);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(82, 196, 26);
      doc.text(`${formatNum(totals.totalFuelAdded)} L`, card3X + 8, yPosition + 38);
      
      // Card 4: Full Tank Events
      const card4X = startX + (cardWidth + cardSpacing) * 3;
      doc.setFillColor(...cardColors[3]);
      doc.roundedRect(card4X, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(card4X, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Full Tank Events', card4X + 8, yPosition + 14);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(250, 84, 28);
      doc.text(`${totals.fullTankCount}`, card4X + 8, yPosition + 38);
      
      // Card 5: Total Fuel Consumed
      const card5X = startX + (cardWidth + cardSpacing) * 4;
      doc.setFillColor(...cardColors[4]);
      doc.roundedRect(card5X, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(card5X, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Fuel Consumed', card5X + 8, yPosition + 14);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(114, 46, 209);
      doc.text(`${formatNum(totals.totalFuelConsumed)} L`, card5X + 8, yPosition + 38);
      
      // Card 6: Total Fuel Loss
      const card6X = startX + (cardWidth + cardSpacing) * 5;
      doc.setFillColor(...cardColors[5]);
      doc.roundedRect(card6X, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(card6X, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      const fuelLossLabel = totals.totalFuelLoss >= 0 ? 'Total Fuel Loss' : 'Total Fuel Saved';
      doc.text(fuelLossLabel, card6X + 8, yPosition + 14);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(totals.totalFuelLoss >= 0 ? 255 : 82, totals.totalFuelLoss >= 0 ? 77 : 196, totals.totalFuelLoss >= 0 ? 79 : 26);
      doc.text(`${formatNum(Math.abs(totals.totalFuelLoss))} L`, card6X + 8, yPosition + 38);
      
      yPosition += cardHeight + 25;
      
      // Table Title
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Asset-wise Monthly Summary', 20, yPosition);
      yPosition += 15;

      // Helper to format asset code with altname/ownercode.
      // Appends '*' for assets with a meter change this month (see footnote below the table).
      const formatAssetCodeForPdf = (row) => {
        const parts = [`${row.assetCode || '-'}${row.hasMeterChange ? ' *' : ''}`];
        if (row.altname) parts.push(row.altname);
        if (row.ownercode) parts.push(row.ownercode);
        return parts.join('\n');
      };

      // Helper to format severity for PDF (ASCII-safe, no Unicode arrows)
      const formatSeverityForPdf = (severity) => {
        if (!severity || !severity.level) return '-';
        if (severity.level === 'OK') return 'OK';
        // Use (+) for worse (higher consumption) and (-) for better (lower consumption)
        const indicator = severity.isBetter ? ' (-)' : ' (+)';
        return severity.level + indicator;
      };

      // Prepare table data
      const tableData = filteredData.map(row => [
        row.snNo,
        formatSeverityForPdf(row.severity),
        formatAssetCodeForPdf(row),
        row.assetType || '-',
        row.assetSubType || '-',
        row.assetModel || '-',
        row.location || '-',
        row.subLocation || '-',
        Math.round(row.totalRefills),
        formatNum(row.totalFuelAdded),
        row.fullTankCount,
        // Meter Run: show gross and, when a meter change occurred, the after-change figure
        // used for the average ("adj"). Keeps both numbers without adding a column.
        row.totalMeterRun > 0
          ? (row.hasMeterChange ? `${row.totalMeterRun.toFixed(2)}\n(adj: ${row.adjustedMeterRun.toFixed(2)})` : row.totalMeterRun.toFixed(2))
          : '-',
        row.meterName || '-',
        row.totalFuelConsumed > 0
          ? (row.hasMeterChange ? `${formatNum(row.totalFuelConsumed)}\n(adj: ${formatNum(row.adjustedFuelConsumed)})` : formatNum(row.totalFuelConsumed))
          : '-',
        row.avgConsumptionRate
          ? `${row.avgConsumptionRate.toFixed(2)} ${row.consumptionRateUnit}`
          : (row.meterChangeNote ? 'No complete cycle' : '-'),
        row.standardRate ? `${row.standardRate.toFixed(2)} ${row.standardRateUnit}` : '-',
        row.variancePercent !== null ? `${row.variancePercent.toFixed(2)}%` : '-',
        row.totalFuelLoss !== null ? `${row.totalFuelLoss >= 0 ? '+' : '-'}${formatNum(Math.abs(row.totalFuelLoss))}` : '-'
      ]);

      const tableColumns = [
        'SN', 'Severity', 'Asset Code', 'Type', 'Sub Type', 'Model', 'Location', 'Sub Location',
        'Refills', 'Fuel Added (L)', 'Full Tank', 'Meter Run', 'Meter', 
        'Consumed (L)', 'Avg Rate', 'Std Rate', 'Variance', 'Fuel Loss (L)'
      ];

      // Add table with improved styling
      autoTable(doc, {
        startY: yPosition,
        head: [tableColumns],
        body: tableData,
        theme: 'striped',
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        styles: { 
          fontSize: 6, 
          cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
          overflow: 'linebreak',
          halign: 'left',
          valign: 'middle',
          lineWidth: 0.5,
          lineColor: [220, 220, 220]
        },
        headStyles: { 
          fillColor: [52, 73, 94], 
          textColor: 255, 
          fontStyle: 'bold', 
          fontSize: 6.5,
          halign: 'center',
          valign: 'middle',
          minCellHeight: 15,
          cellPadding: { top: 5, bottom: 5, left: 4, right: 4 }
        },
        bodyStyles: {
          fontSize: 6,
          halign: 'left',
          valign: 'middle',
          minCellHeight: 12
        },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        margin: { left: pageMargin, right: pageMargin, top: 20, bottom: 20 },
        tableWidth: pageWidth - (pageMargin * 2),
        columnStyles: {
          0: { cellWidth: 22, halign: 'center' },  // SN
          1: { cellWidth: 52, halign: 'center' },  // Severity (wider for CRITICAL to fit)
          2: { cellWidth: 58, halign: 'left' },    // Asset Code (with altname/ownercode)
          3: { cellWidth: 42, halign: 'left' },    // Type
          4: { cellWidth: 45, halign: 'left' },    // Sub Type
          5: { cellWidth: 50, halign: 'left' },    // Model
          6: { cellWidth: 45, halign: 'left' },    // Location
          7: { cellWidth: 45, halign: 'left' },    // Sub Location
          8: { cellWidth: 32, halign: 'right' },   // Refills
          9: { cellWidth: 45, halign: 'right' },   // Fuel Added
          10: { cellWidth: 35, halign: 'right' },  // Full Tank
          11: { cellWidth: 42, halign: 'right' },  // Meter Run
          12: { cellWidth: 35, halign: 'left' },   // Meter
          13: { cellWidth: 45, halign: 'right' },  // Consumed
          14: { cellWidth: 52, halign: 'right' },  // Avg Rate
          15: { cellWidth: 52, halign: 'right' },  // Std Rate
          16: { cellWidth: 42, halign: 'right' },  // Variance
          17: { cellWidth: 45, halign: 'right' }   // Fuel Loss
        },
        didParseCell: function(data) {
          const columnIndex = data.column.index;
          
          // Severity column (1) - horizontally centered, vertically top-aligned like other columns
          if (columnIndex === 1) {
            data.cell.styles.halign = 'center';
            data.cell.styles.valign = 'top';
          }
          
          if (data.row.section !== 'body') return;
          
          const rowIndex = data.row.index;
          
          // Severity column (1) - text color only
          if (columnIndex === 1 && rowIndex < filteredData.length) {
            const severity = filteredData[rowIndex].severity;
            if (severity && severity.level) {
              data.cell.styles.fontStyle = 'bold';
              if (severity.level === 'CRITICAL') {
                data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [255, 77, 79];
              } else if (severity.level === 'HIGH') {
                data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [250, 84, 28];
              } else if (severity.level === 'MEDIUM') {
                data.cell.styles.textColor = severity.isBetter ? [19, 194, 194] : [250, 173, 20];
              } else if (severity.level === 'LOW') {
                data.cell.styles.textColor = severity.isBetter ? [82, 196, 26] : [24, 144, 255];
              } else if (severity.level === 'OK') {
                data.cell.styles.textColor = [82, 196, 26];
              }
            }
          }
          
          // Color code Variance column (16)
          if (columnIndex === 16 && rowIndex < filteredData.length) {
            const variance = filteredData[rowIndex].variancePercent;
            if (variance !== null && variance !== undefined) {
              if (variance > 0) {
                data.cell.styles.textColor = [255, 77, 79]; // Red for positive variance (worse)
              } else if (variance < 0) {
                data.cell.styles.textColor = [82, 196, 26]; // Green for negative variance (better)
              }
            }
          }
          
          // Color code Fuel Loss column (17)
          if (columnIndex === 17 && rowIndex < filteredData.length) {
            const fuelLoss = filteredData[rowIndex].totalFuelLoss;
            if (fuelLoss !== null && fuelLoss !== undefined) {
              if (fuelLoss > 0) {
                data.cell.styles.textColor = [255, 77, 79]; // Red for loss
              } else if (fuelLoss < 0) {
                data.cell.styles.textColor = [82, 196, 26]; // Green for saved
              }
            }
          }
        }
      });

      // Meter-change footnote: documents the consumption-averaging exclusion and lists assets.
      const meterChangeAssets = filteredData.filter(r => r.hasMeterChange).map(r => r.assetCode);
      if (meterChangeAssets.length > 0) {
        let noteY = (doc.lastAutoTable && doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY : yPosition) + 16;
        if (noteY > pageHeight - 60) { doc.addPage(); noteY = 40; }
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(135, 77, 0);
        const noteText = '* Meter change during the month: the first full-tank reading after the meter change is omitted from the month\'s averaged consumption (both fuel consumed and meter run), because it does not include the running hours/distance accumulated while the meter was being changed. "adj" shows the after-meter-change figures used for the average. Affected asset(s): ' + meterChangeAssets.join(', ');
        const wrapped = doc.splitTextToSize(noteText, pageWidth - 40);
        doc.text(wrapped, 20, noteY);
        doc.setTextColor(0, 0, 0);
      }

      drawSeverityGuidePdfAtEnd(doc, pageWidth, pageHeight);

      // Footer with page numbers
      const formatTrendSeverity = (severity) => {
        if (!severity || !severity.level) return '-';
        if (severity.level === 'OK') return 'OK';
        const indicator = severity.isBetter ? ' (-)' : ' (+)';
        return severity.level + indicator;
      };

      const formatAssetCodeForTrendPdf = (row) => {
        const parts = [row.assetCode || '-'];
        if (row.altname) parts.push(row.altname);
        if (row.ownercode) parts.push(row.ownercode);
        return parts.join('\n');
      };

      const createExecutiveAnnexurePdf = async () => {
        const annexureDoc = new jsPDF('landscape', 'pt', 'a4');
        const annexurePageWidth = annexureDoc.internal.pageSize.getWidth();
        const annexurePageHeight = annexureDoc.internal.pageSize.getHeight();
        let annexureY = 20;

        await loadImageToPdf(annexureDoc);

        const headerY = 25;
        annexureDoc.setFontSize(18);
        annexureDoc.setFont(undefined, 'bold');
        annexureDoc.text('Executive Fuel Trend Report Annexure', annexurePageWidth - 20, headerY, { align: 'right' });
        annexureDoc.setFontSize(11);
        annexureDoc.setFont(undefined, 'normal');
        annexureDoc.setTextColor(80, 80, 80);
        annexureDoc.text(`${selectedMonth.format('MMMM YYYY')}`, annexurePageWidth - 20, headerY + 18, { align: 'right' });
        annexureDoc.setFontSize(8);
        annexureDoc.setTextColor(120, 120, 120);
        annexureDoc.text(`Generated: ${moment().format('DD MMM YYYY, HH:mm')}`, annexurePageWidth - 20, headerY + 44, { align: 'right' });
        annexureDoc.setTextColor(0, 0, 0);
        annexureY = headerY + 60;

        annexureY = 30 + 34;
        annexureDoc.setFontSize(14);
        annexureDoc.setFont(undefined, 'bold');
        annexureDoc.setTextColor(...palette.ink);
        annexureDoc.text('Asset-wise Trend Analysis Detail', 20, 30);
        annexureDoc.setFontSize(8);
        annexureDoc.setFont(undefined, 'normal');
        annexureDoc.setTextColor(...palette.mutedInk);
        annexureDoc.text('Detailed asset-level trend view with month-on-month and 3M comparison.', 20, 42);
        annexureDoc.setDrawColor(...palette.line);
        annexureDoc.setLineWidth(1);
        annexureDoc.line(20, 49, annexurePageWidth - 20, 49);
        annexureDoc.setTextColor(0, 0, 0);

        const detailTableColumns = [
          'SN', 'Severity', 'Asset Code', 'Type', 'Sub Type', 'Model', 'Location', 'Sub Location',
          'Refills', 'Fuel Added (L)', 'Full Tank', 'Meter Run', 'Meter',
          'Consumed (L)', 'Avg Rate', 'Std Rate', 'Variance', 'Fuel Loss (L)'
        ];

        const detailTableData = trendData.map((row, idx) => {
          const formatWithTrend = (curr, prev, mom, avg3, useThousands = false, decimals = 2, suffix = '') => {
            const fmt = (v) => useThousands ? formatNum(v) : (typeof v === 'number' ? (decimals === 0 ? Math.round(v).toString() : v.toFixed(decimals)) : String(v));
            const currStr = fmt(curr) + suffix;
            const prevStr = fmt(prev);
            const avg3Str = fmt(avg3);
            const momSign = mom >= 0 ? '+' : '';
            return `${currStr}\nPrev: ${prevStr}\nMoM: ${momSign}${mom.toFixed(0)}%\n3M Avg: ${avg3Str}`;
          };

          const meterRunVal = row.totalMeterRun > 0 ? row.totalMeterRun : 0;
          const meterRunPrev = row.prevMeterRun || 0;
          const avgRateVal = row.avgConsumptionRate || 0;
          const avgRatePrev = row.prevAvgRate || 0;
          const rateUnit = row.consumptionRateUnit || '';

          return [
            idx + 1,
            formatTrendSeverity(row.severity),
            formatAssetCodeForTrendPdf(row),
            row.assetType || '-',
            row.assetSubType || '-',
            row.assetModel || '-',
            row.location || '-',
            row.subLocation || '-',
            formatWithTrend(row.totalRefills, row.prevRefills, row.momRefills, row.avg3Refills, false, 0),
            formatWithTrend(row.totalFuelAdded, row.prevFuelAdded, row.momFuelAdded, row.avg3FuelAdded, true),
            row.fullTankCount,
            meterRunVal > 0 ? formatWithTrend(meterRunVal, meterRunPrev, row.momMeterRun, row.avg3MeterRun, false, 2) : '-',
            row.meterName || '-',
            formatWithTrend(row.totalFuelConsumed, row.prevFuelConsumed, row.momFuelConsumed, row.avg3FuelConsumed, true),
            avgRateVal > 0 ? formatWithTrend(avgRateVal, avgRatePrev, row.momAvgRate, row.avg3AvgRate, false, 2, rateUnit ? ` ${rateUnit}` : '') : '-',
            row.standardRate ? `${row.standardRate.toFixed(2)} ${row.standardRateUnit}` : '-',
            row.variancePercent !== null ? `${row.variancePercent.toFixed(1)}%` : '-',
            row.totalFuelLoss !== null ? formatWithTrend(row.totalFuelLoss, row.prevFuelLoss, row.momFuelLoss, row.avg3FuelLoss, true) : '-'
          ];
        });

        const detailTrendColumns = [8, 9, 11, 13, 14, 17];
        const cellTextStore = {};

        autoTable(annexureDoc, {
          startY: annexureY,
          head: [detailTableColumns],
          body: detailTableData,
          theme: 'striped',
          rowPageBreak: 'avoid',
          showHead: 'everyPage',
          styles: {
            fontSize: 5.2,
            cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
            overflow: 'linebreak',
            halign: 'left',
            valign: 'top',
            lineWidth: 0.5,
            lineColor: palette.line
          },
          headStyles: {
            fillColor: palette.navy,
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 5.8,
            halign: 'center',
            valign: 'middle',
            minCellHeight: 15,
            cellPadding: { top: 4, bottom: 4, left: 3, right: 3 }
          },
          bodyStyles: { fontSize: 5.2, minCellHeight: 38, valign: 'top' },
          alternateRowStyles: { fillColor: palette.panel },
          margin: { left: pageMargin, right: pageMargin, top: 20, bottom: 20 },
          tableWidth: annexurePageWidth - (pageMargin * 2),
          columnStyles: {
            0: { cellWidth: 18, halign: 'center' },
            1: { cellWidth: 48, halign: 'center' },
            2: { cellWidth: 48, halign: 'left' },
            3: { cellWidth: 38, halign: 'left' },
            4: { cellWidth: 42, halign: 'left' },
            5: { cellWidth: 45, halign: 'left' },
            6: { cellWidth: 42, halign: 'left' },
            7: { cellWidth: 42, halign: 'left' },
            8: { cellWidth: 52, halign: 'right' },
            9: { cellWidth: 55, halign: 'right' },
            10: { cellWidth: 32, halign: 'right' },
            11: { cellWidth: 38, halign: 'right' },
            12: { cellWidth: 32, halign: 'left' },
            13: { cellWidth: 55, halign: 'right' },
            14: { cellWidth: 48, halign: 'right' },
            15: { cellWidth: 48, halign: 'right' },
            16: { cellWidth: 38, halign: 'right' },
            17: { cellWidth: 42, halign: 'right' }
          },
          didParseCell: function(data) {
            const columnIndex = data.column.index;
            if (columnIndex === 1) {
              data.cell.styles.halign = 'center';
              data.cell.styles.valign = 'top';
            }
            if (data.row.section !== 'body') return;

            const rowIndex = data.row.index;
            if (columnIndex === 1 && rowIndex < trendData.length) {
              const severity = trendData[rowIndex].severity;
              if (severity && severity.level) {
                data.cell.styles.fontStyle = 'bold';
                if (severity.level === 'CRITICAL') data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [255, 77, 79];
                else if (severity.level === 'HIGH') data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [250, 84, 28];
                else if (severity.level === 'MEDIUM') data.cell.styles.textColor = severity.isBetter ? [19, 194, 194] : [250, 173, 20];
                else if (severity.level === 'LOW') data.cell.styles.textColor = severity.isBetter ? [82, 196, 26] : [24, 144, 255];
                else if (severity.level === 'OK') data.cell.styles.textColor = [82, 196, 26];
              }
            }
            if (columnIndex === 16 && rowIndex < trendData.length) {
              const variance = trendData[rowIndex].variancePercent;
              if (variance > 0) data.cell.styles.textColor = [255, 77, 79];
              else if (variance < 0) data.cell.styles.textColor = [82, 196, 26];
            }
            if (columnIndex === 17 && rowIndex < trendData.length) {
              const fuelLoss = trendData[rowIndex].totalFuelLoss;
              if (fuelLoss > 0) data.cell.styles.textColor = [255, 77, 79];
              else if (fuelLoss < 0) data.cell.styles.textColor = [82, 196, 26];
            }
            if (detailTrendColumns.includes(columnIndex)) {
              const cellText = data.cell.text;
              if (Array.isArray(cellText) && cellText.length >= 2) {
                const key = `${rowIndex}_${columnIndex}`;
                cellTextStore[key] = [...cellText];
                data.cell.text = [];
              }
            }
          },
          didDrawCell: function(data) {
            if (data.row.section !== 'body') return;
            const columnIndex = data.column.index;
            if (!detailTrendColumns.includes(columnIndex)) return;

            const rowIndex = data.row.index;
            const key = `${rowIndex}_${columnIndex}`;
            const storedText = cellTextStore[key];
            if (!storedText || storedText.length < 2) return;

            const cell = data.cell;
            const x = cell.x;
            const y = cell.y;
            const cellWidth = cell.width;
            const paddingObj = cell.styles.cellPadding;
            const paddingLeft = typeof paddingObj === 'object' ? (paddingObj.left || 3) : (paddingObj || 3);
            const paddingRight = typeof paddingObj === 'object' ? (paddingObj.right || 3) : (paddingObj || 3);
            const paddingTop = typeof paddingObj === 'object' ? (paddingObj.top || 2) : (paddingObj || 2);
            const halign = cell.styles.halign || 'left';
            let textX;
            if (halign === 'right') textX = x + cellWidth - paddingRight;
            else if (halign === 'center') textX = x + cellWidth / 2;
            else textX = x + paddingLeft;

            let textY = y + paddingTop + 5;
            const baseColor = cell.styles.textColor;
            let textColorArr = [0, 0, 0];
            if (Array.isArray(baseColor)) textColorArr = baseColor;
            else if (typeof baseColor === 'number') textColorArr = [baseColor, baseColor, baseColor];

            annexureDoc.setFontSize((cell.styles.fontSize || 5) + 0.5);
            annexureDoc.setFont(undefined, 'bold');
            annexureDoc.setTextColor(textColorArr[0], textColorArr[1], textColorArr[2]);
            annexureDoc.text(storedText[0], textX, textY, { align: halign });
            textY += 8;

            annexureDoc.setFontSize((cell.styles.fontSize || 5) - 0.5);
            annexureDoc.setFont(undefined, 'italic');
            for (let i = 1; i < storedText.length; i++) {
              const lineText = storedText[i];
              if (columnIndex === 14 && lineText.startsWith('MoM:')) {
                const momMatch = lineText.match(/MoM:\s*([+-]?\d+)/);
                if (momMatch) {
                  const momValue = parseFloat(momMatch[1]);
                  if (momValue < 0) annexureDoc.setTextColor(82, 196, 26);
                  else if (momValue > 0) annexureDoc.setTextColor(255, 77, 79);
                  else annexureDoc.setTextColor(100, 100, 100);
                } else {
                  annexureDoc.setTextColor(100, 100, 100);
                }
              } else {
                annexureDoc.setTextColor(100, 100, 100);
              }
              annexureDoc.text(lineText, textX, textY, { align: halign });
              textY += 5;
            }
            annexureDoc.setFont(undefined, 'normal');
          }
        });

        const annexurePageCount = annexureDoc.internal.getNumberOfPages();
        for (let i = 1; i <= annexurePageCount; i++) {
          annexureDoc.setPage(i);
          annexureDoc.setFontSize(8);
          annexureDoc.setTextColor(150, 150, 150);
          annexureDoc.text(`Page ${i} of ${annexurePageCount}`, annexurePageWidth - 60, annexurePageHeight - 15);
          annexureDoc.text(`Executive Fuel Trend Report Annexure - ${selectedMonth.format('MMMM YYYY')}`, 20, annexurePageHeight - 15);
        }

        return annexureDoc.output('blob');
      };

      const triggerBlobDownload = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 60, pageHeight - 15);
        doc.text(`Monthly Full Tank Report - ${selectedMonth.format('MMMM YYYY')}`, 20, pageHeight - 15);
      }

      doc.save(`Monthly_Full_Tank_Report_${selectedMonth.format('YYYY-MM')}.pdf`);
      message.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    }
  };

  // Trend Analysis PDF Report
  const handleDownloadTrendPDF = async () => {
    try {
      setLoading(true);
      message.loading({ content: 'Generating Trend Analysis Report...', key: 'trendPdf', duration: 0 });
      const {
        trendData,
        currentTotals,
        prevTotals,
        m2Totals,
        m3Totals,
        avg3Totals,
        momRefillsChange,
        momFuelAddedChange,
        momFuelConsumedChange,
        momFullTankChange,
        momFuelLossChange
      } = await buildTrendDataBundle();
      
      // Create PDF
      const doc = new jsPDF('landscape', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;
      const tableMargin = 30;
      const availableWidth = pageWidth - tableMargin;
      
      await loadImageToPdf(doc);
      
      // Header
      const headerY = 25;
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Monthly Fuel Trend Analysis Report', pageWidth - 20, headerY, { align: 'right' });
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(`${selectedMonth.format('MMMM YYYY')}`, pageWidth - 20, headerY + 18, { align: 'right' });
      
      try {
        const userData = getUserData();
        if (userData?.organisation) {
          const companyName = typeof userData.organisation === 'string' ? userData.organisation : (userData.organisation.name || '');
          if (companyName) {
            doc.setFontSize(10);
            doc.text(companyName, pageWidth - 20, headerY + 32, { align: 'right' });
          }
        }
      } catch (e) {}
      
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Generated: ${moment().format('DD MMM YYYY, HH:mm')}`, pageWidth - 20, headerY + 44, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      
      yPosition = headerY + 55;
      
      // Display Active Filters (if any)
      const trendPdfActiveFilters = getActiveFilters();
      if (trendPdfActiveFilters.length > 0) {
        // Filter section background
        doc.setFillColor(240, 245, 255);
        doc.roundedRect(20, yPosition, pageWidth - 40, 20, 3, 3, 'F');
        doc.setDrawColor(214, 228, 255);
        doc.roundedRect(20, yPosition, pageWidth - 40, 20, 3, 3, 'S');
        
        // Filter label
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(24, 144, 255);
        doc.text('Filtered Report:', 28, yPosition + 13);
        
        // Filter values
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
        const trendFilterText = trendPdfActiveFilters.map(f => `${f.label}: ${f.value}`).join('  |  ');
        doc.text(trendFilterText, 100, yPosition + 13);
        
        yPosition += 28;
      }
      
      // Summary Statistics Cards - Single row with trend data inside each card
      const cardHeight = 76;
      const startX = 20;
      const endX = pageWidth - 20;
      const totalCardArea = endX - startX;
      const cardSpacing = 10;
      const numCards = 6;
      const cardWidth = (totalCardArea - (cardSpacing * (numCards - 1))) / numCards;
      
      // Helper to format trend indicator (using ASCII-safe characters for PDF)
      const getTrendText = (value, inverse = false) => {
        const isGood = inverse ? value < 0 : value > 0;
        const isBad = inverse ? value > 0 : value < 0;
        const sign = value >= 0 ? '+' : '';
        return { text: `${sign}${value.toFixed(1)}%`, isGood, isBad };
      };
      
      // Card drawing function with trend data
      const drawTrendCard = (x, y, title, currentVal, prevVal, momChange, avg3Val, colorMain, inverse = false) => {
        // Card background
        doc.setFillColor(250, 250, 255);
        doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'S');
        
        // Title
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(title, x + 6, y + 11);
        
        // Current value (main)
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...colorMain);
        doc.text(`${currentVal}`, x + 6, y + 28);
        
        // MoM trend indicator
        const trend = getTrendText(momChange, inverse);
        doc.setFontSize(7);
        doc.setFont(undefined, 'bold');
        if (trend.isGood) {
          doc.setTextColor(82, 196, 26);
        } else if (trend.isBad) {
          doc.setTextColor(255, 77, 79);
        } else {
          doc.setTextColor(100, 100, 100);
        }
        doc.text(`MoM: ${trend.text}`, x + 6, y + 42);
        
        // Previous month and 3M avg
        doc.setFontSize(6);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(120, 120, 120);
        doc.text(`Prev: ${prevVal}`, x + 6, y + 54);
        doc.text(`3M Avg: ${avg3Val}`, x + 6, y + 64);
      };
      
      // Draw 6 summary cards
      drawTrendCard(startX, yPosition, 'Total Assets', 
        filteredData.length.toString(), 
        prevTotals.totalAssets.toString(),
        prevTotals.totalAssets > 0 ? ((filteredData.length - prevTotals.totalAssets) / prevTotals.totalAssets) * 100 : 0,
        ((prevTotals.totalAssets + m2Totals.totalAssets + m3Totals.totalAssets) / 3).toFixed(0),
        [24, 144, 255], false);
      
      drawTrendCard(startX + cardWidth + cardSpacing, yPosition, 'Total Refills',
        Math.round(currentTotals.totalRefills).toString(),
        Math.round(prevTotals.totalRefills).toString(),
        momRefillsChange,
        Math.round(avg3Totals.totalRefills).toString(),
        [250, 140, 22], false);
      
      drawTrendCard(startX + (cardWidth + cardSpacing) * 2, yPosition, 'Fuel Added (L)',
        formatNum(currentTotals.totalFuelAdded),
        formatNum(prevTotals.totalFuelAdded),
        momFuelAddedChange,
        formatNum(avg3Totals.totalFuelAdded),
        [82, 196, 26], false);
      
      drawTrendCard(startX + (cardWidth + cardSpacing) * 3, yPosition, 'Full Tank Events',
        currentTotals.fullTankCount.toString(),
        prevTotals.fullTankCount.toString(),
        momFullTankChange,
        avg3Totals.fullTankCount.toFixed(0),
        [250, 84, 28], false);
      
      drawTrendCard(startX + (cardWidth + cardSpacing) * 4, yPosition, 'Fuel Consumed (L)',
        formatNum(currentTotals.totalFuelConsumed),
        formatNum(prevTotals.totalFuelConsumed),
        momFuelConsumedChange,
        formatNum(avg3Totals.totalFuelConsumed),
        [114, 46, 209], true); // inverse - decrease is good
      
      // Fuel Loss card
      const lossX = startX + (cardWidth + cardSpacing) * 5;
      doc.setFillColor(currentTotals.totalFuelLoss >= 0 ? 255 : 240, currentTotals.totalFuelLoss >= 0 ? 248 : 255, currentTotals.totalFuelLoss >= 0 ? 250 : 240);
      doc.roundedRect(lossX, yPosition, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.roundedRect(lossX, yPosition, cardWidth, cardHeight, 3, 3, 'S');
      doc.setFontSize(7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(currentTotals.totalFuelLoss >= 0 ? 'Total Fuel Loss (L)' : 'Total Fuel Saved (L)', lossX + 6, yPosition + 11);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(currentTotals.totalFuelLoss >= 0 ? 255 : 82, currentTotals.totalFuelLoss >= 0 ? 77 : 196, currentTotals.totalFuelLoss >= 0 ? 79 : 26);
      doc.text(`${formatNum(Math.abs(currentTotals.totalFuelLoss))}`, lossX + 6, yPosition + 28);
      const lossTrend = getTrendText(momFuelLossChange, true);
      doc.setFontSize(7);
      doc.setFont(undefined, 'bold');
      if (lossTrend.isGood) doc.setTextColor(82, 196, 26);
      else if (lossTrend.isBad) doc.setTextColor(255, 77, 79);
      else doc.setTextColor(100, 100, 100);
      doc.text(`MoM: ${lossTrend.text}`, lossX + 6, yPosition + 42);
      doc.setFontSize(6);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text(`Prev: ${formatNum(Math.abs(prevTotals.totalFuelLoss))}`, lossX + 6, yPosition + 54);
      doc.text(`3M Avg: ${formatNum(Math.abs(avg3Totals.totalFuelLoss))}`, lossX + 6, yPosition + 64);
      
      yPosition += cardHeight + 20;
      
      // Table Title
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Asset-wise Trend Analysis', 20, yPosition);
      yPosition += 15;

      // Prepare table data - same columns as regular PDF but with trend data in cells
      const tableColumns = [
        'SN', 'Severity', 'Asset Code', 'Type', 'Sub Type', 'Model', 'Location', 'Sub Location',
        'Refills', 'Fuel Added (L)', 'Full Tank', 'Meter Run', 'Meter', 
        'Consumed (L)', 'Avg Rate', 'Std Rate', 'Variance', 'Fuel Loss (L)'
      ];

      // Helper to format asset code with altname/ownercode for trend PDF
      const formatAssetCodeForTrendPdf = (row) => {
        const parts = [row.assetCode || '-'];
        if (row.altname) parts.push(row.altname);
        if (row.ownercode) parts.push(row.ownercode);
        return parts.join('\n');
      };

      // Helper to format severity for trend PDF (ASCII-safe, no Unicode arrows)
      const formatTrendSeverity = (severity) => {
        if (!severity || !severity.level) return '-';
        if (severity.level === 'OK') return 'OK';
        // Use (+) for worse (higher consumption) and (-) for better (lower consumption)
        const indicator = severity.isBetter ? ' (-)' : ' (+)';
        return severity.level + indicator;
      };

      // Build table data with trend info embedded in cells
      const tableData = trendData.map((row, idx) => {
        // Format with trend creates 4 lines: current value, Prev, MoM, 3M Avg
        const formatWithTrend = (curr, prev, mom, avg3, useThousands = false, decimals = 2, suffix = '') => {
          const fmt = (v) => useThousands ? formatNum(v) : (typeof v === 'number' ? (decimals === 0 ? Math.round(v).toString() : v.toFixed(decimals)) : String(v));
          const currStr = fmt(curr) + suffix;
          const prevStr = fmt(prev);
          const avg3Str = fmt(avg3);
          const momSign = mom >= 0 ? '+' : '';
          // 4 separate lines: Current, Prev, MoM, 3M Avg
          return `${currStr}\nPrev: ${prevStr}\nMoM: ${momSign}${mom.toFixed(0)}%\n3M Avg: ${avg3Str}`;
        };
        
        const meterRunVal = row.totalMeterRun > 0 ? row.totalMeterRun : 0;
        const meterRunPrev = row.prevMeterRun || 0;
        const avgRateVal = row.avgConsumptionRate || 0;
        const avgRatePrev = row.prevAvgRate || 0;
        const rateUnit = row.consumptionRateUnit || '';
        
        return [
          idx + 1,
          formatTrendSeverity(row.severity),
          formatAssetCodeForTrendPdf(row),
          row.assetType || '-',
          row.assetSubType || '-',
          row.assetModel || '-',
          row.location || '-',
          row.subLocation || '-',
          formatWithTrend(row.totalRefills, row.prevRefills, row.momRefills, row.avg3Refills, false, 0),
          formatWithTrend(row.totalFuelAdded, row.prevFuelAdded, row.momFuelAdded, row.avg3FuelAdded, true),
          row.fullTankCount,
          meterRunVal > 0 ? formatWithTrend(meterRunVal, meterRunPrev, row.momMeterRun, row.avg3MeterRun, false, 2) : '-',
          row.meterName || '-',
          formatWithTrend(row.totalFuelConsumed, row.prevFuelConsumed, row.momFuelConsumed, row.avg3FuelConsumed, true),
          avgRateVal > 0 ? formatWithTrend(avgRateVal, avgRatePrev, row.momAvgRate, row.avg3AvgRate, false, 2, rateUnit ? ` ${rateUnit}` : '') : '-',
          row.standardRate ? `${row.standardRate.toFixed(2)} ${row.standardRateUnit}` : '-',
          row.variancePercent !== null ? `${row.variancePercent.toFixed(1)}%` : '-',
          row.totalFuelLoss !== null ? formatWithTrend(row.totalFuelLoss, row.prevFuelLoss, row.momFuelLoss, row.avg3FuelLoss, true) : '-'
        ];
      });

      // Define columns that have trend data (multi-line with current value + prev/mom/avg)
      const trendColumns = [8, 9, 11, 13, 14, 17]; // Refills, Fuel Added, Meter Run, Consumed, Avg Rate, Fuel Loss (shifted +1 for Severity column)
      
      // Store original text for custom rendering
      const cellTextStore = {};
      
      // Add table with improved styling
      autoTable(doc, {
        startY: yPosition,
        head: [tableColumns],
        body: tableData,
        theme: 'striped',
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        styles: { 
          fontSize: 5, 
          cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
          overflow: 'linebreak',
          halign: 'left',
          valign: 'top',
          lineWidth: 0.5,
          lineColor: [220, 220, 220]
        },
        headStyles: { 
          fillColor: [52, 73, 94], 
          textColor: 255, 
          fontStyle: 'bold', 
          fontSize: 5.5,
          halign: 'center',
          valign: 'middle',
          minCellHeight: 15,
          cellPadding: { top: 4, bottom: 4, left: 3, right: 3 }
        },
        bodyStyles: {
          fontSize: 5,
          minCellHeight: 38,
          valign: 'top'
        },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        margin: { left: 15, right: 15, top: 20, bottom: 20 },
        tableWidth: availableWidth,
        columnStyles: {
          0: { cellWidth: 18, halign: 'center' },  // SN
          1: { cellWidth: 48, halign: 'center' },  // Severity (wider for CRITICAL to fit)
          2: { cellWidth: 48, halign: 'left' },   // Asset Code (with altname/ownercode)
          3: { cellWidth: 38, halign: 'left' },    // Type
          4: { cellWidth: 42, halign: 'left' },    // Sub Type
          5: { cellWidth: 45, halign: 'left' },    // Model
          6: { cellWidth: 42, halign: 'left' },    // Location
          7: { cellWidth: 42, halign: 'left' },    // Sub Location
          8: { cellWidth: 52, halign: 'right' },   // Refills (with trend)
          9: { cellWidth: 55, halign: 'right' },   // Fuel Added (with trend)
          10: { cellWidth: 32, halign: 'right' },  // Full Tank
          11: { cellWidth: 38, halign: 'right' },  // Meter Run
          12: { cellWidth: 32, halign: 'left' },   // Meter
          13: { cellWidth: 55, halign: 'right' },  // Consumed (with trend)
          14: { cellWidth: 48, halign: 'right' },  // Avg Rate
          15: { cellWidth: 48, halign: 'right' },  // Std Rate
          16: { cellWidth: 38, halign: 'right' },  // Variance
          17: { cellWidth: 42, halign: 'right' }   // Fuel Loss
        },
        didParseCell: function(data) {
          const columnIndex = data.column.index;
          
          // Severity column (1) - horizontally centered, vertically top-aligned like other columns
          if (columnIndex === 1) {
            data.cell.styles.halign = 'center';
            data.cell.styles.valign = 'top';
          }
          
          if (data.row.section !== 'body') return;
          
          const rowIndex = data.row.index;
          
          // Severity column (1) - text color only
          if (columnIndex === 1 && rowIndex < trendData.length) {
            const severity = trendData[rowIndex].severity;
            if (severity && severity.level) {
              data.cell.styles.fontStyle = 'bold';
              if (severity.level === 'CRITICAL') {
                data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [255, 77, 79];
              } else if (severity.level === 'HIGH') {
                data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [250, 84, 28];
              } else if (severity.level === 'MEDIUM') {
                data.cell.styles.textColor = severity.isBetter ? [19, 194, 194] : [250, 173, 20];
              } else if (severity.level === 'LOW') {
                data.cell.styles.textColor = severity.isBetter ? [82, 196, 26] : [24, 144, 255];
              } else if (severity.level === 'OK') {
                data.cell.styles.textColor = [82, 196, 26];
              }
            }
          }
          
          // Color code Variance column (16)
          if (columnIndex === 16 && rowIndex < trendData.length) {
            const variance = trendData[rowIndex].variancePercent;
            if (variance !== null && variance !== undefined) {
              if (variance > 0) data.cell.styles.textColor = [255, 77, 79];
              else if (variance < 0) data.cell.styles.textColor = [82, 196, 26];
            }
          }
          
          // Color code Fuel Loss column (17)
          if (columnIndex === 17 && rowIndex < trendData.length) {
            const fuelLoss = trendData[rowIndex].totalFuelLoss;
            if (fuelLoss !== null && fuelLoss !== undefined) {
              if (fuelLoss > 0) data.cell.styles.textColor = [255, 77, 79];
              else if (fuelLoss < 0) data.cell.styles.textColor = [82, 196, 26];
            }
          }
          
          // For trend columns with multi-line content, store original text and clear it
          if (trendColumns.includes(columnIndex)) {
            const cellText = data.cell.text;
            if (Array.isArray(cellText) && cellText.length >= 2) {
              // Store the original text for custom rendering in didDrawCell
              const key = `${rowIndex}_${columnIndex}`;
              cellTextStore[key] = [...cellText];
              // Clear the text so default rendering is skipped
              data.cell.text = [];
            }
          }
        },
        didDrawCell: function(data) {
          // Only process body cells in trend columns
          if (data.row.section !== 'body') return;
          const columnIndex = data.column.index;
          
          if (!trendColumns.includes(columnIndex)) return;
          
          const rowIndex = data.row.index;
          const key = `${rowIndex}_${columnIndex}`;
          const storedText = cellTextStore[key];
          
          if (!storedText || storedText.length < 2) return;
          
          const cell = data.cell;
          const x = cell.x;
          const y = cell.y;
          const cellWidth = cell.width;
          
          // Get padding (handle both object and number formats)
          const paddingObj = cell.styles.cellPadding;
          const paddingLeft = typeof paddingObj === 'object' ? (paddingObj.left || 3) : (paddingObj || 3);
          const paddingRight = typeof paddingObj === 'object' ? (paddingObj.right || 3) : (paddingObj || 3);
          const paddingTop = typeof paddingObj === 'object' ? (paddingObj.top || 2) : (paddingObj || 2);
          
          // Determine text alignment
          const halign = cell.styles.halign || 'left';
          let textX;
          if (halign === 'right') {
            textX = x + cellWidth - paddingRight;
          } else if (halign === 'center') {
            textX = x + cellWidth / 2;
          } else {
            textX = x + paddingLeft;
          }
          
          let textY = y + paddingTop + 5;
          
          // Get base text color (handle array or single value)
          const baseColor = cell.styles.textColor;
          let textColorArr = [0, 0, 0];
          if (Array.isArray(baseColor)) {
            textColorArr = baseColor;
          } else if (typeof baseColor === 'number') {
            textColorArr = [baseColor, baseColor, baseColor];
          }
          
          // Line 1: Current value - BOLD (larger font)
          doc.setFontSize((cell.styles.fontSize || 5) + 0.5);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(textColorArr[0], textColorArr[1], textColorArr[2]);
          doc.text(storedText[0], textX, textY, { align: halign });
          
          // Add extra space below current value before trend info
          textY += 8;
          
          // Lines 2-4: Trend info (Prev, MoM, 3M Avg) - ITALIC (smaller font, grey color)
          doc.setFontSize((cell.styles.fontSize || 5) - 0.5);
          doc.setFont(undefined, 'italic');
          
          for (let i = 1; i < storedText.length; i++) {
            const lineText = storedText[i];
            
            // For Avg Rate column (index 14), apply color to MoM line
            // Negative = better (green), Positive = worse (red)
            if (columnIndex === 14 && lineText.startsWith('MoM:')) {
              // Extract the percentage value from "MoM: +X%" or "MoM: -X%"
              const momMatch = lineText.match(/MoM:\s*([+-]?\d+)/);
              if (momMatch) {
                const momValue = parseFloat(momMatch[1]);
                if (momValue < 0) {
                  // Negative = better = green
                  doc.setTextColor(82, 196, 26);
                } else if (momValue > 0) {
                  // Positive = worse = red
                  doc.setTextColor(255, 77, 79);
                } else {
                  doc.setTextColor(100, 100, 100); // No change = grey
                }
              } else {
                doc.setTextColor(100, 100, 100);
              }
            } else {
              doc.setTextColor(100, 100, 100); // Grey for other trend info
            }
            
            doc.text(lineText, textX, textY, { align: halign });
            textY += 5; // Line spacing for each trend line
          }
          
          // Reset font to normal for subsequent cells
          doc.setFont(undefined, 'normal');
        }
      });

      drawSeverityGuidePdfAtEnd(doc, pageWidth, pageHeight);

      // Footer with page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 60, pageHeight - 15);
        doc.text(`Fuel Trend Analysis Report - ${selectedMonth.format('MMMM YYYY')}`, 20, pageHeight - 15);
      }

      doc.save(`Fuel_Trend_Analysis_${selectedMonth.format('YYYY-MM')}.pdf`);
      message.destroy('trendPdf');
      message.success('Trend Analysis PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating Trend PDF:', error);
      message.destroy('trendPdf');
      message.error('Failed to generate Trend Analysis PDF');
    } finally {
      setLoading(false);
    }
  };

  const generateExecutiveTrendAnnexurePDF = async () => {
    try {
      setLoading(true);
      message.loading({
        content: 'Generating AssetWise Detailed Report...',
        key: 'execTrendAnnexurePdf',
        duration: 0
      });

      const {
        trendData,
        currentTotals,
        prevTotals,
        m2Totals,
        m3Totals,
        avg3Totals,
        momRefillsChange,
        momFuelAddedChange,
        momFuelConsumedChange,
        momFullTankChange
      } = await buildTrendDataBundle();
      const currentImpact = getExecutiveImpactSummary(trendData);
      const prevImpact = getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.prevFuelLoss || 0 })));
      const avg3Impact = {
        loss: Math.round(((prevImpact.loss + getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.m2?.totalFuelLoss || 0 }))).loss + getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.m3?.totalFuelLoss || 0 }))).loss) / 3) * 100) / 100,
        gain: Math.round(((prevImpact.gain + getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.m2?.totalFuelLoss || 0 }))).gain + getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.m3?.totalFuelLoss || 0 }))).gain) / 3) * 100) / 100,
        net: 0
      };
      avg3Impact.net = Math.round((avg3Impact.loss - avg3Impact.gain) * 100) / 100;
      const doc = new jsPDF('landscape', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageMargin = 20;
      let yPosition = 20;
      const palette = {
        ink: [24, 39, 75],
        mutedInk: [92, 105, 138],
        line: [220, 227, 240],
        panel: [248, 250, 255],
        panelAlt: [243, 247, 255],
        navy: [31, 68, 130]
      };

      const formatTrendSeverity = (severity) => {
        if (!severity || !severity.level) return '-';
        if (severity.level === 'OK') return 'OK';
        const indicator = severity.isBetter ? ' (-)' : ' (+)';
        return severity.level + indicator;
      };

      const formatAssetCodeForTrendPdf = (row) => {
        const parts = [row.assetCode || '-'];
        if (row.altname) parts.push(row.altname);
        if (row.ownercode) parts.push(row.ownercode);
        return parts.join('\n');
      };

      await loadImageToPdf(doc);

      const headerY = 25;
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Executive Fuel Trend Report', pageWidth - pageMargin, headerY, { align: 'right' });
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(`${selectedMonth.format('MMMM YYYY')}`, pageWidth - pageMargin, headerY + 18, { align: 'right' });
      try {
        const userData = getUserData();
        if (userData?.organisation) {
          const companyName = typeof userData.organisation === 'string'
            ? userData.organisation
            : (userData.organisation.name || '');
          if (companyName) {
            doc.setFontSize(10);
            doc.text(companyName, pageWidth - pageMargin, headerY + 32, { align: 'right' });
          }
        }
      } catch (e) {}
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Generated: ${moment().format('DD MMM YYYY, HH:mm')}`, pageWidth - pageMargin, headerY + 44, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      yPosition = headerY + 55;

      const activeFilters = getActiveFilters();
      if (activeFilters.length > 0) {
        doc.setFillColor(...palette.panelAlt);
        doc.roundedRect(pageMargin, yPosition, pageWidth - (pageMargin * 2), 22, 4, 4, 'F');
        doc.setDrawColor(...palette.line);
        doc.roundedRect(pageMargin, yPosition, pageWidth - (pageMargin * 2), 22, 4, 4, 'S');
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(56, 132, 255);
        doc.text('Filtered Report:', pageMargin + 8, yPosition + 14);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...palette.ink);
        doc.text(activeFilters.map(f => `${f.label}: ${f.value}`).join('  |  '), pageMargin + 84, yPosition + 14, {
          maxWidth: pageWidth - (pageMargin * 2) - 96
        });
        doc.setTextColor(0, 0, 0);
        yPosition += 30;
      }

      const cardHeight = 76;
      const startX = pageMargin;
      const endX = pageWidth - pageMargin;
      const totalCardArea = endX - startX;
      const cardSpacing = 10;
      const numCards = 6;
      const cardWidth = (totalCardArea - (cardSpacing * (numCards - 1))) / numCards;

      const getTrendText = (value, inverse = false) => {
        const isGood = inverse ? value < 0 : value > 0;
        const isBad = inverse ? value > 0 : value < 0;
        const sign = value >= 0 ? '+' : '';
        return { text: `${sign}${value.toFixed(1)}%`, isGood, isBad };
      };

      const drawTrendCard = (x, y, title, currentVal, prevVal, momChange, avg3Val, colorMain, inverse = false) => {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'F');
        doc.setDrawColor(...palette.line);
        doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'S');
        doc.setFillColor(...colorMain);
        doc.roundedRect(x, y, cardWidth, 4, 3, 3, 'F');
        doc.setFontSize(7);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...palette.mutedInk);
        doc.text(title, x + 10, y + 15);
        doc.setFontSize(15);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...colorMain);
        doc.text(`${currentVal}`, x + 10, y + 34);
        const trend = getTrendText(momChange, inverse);
        doc.setFontSize(7);
        doc.setFont(undefined, 'bold');
        if (trend.isGood) doc.setTextColor(56, 161, 105);
        else if (trend.isBad) doc.setTextColor(220, 74, 74);
        else doc.setTextColor(...palette.mutedInk);
        doc.text(`MoM: ${trend.text}`, x + 10, y + 49);
        doc.setFontSize(6);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...palette.mutedInk);
        doc.text(`Prev: ${prevVal}`, x + 10, y + 61);
        doc.text(`3M Avg: ${avg3Val}`, x + 10, y + 67);
        doc.setTextColor(0, 0, 0);
      };

      const executiveCards = [
        ['Total Assets', filteredData.length.toString(), prevTotals.totalAssets.toString(), prevTotals.totalAssets > 0 ? ((filteredData.length - prevTotals.totalAssets) / prevTotals.totalAssets) * 100 : 0, ((prevTotals.totalAssets + m2Totals.totalAssets + m3Totals.totalAssets) / 3).toFixed(0), [24, 144, 255], false],
        ['Total Refills', Math.round(currentTotals.totalRefills).toString(), Math.round(prevTotals.totalRefills).toString(), momRefillsChange, Math.round(avg3Totals.totalRefills).toString(), [250, 140, 22], false],
        ['Fuel Added (L)', formatNum(currentTotals.totalFuelAdded), formatNum(prevTotals.totalFuelAdded), momFuelAddedChange, formatNum(avg3Totals.totalFuelAdded), [82, 196, 26], false],
        ['Full Tank Events', currentTotals.fullTankCount.toString(), prevTotals.fullTankCount.toString(), momFullTankChange, avg3Totals.fullTankCount.toFixed(0), [250, 84, 28], false],
        ['Fuel Consumed (L)', formatNum(currentTotals.totalFuelConsumed), formatNum(prevTotals.totalFuelConsumed), momFuelConsumedChange, formatNum(avg3Totals.totalFuelConsumed), [114, 46, 209], true],
        ['Net Fuel Impact (L)', formatNum(currentImpact.net), formatNum(prevImpact.net), prevImpact.net !== 0 ? ((currentImpact.net - prevImpact.net) / Math.abs(prevImpact.net)) * 100 : (currentImpact.net !== 0 ? 100 : 0), formatNum(avg3Impact.net), currentImpact.net >= 0 ? [255, 77, 79] : [82, 196, 26], true]
      ];

      executiveCards.forEach((card, index) => {
        drawTrendCard(startX + (cardWidth + cardSpacing) * index, yPosition, ...card);
      });
      yPosition += cardHeight + 22;

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...palette.ink);
      doc.text('Asset-wise Trend Analysis Detail', pageMargin, yPosition);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...palette.mutedInk);
      doc.text('Detailed asset-level trend view with month-on-month and 3M comparison.', pageMargin, yPosition + 12);
      doc.setDrawColor(...palette.line);
      doc.setLineWidth(1);
      doc.line(pageMargin, yPosition + 19, pageWidth - pageMargin, yPosition + 19);
      doc.setTextColor(0, 0, 0);
      yPosition += 34;

      const detailTableColumns = [
        'SN', 'Severity', 'Asset Code', 'Type', 'Sub Type', 'Model', 'Location', 'Sub Location',
        'Refills', 'Fuel Added (L)', 'Full Tank', 'Meter Run', 'Meter',
        'Consumed (L)', 'Avg Rate', 'Std Rate', 'Variance', 'Fuel Loss (L)'
      ];

      const detailTableData = trendData.map((row, idx) => {
        const formatWithTrend = (curr, prev, mom, avg3, useThousands = false, decimals = 2, suffix = '') => {
          const fmt = (v) => useThousands ? formatNum(v) : (typeof v === 'number' ? (decimals === 0 ? Math.round(v).toString() : v.toFixed(decimals)) : String(v));
          const currStr = fmt(curr) + suffix;
          const prevStr = fmt(prev);
          const avg3Str = fmt(avg3);
          const momSign = mom >= 0 ? '+' : '';
          return `${currStr}\nPrev: ${prevStr}\nMoM: ${momSign}${mom.toFixed(0)}%\n3M Avg: ${avg3Str}`;
        };

        const meterRunVal = row.totalMeterRun > 0 ? row.totalMeterRun : 0;
        const meterRunPrev = row.prevMeterRun || 0;
        const avgRateVal = row.avgConsumptionRate || 0;
        const avgRatePrev = row.prevAvgRate || 0;
        const rateUnit = row.consumptionRateUnit || '';

        return [
          idx + 1,
          formatTrendSeverity(row.severity),
          formatAssetCodeForTrendPdf(row),
          row.assetType || '-',
          row.assetSubType || '-',
          row.assetModel || '-',
          row.location || '-',
          row.subLocation || '-',
          formatWithTrend(row.totalRefills, row.prevRefills, row.momRefills, row.avg3Refills, false, 0),
          formatWithTrend(row.totalFuelAdded, row.prevFuelAdded, row.momFuelAdded, row.avg3FuelAdded, true),
          row.fullTankCount,
          meterRunVal > 0 ? formatWithTrend(meterRunVal, meterRunPrev, row.momMeterRun, row.avg3MeterRun, false, 2) : '-',
          row.meterName || '-',
          formatWithTrend(row.totalFuelConsumed, row.prevFuelConsumed, row.momFuelConsumed, row.avg3FuelConsumed, true),
          avgRateVal > 0 ? formatWithTrend(avgRateVal, avgRatePrev, row.momAvgRate, row.avg3AvgRate, false, 2, rateUnit ? ` ${rateUnit}` : '') : '-',
          row.standardRate ? `${row.standardRate.toFixed(2)} ${row.standardRateUnit}` : '-',
          row.variancePercent !== null ? `${row.variancePercent.toFixed(1)}%` : '-',
          row.totalFuelLoss !== null ? formatWithTrend(row.totalFuelLoss, row.prevFuelLoss, row.momFuelLoss, row.avg3FuelLoss, true) : '-'
        ];
      });

      const detailTrendColumns = [8, 9, 11, 13, 14, 17];
      const cellTextStore = {};

      autoTable(doc, {
        startY: yPosition,
        head: [detailTableColumns],
        body: detailTableData,
        theme: 'striped',
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        styles: {
          fontSize: 5.2,
          cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
          overflow: 'linebreak',
          halign: 'left',
          valign: 'top',
          lineWidth: 0.5,
          lineColor: palette.line
        },
        headStyles: {
          fillColor: palette.navy,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 5.8,
          halign: 'center',
          valign: 'middle',
          minCellHeight: 15,
          cellPadding: { top: 4, bottom: 4, left: 3, right: 3 }
        },
        bodyStyles: { fontSize: 5.2, minCellHeight: 38, valign: 'top' },
        alternateRowStyles: { fillColor: palette.panel },
        margin: { left: pageMargin, right: pageMargin, top: 20, bottom: 20 },
        tableWidth: pageWidth - (pageMargin * 2),
        columnStyles: {
          0: { cellWidth: 18, halign: 'center' },
          1: { cellWidth: 48, halign: 'center' },
          2: { cellWidth: 48, halign: 'left' },
          3: { cellWidth: 38, halign: 'left' },
          4: { cellWidth: 42, halign: 'left' },
          5: { cellWidth: 45, halign: 'left' },
          6: { cellWidth: 42, halign: 'left' },
          7: { cellWidth: 42, halign: 'left' },
          8: { cellWidth: 52, halign: 'right' },
          9: { cellWidth: 55, halign: 'right' },
          10: { cellWidth: 32, halign: 'right' },
          11: { cellWidth: 38, halign: 'right' },
          12: { cellWidth: 32, halign: 'left' },
          13: { cellWidth: 55, halign: 'right' },
          14: { cellWidth: 48, halign: 'right' },
          15: { cellWidth: 48, halign: 'right' },
          16: { cellWidth: 38, halign: 'right' },
          17: { cellWidth: 42, halign: 'right' }
        },
        didParseCell: function(data) {
          const columnIndex = data.column.index;
          if (columnIndex === 1) {
            data.cell.styles.halign = 'center';
            data.cell.styles.valign = 'top';
          }
          if (data.row.section !== 'body') return;

          const rowIndex = data.row.index;
          if (columnIndex === 1 && rowIndex < trendData.length) {
            const severity = trendData[rowIndex].severity;
            if (severity && severity.level) {
              data.cell.styles.fontStyle = 'bold';
              if (severity.level === 'CRITICAL') data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [255, 77, 79];
              else if (severity.level === 'HIGH') data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [250, 84, 28];
              else if (severity.level === 'MEDIUM') data.cell.styles.textColor = severity.isBetter ? [19, 194, 194] : [250, 173, 20];
              else if (severity.level === 'LOW') data.cell.styles.textColor = severity.isBetter ? [82, 196, 26] : [24, 144, 255];
              else if (severity.level === 'OK') data.cell.styles.textColor = [82, 196, 26];
            }
          }
          if (columnIndex === 16 && rowIndex < trendData.length) {
            const variance = trendData[rowIndex].variancePercent;
            if (variance > 0) data.cell.styles.textColor = [255, 77, 79];
            else if (variance < 0) data.cell.styles.textColor = [82, 196, 26];
          }
          if (columnIndex === 17 && rowIndex < trendData.length) {
            const fuelLoss = trendData[rowIndex].totalFuelLoss;
            if (fuelLoss > 0) data.cell.styles.textColor = [255, 77, 79];
            else if (fuelLoss < 0) data.cell.styles.textColor = [82, 196, 26];
          }
          if (detailTrendColumns.includes(columnIndex)) {
            const cellText = data.cell.text;
            if (Array.isArray(cellText) && cellText.length >= 2) {
              const key = `${rowIndex}_${columnIndex}`;
              cellTextStore[key] = [...cellText];
              data.cell.text = [];
            }
          }
        },
        didDrawCell: function(data) {
          if (data.row.section !== 'body') return;
          const columnIndex = data.column.index;
          if (!detailTrendColumns.includes(columnIndex)) return;

          const rowIndex = data.row.index;
          const key = `${rowIndex}_${columnIndex}`;
          const storedText = cellTextStore[key];
          if (!storedText || storedText.length < 2) return;

          const cell = data.cell;
          const x = cell.x;
          const y = cell.y;
          const cellWidth = cell.width;
          const paddingObj = cell.styles.cellPadding;
          const paddingLeft = typeof paddingObj === 'object' ? (paddingObj.left || 3) : (paddingObj || 3);
          const paddingRight = typeof paddingObj === 'object' ? (paddingObj.right || 3) : (paddingObj || 3);
          const paddingTop = typeof paddingObj === 'object' ? (paddingObj.top || 2) : (paddingObj || 2);
          const halign = cell.styles.halign || 'left';
          let textX;
          if (halign === 'right') textX = x + cellWidth - paddingRight;
          else if (halign === 'center') textX = x + cellWidth / 2;
          else textX = x + paddingLeft;

          let textY = y + paddingTop + 5;
          const baseColor = cell.styles.textColor;
          let textColorArr = [0, 0, 0];
          if (Array.isArray(baseColor)) textColorArr = baseColor;
          else if (typeof baseColor === 'number') textColorArr = [baseColor, baseColor, baseColor];

          doc.setFontSize((cell.styles.fontSize || 5) + 0.5);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(textColorArr[0], textColorArr[1], textColorArr[2]);
          doc.text(storedText[0], textX, textY, { align: halign });
          textY += 8;

          doc.setFontSize((cell.styles.fontSize || 5) - 0.5);
          doc.setFont(undefined, 'italic');
          for (let i = 1; i < storedText.length; i++) {
            const lineText = storedText[i];
            if (columnIndex === 14 && lineText.startsWith('MoM:')) {
              const momMatch = lineText.match(/MoM:\s*([+-]?\d+)/);
              if (momMatch) {
                const momValue = parseFloat(momMatch[1]);
                if (momValue < 0) doc.setTextColor(82, 196, 26);
                else if (momValue > 0) doc.setTextColor(255, 77, 79);
                else doc.setTextColor(100, 100, 100);
              } else {
                doc.setTextColor(100, 100, 100);
              }
            } else {
              doc.setTextColor(100, 100, 100);
            }
            doc.text(lineText, textX, textY, { align: halign });
            textY += 5;
          }
          doc.setFont(undefined, 'normal');
        }
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 60, pageHeight - 15);
        doc.text(`AssetWise Detailed Report - ${selectedMonth.format('MMMM YYYY')}`, 20, pageHeight - 15);
      }

      doc.save(`AssetWise_Detailed_Report_${selectedMonth.format('YYYY-MM')}.pdf`);
      message.destroy('execTrendAnnexurePdf');
      message.success('AssetWise Detailed Report downloaded successfully');
    } catch (error) {
      console.error('Error generating AssetWise Detailed Report:', error);
      message.destroy('execTrendAnnexurePdf');
      message.error('Failed to generate AssetWise Detailed Report');
    } finally {
      setLoading(false);
    }
  };

  const generateExecutiveTrendPDF = async () => {
    try {
      setLoading(true);
      message.loading({
        content: 'Generating Executive Trend Report...',
        key: 'execTrendPdf',
        duration: 0
      });

      const {
        trendData,
        currentTotals,
        prevTotals,
        m2Totals,
        m3Totals,
        avg3Totals,
        momRefillsChange,
        momFuelAddedChange,
        momFuelConsumedChange,
        momFullTankChange,
        momFuelLossChange,
        prevMonthData
      } = await buildTrendDataBundle();

      // Fetch fuel type rates for Rs. conversion
      let fuelTypeRateMap = {};
      let defaultFuelRate = 0;
      let fuelRateEntries = [];
      try {
        const fuelTypesResult = await apiGet('/fuel-type');
        if (fuelTypesResult.success && fuelTypesResult.data) {
          const rawData = fuelTypesResult.data;
          let fuelTypes = [];
          if (Array.isArray(rawData)) fuelTypes = rawData;
          else if (rawData.data && Array.isArray(rawData.data)) fuelTypes = rawData.data;
          else if (rawData.fuelTypes && Array.isArray(rawData.fuelTypes)) fuelTypes = rawData.fuelTypes;
          else if (typeof rawData === 'object') {
            // Try to find any array in the response
            for (const key of Object.keys(rawData)) {
              if (Array.isArray(rawData[key])) {
                fuelTypes = rawData[key];
                break;
              }
            }
          }
          fuelRateEntries = [];
          fuelTypes.forEach(ft => {
            const name = (ft.name || '').trim();
            const nameLower = name.toLowerCase();
            const rate = ft.rate || ft.pricePerLitre || ft.price || ft.costPerLitre || ft.cost || ft.unitPrice || ft.currentRate || ft.currentPrice || 0;
            if (nameLower) {
              fuelTypeRateMap[nameLower] = rate;
              if (ft._id) fuelTypeRateMap[ft._id] = rate;
              if (rate > 0) fuelRateEntries.push({ name, rate });
            }
          });
          const rates = Object.values(fuelTypeRateMap).filter(r => typeof r === 'number' && r > 0);
          if (rates.length > 0) defaultFuelRate = rates[0];
        }
      } catch (e) {
        console.warn('Could not fetch fuel type rates:', e);
      }

      const getFuelRate = (row) => {
        if (!row._assetModelObj) return defaultFuelRate;
        const ft = row._assetModelObj.fuelType || row._assetModelObj.fuel_type;
        if (!ft) return defaultFuelRate;
        // ft can be a string ID, an object with _id/name, or just a name
        if (typeof ft === 'string') {
          return fuelTypeRateMap[ft] || fuelTypeRateMap[ft.toLowerCase()] || defaultFuelRate;
        }
        // Object: try _id first, then name
        return fuelTypeRateMap[ft._id] || fuelTypeRateMap[(ft.name || '').toLowerCase()] || defaultFuelRate;
      };

      const formatRs = (amount) => {
        if (amount === null || amount === undefined || isNaN(amount)) return '-';
        const abs = Math.abs(amount);
        const intPart = Math.floor(abs);
        const decPart = (abs - intPart).toFixed(2).slice(1); // ".XX"
        // Indian numbering: 1,23,456.00
        const s = intPart.toString();
        let formatted;
        if (s.length <= 3) {
          formatted = s;
        } else {
          formatted = s.slice(-3);
          let remaining = s.slice(0, -3);
          while (remaining.length > 0) {
            formatted = remaining.slice(-2) + ',' + formatted;
            remaining = remaining.slice(0, -2);
          }
        }
        return formatted + decPart;
      };
      const toRs = (litres, rate) => {
        if (!rate || rate <= 0) return '';
        return `Rs. ${formatRs(Math.abs(litres) * rate)}`;
      };

      const formatLossWithRs = (litres, rate) => {
        const base = formatNum(litres || 0);
        const rs = toRs(litres, rate);
        return rs ? `${base}\n${rs}` : base;
      };

      const currentImpact = getExecutiveImpactSummary(trendData);
      const prevImpact = getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.prevFuelLoss || 0 })));
      const avg3Impact = {
        loss: Math.round(((prevImpact.loss + getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.m2?.totalFuelLoss || 0 }))).loss + getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.m3?.totalFuelLoss || 0 }))).loss) / 3) * 100) / 100,
        gain: Math.round(((prevImpact.gain + getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.m2?.totalFuelLoss || 0 }))).gain + getExecutiveImpactSummary(trendData.map(row => ({ ...row, totalFuelLoss: row.m3?.totalFuelLoss || 0 }))).gain) / 3) * 100) / 100,
        net: 0
      };
      avg3Impact.net = Math.round((avg3Impact.loss - avg3Impact.gain) * 100) / 100;
      const assetTypeSummary = Object.values(
        trendData.reduce((acc, row) => {
          const key = row.assetType || 'Unspecified';
          if (!acc[key]) {
            acc[key] = {
              assetType: key,
              assetCount: 0,
              totalFuelAdded: 0,
              prevFuelAdded: 0,
              avg3FuelAdded: 0,
              totalFuelConsumed: 0,
              prevFuelConsumed: 0,
              avg3FuelConsumed: 0,
              totalFuelLoss: 0,
              prevFuelLoss: 0,
              avg3FuelLoss: 0
            };
          }
          acc[key].assetCount += 1;
          acc[key].totalFuelAdded += row.totalFuelAdded || 0;
          acc[key].prevFuelAdded += row.prevFuelAdded || 0;
          acc[key].avg3FuelAdded += (((row.prevFuelAdded || 0) + (row.m2?.totalFuelAdded || 0) + (row.m3?.totalFuelAdded || 0)) / 3);
          acc[key].totalFuelConsumed += row.totalFuelConsumed || 0;
          acc[key].prevFuelConsumed += row.prevFuelConsumed || 0;
          acc[key].avg3FuelConsumed += (((row.prevFuelConsumed || 0) + (row.m2?.totalFuelConsumed || 0) + (row.m3?.totalFuelConsumed || 0)) / 3);
          acc[key].totalFuelLoss += row.totalFuelLoss || 0;
          acc[key].prevFuelLoss += row.prevFuelLoss || 0;
          acc[key].avg3FuelLoss += (((row.prevFuelLoss || 0) + (row.m2?.totalFuelLoss || 0) + (row.m3?.totalFuelLoss || 0)) / 3);
          return acc;
        }, {})
      ).sort((a, b) => Math.abs(b.totalFuelLoss) - Math.abs(a.totalFuelLoss));

      // Location Wise Summary aggregation
      const locationSummary = Object.values(
        trendData.reduce((acc, row) => {
          const key = row.location || '-';
          if (!acc[key]) {
            acc[key] = {
              location: key,
              assetCount: 0,
              totalFuelAdded: 0, prevFuelAdded: 0, avg3FuelAdded: 0,
              totalFuelConsumed: 0, prevFuelConsumed: 0, avg3FuelConsumed: 0
            };
          }
          acc[key].assetCount += 1;
          acc[key].totalFuelAdded += row.totalFuelAdded || 0;
          acc[key].prevFuelAdded += row.prevFuelAdded || 0;
          acc[key].avg3FuelAdded += (((row.prevFuelAdded || 0) + (row.m2?.totalFuelAdded || 0) + (row.m3?.totalFuelAdded || 0)) / 3);
          acc[key].totalFuelConsumed += row.totalFuelConsumed || 0;
          acc[key].prevFuelConsumed += row.prevFuelConsumed || 0;
          acc[key].avg3FuelConsumed += (((row.prevFuelConsumed || 0) + (row.m2?.totalFuelConsumed || 0) + (row.m3?.totalFuelConsumed || 0)) / 3);
          return acc;
        }, {})
      ).sort((a, b) => b.totalFuelConsumed - a.totalFuelConsumed);

      // Asset Type grand totals
      const totalFuelAddedAll = assetTypeSummary.reduce((sum, item) => sum + item.totalFuelAdded, 0);
      const totalFuelConsumedAll = assetTypeSummary.reduce((sum, item) => sum + item.totalFuelConsumed, 0);
      const totalFuelLossAll = assetTypeSummary.reduce((sum, item) => sum + item.totalFuelLoss, 0);
      const totalPrevFuelAddedAll = assetTypeSummary.reduce((sum, item) => sum + item.prevFuelAdded, 0);
      const totalAvg3FuelAddedAll = assetTypeSummary.reduce((sum, item) => sum + item.avg3FuelAdded, 0);
      const totalPrevFuelConsumedAll = assetTypeSummary.reduce((sum, item) => sum + item.prevFuelConsumed, 0);
      const totalAvg3FuelConsumedAll = assetTypeSummary.reduce((sum, item) => sum + item.avg3FuelConsumed, 0);
      const totalPrevFuelLossAll = assetTypeSummary.reduce((sum, item) => sum + item.prevFuelLoss, 0);
      const totalAvg3FuelLossAll = assetTypeSummary.reduce((sum, item) => sum + item.avg3FuelLoss, 0);
      const totalAssetCountAll = assetTypeSummary.reduce((sum, item) => sum + item.assetCount, 0);

      // Lowest Full Tank Compliance assets
      const lowestFullTankAssets = trendData
        .filter(row => (row.totalRefills || 0) > 0)
        .map(row => ({
          ...row,
          fullTankPct: ((row.fullTankCount || 0) / (row.totalRefills || 1)) * 100
        }))
        .sort((a, b) => a.fullTankPct - b.fullTankPct)
        .slice(0, 10);

      // Last month's top 10 loss assets — current status
      // Use prevMonthData directly so we capture assets that may not have activity in current month
      const trendDataMap = {};
      trendData.forEach(row => {
        const key = `${row.assetCode}_${row.location || '-'}_${row.subLocation || '-'}`;
        trendDataMap[key] = row;
      });
      const prevTopLossAssets = (prevMonthData || [])
        .filter(row => (row.totalFuelLoss || 0) > 0)
        .sort((a, b) => (b.totalFuelLoss || 0) - (a.totalFuelLoss || 0))
        .slice(0, 10)
        .map(prevRow => {
          const key = `${prevRow.assetCode}_${prevRow.location || '-'}_${prevRow.subLocation || '-'}`;
          const currRow = trendDataMap[key];
          const prevLoss = prevRow.totalFuelLoss || 0;
          const currLoss = currRow ? (currRow.totalFuelLoss || 0) : 0;
          const hasCurrentData = !!currRow;
          return {
            ...prevRow,
            prevFuelLoss: prevLoss,
            totalFuelLoss: currLoss,
            _assetModelObj: prevRow._assetModelObj || (currRow ? currRow._assetModelObj : null),
            lossChange: currLoss - prevLoss,
            hasCurrentData,
            prevAvgRate: prevRow.avgConsumptionRate || 0,
            prevRateUnit: prevRow.consumptionRateUnit || '',
            currAvgRate: currRow ? (currRow.avgConsumptionRate || 0) : 0,
            currRateUnit: currRow ? (currRow.consumptionRateUnit || '') : '',
            stdRate: prevRow.standardRate || (currRow ? (currRow.standardRate || 0) : 0),
            stdRateUnit: prevRow.standardRateUnit || (currRow ? (currRow.standardRateUnit || '') : ''),
            status: !hasCurrentData ? 'No Data'
              : currLoss <= 0 ? 'Resolved'
              : currLoss < prevLoss ? 'Improved' : 'Worsened'
          };
        });

      // Weighted average fuel rate for aggregate Rs. calculations
      const totalLossRs = trendData.reduce((sum, row) => {
        const rate = getFuelRate(row);
        return sum + ((row.totalFuelLoss || 0) > 0 ? (row.totalFuelLoss * rate) : 0);
      }, 0);
      const totalGainRs = trendData.reduce((sum, row) => {
        const rate = getFuelRate(row);
        return sum + ((row.totalFuelLoss || 0) < 0 ? (Math.abs(row.totalFuelLoss) * rate) : 0);
      }, 0);
      const netImpactRs = totalLossRs - totalGainRs;

      const topLossAssets = trendData
        .filter(row => (row.totalFuelLoss || 0) > 0)
        .sort((a, b) => (b.totalFuelLoss || 0) - (a.totalFuelLoss || 0))
        .slice(0, 10);

      const topGainAssets = trendData
        .filter(row => (row.totalFuelLoss || 0) < 0)
        .sort((a, b) => Math.abs(b.totalFuelLoss || 0) - Math.abs(a.totalFuelLoss || 0))
        .slice(0, 10);

      const deteriorationAssets = trendData
        .map(row => ({
          ...row,
          fuelLossDelta: (row.totalFuelLoss || 0) - (row.prevFuelLoss || 0)
        }))
        .filter(row => row.fuelLossDelta > 0)
        .sort((a, b) => b.fuelLossDelta - a.fuelLossDelta)
        .slice(0, 10);

      const improvementAssets = trendData
        .map(row => ({
          ...row,
          fuelLossDelta: (row.totalFuelLoss || 0) - (row.prevFuelLoss || 0)
        }))
        .filter(row => row.fuelLossDelta < 0)
        .sort((a, b) => a.fuelLossDelta - b.fuelLossDelta)
        .slice(0, 10);

      const focusAssets = [...trendData]
        .filter(row => row.totalFuelLoss !== null && row.totalFuelLoss !== undefined)
        .sort((a, b) => {
          const aLoss = a.totalFuelLoss || 0;
          const bLoss = b.totalFuelLoss || 0;
          const aPositive = aLoss > 0 ? 0 : 1;
          const bPositive = bLoss > 0 ? 0 : 1;
          if (aPositive !== bPositive) return aPositive - bPositive;

          const severityA = a.severity?.order || 999;
          const severityB = b.severity?.order || 999;
          if (severityA !== severityB) return severityA - severityB;

          const lossDiff = Math.abs(bLoss) - Math.abs(aLoss);
          if (lossDiff !== 0) return lossDiff;

          const varianceA = Math.abs(a.variancePercent || 0);
          const varianceB = Math.abs(b.variancePercent || 0);
          if (varianceA !== varianceB) return varianceB - varianceA;

          const subLocationCompare = (a.subLocation || '').localeCompare(b.subLocation || '');
          if (subLocationCompare !== 0) return subLocationCompare;
          const locationCompare = (a.location || '').localeCompare(b.location || '');
          if (locationCompare !== 0) return locationCompare;
          const modelCompare = (a.assetModel || '').localeCompare(b.assetModel || '');
          if (modelCompare !== 0) return modelCompare;
          const subTypeCompare = (a.assetSubType || '').localeCompare(b.assetSubType || '');
          if (subTypeCompare !== 0) return subTypeCompare;
          return (a.assetType || '').localeCompare(b.assetType || '');
        })
        .slice(0, 20)
        .map(row => ({ ...row, actionSignal: getExecutiveActionSignal(row) }));

      const portrait = true;
      const doc = new jsPDF('portrait', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const availableWidth = pageWidth - (portrait ? 40 : 30);
      const pageMargin = portrait ? 24 : 20;
      const palette = {
        ink: [24, 39, 75],
        mutedInk: [92, 105, 138],
        line: [220, 227, 240],
        panel: [248, 250, 255],
        panelAlt: [243, 247, 255],
        navy: [31, 68, 130],
        blue: [56, 132, 255],
        green: [56, 161, 105],
        red: [220, 74, 74],
        orange: [224, 115, 44],
        mint: [235, 249, 239],
        peach: [255, 244, 237]
      };
      let yPosition = 20;

      const drawSectionHeader = (title, y, subtitle = '') => {
        doc.setFontSize(portrait ? 16 : 14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...palette.ink);
        doc.text(title, pageMargin, y);

        if (subtitle) {
          doc.setFontSize(portrait ? 9 : 8);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(...palette.mutedInk);
          doc.text(subtitle, pageMargin, y + (portrait ? 14 : 12));
        }

        doc.setDrawColor(...palette.line);
        doc.setLineWidth(1);
        doc.line(pageMargin, y + (subtitle ? (portrait ? 22 : 19) : 10), pageWidth - pageMargin, y + (subtitle ? (portrait ? 22 : 19) : 10));
        doc.setTextColor(0, 0, 0);

        return y + (subtitle ? (portrait ? 40 : 34) : (portrait ? 24 : 20));
      };

      await loadImageToPdf(doc);

      const headerY = 25;
      doc.setFontSize(portrait ? 20 : 18);
      doc.setFont(undefined, 'bold');
      doc.text('Executive Fuel Trend Report', pageWidth - pageMargin, headerY, { align: 'right' });

      doc.setFontSize(portrait ? 12 : 11);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(`${selectedMonth.format('MMMM YYYY')}`, pageWidth - pageMargin, headerY + 18, { align: 'right' });

      try {
        const userData = getUserData();
        if (userData?.organisation) {
          const companyName = typeof userData.organisation === 'string'
            ? userData.organisation
            : (userData.organisation.name || '');
          if (companyName) {
            doc.setFontSize(portrait ? 11 : 10);
            doc.text(companyName, pageWidth - pageMargin, headerY + 32, { align: 'right' });
          }
        }
      } catch (e) {}

      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Generated: ${moment().format('DD MMM YYYY, HH:mm')}`, pageWidth - pageMargin, headerY + 44, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      yPosition = headerY + 55;

      const trendPdfActiveFilters = getActiveFilters();
      if (trendPdfActiveFilters.length > 0) {
        doc.setFillColor(...palette.panelAlt);
        doc.roundedRect(pageMargin, yPosition, pageWidth - (pageMargin * 2), portrait ? 26 : 22, 4, 4, 'F');
        doc.setDrawColor(...palette.line);
        doc.roundedRect(pageMargin, yPosition, pageWidth - (pageMargin * 2), portrait ? 26 : 22, 4, 4, 'S');
        doc.setFontSize(portrait ? 9 : 8);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...palette.blue);
        doc.text('Filtered Report:', pageMargin + 8, yPosition + (portrait ? 17 : 14));
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...palette.ink);
        const trendFilterText = trendPdfActiveFilters.map(f => `${f.label}: ${f.value}`).join('  |  ');
        doc.text(trendFilterText, pageMargin + 84, yPosition + (portrait ? 17 : 14), {
          maxWidth: pageWidth - (pageMargin * 2) - 96
        });
        doc.setTextColor(0, 0, 0);
        yPosition += portrait ? 34 : 30;
      }

      const cardHeight = portrait ? 98 : 82;
      const startX = pageMargin;
      const endX = pageWidth - pageMargin;
      const totalCardArea = endX - startX;
      const cardSpacing = portrait ? 12 : 10;
      const numCards = portrait ? 2 : 6;
      const cardWidth = (totalCardArea - (cardSpacing * (numCards - 1))) / numCards;

      const getTrendText = (value, inverse = false) => {
        const isGood = inverse ? value < 0 : value > 0;
        const isBad = inverse ? value > 0 : value < 0;
        const sign = value >= 0 ? '+' : '';
        return { text: `${sign}${value.toFixed(1)}%`, isGood, isBad };
      };

      const drawTrendCard = (x, y, title, currentVal, prevVal, momChange, avg3Val, colorMain, inverse = false) => {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'F');
        doc.setDrawColor(...palette.line);
        doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'S');
        doc.setFillColor(...colorMain);
        doc.roundedRect(x, y, cardWidth, 4, 3, 3, 'F');
        doc.setFontSize(portrait ? 9 : 7);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...palette.mutedInk);
        doc.text(title, x + 10, y + (portrait ? 18 : 15));
        const valLines = `${currentVal}`.split('\n');
        doc.setFontSize(portrait ? 18 : 15);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...colorMain);
        doc.text(valLines[0], x + 10, y + (portrait ? 38 : 32));
        const hasSubLine = valLines.length > 1;
        const subOffset = portrait ? 12 : 10;
        if (hasSubLine) {
          doc.setFontSize(portrait ? 8 : 6.5);
          doc.setFont(undefined, 'normal');
          doc.text(valLines[1], x + 10, y + (portrait ? 48 : 40));
        }
        const trend = getTrendText(momChange, inverse);
        doc.setFontSize(portrait ? 8.5 : 7);
        doc.setFont(undefined, 'bold');
        if (trend.isGood) doc.setTextColor(...palette.green);
        else if (trend.isBad) doc.setTextColor(...palette.red);
        else doc.setTextColor(...palette.mutedInk);
        doc.text(`MoM: ${trend.text}`, x + 10, y + (portrait ? 58 : 49) + subOffset);
        doc.setFontSize(portrait ? 7.2 : 6);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...palette.mutedInk);
        doc.text(`Prev: ${prevVal}`, x + 10, y + (portrait ? 69 : 58) + subOffset);
        doc.text(`3M Avg: ${avg3Val}`, x + 10, y + (portrait ? 78 : 64) + subOffset);
        doc.setTextColor(0, 0, 0);
      };

      // Compute Rs. totals for fuel added and consumed cards
      const totalFuelAddedRs = trendData.reduce((sum, row) => {
        const rate = getFuelRate(row);
        return sum + ((row.totalFuelAdded || 0) * rate);
      }, 0);
      const totalFuelConsumedRs = trendData.reduce((sum, row) => {
        const rate = getFuelRate(row);
        return sum + ((row.totalFuelConsumed || 0) * rate);
      }, 0);

      // Full tank percentage calculations for inline display
      const currentFTPct = currentTotals.totalRefills > 0 ? (currentTotals.fullTankCount / currentTotals.totalRefills * 100) : 0;
      const prevFTPct = prevTotals.totalRefills > 0 ? (prevTotals.fullTankCount / prevTotals.totalRefills * 100) : 0;
      const avg3FTPct = avg3Totals.totalRefills > 0 ? (avg3Totals.fullTankCount / avg3Totals.totalRefills * 100) : 0;

      const executiveCards = [
        ['Total Assets', filteredData.length.toString(), prevTotals.totalAssets.toString(), prevTotals.totalAssets > 0 ? ((filteredData.length - prevTotals.totalAssets) / prevTotals.totalAssets) * 100 : 0, ((prevTotals.totalAssets + m2Totals.totalAssets + m3Totals.totalAssets) / 3).toFixed(0), [24, 144, 255], false],
        ['Total Refills', Math.round(currentTotals.totalRefills).toString(), Math.round(prevTotals.totalRefills).toString(), momRefillsChange, Math.round(avg3Totals.totalRefills).toString(), [250, 140, 22], false],
        ['Fuel Added (L)', `${formatNum(currentTotals.totalFuelAdded)}${totalFuelAddedRs > 0 ? `\n(Rs. ${formatRs(totalFuelAddedRs)})` : ''}`, formatNum(prevTotals.totalFuelAdded), momFuelAddedChange, formatNum(avg3Totals.totalFuelAdded), [82, 196, 26], false],
        ['Full Tank Events', `${currentTotals.fullTankCount} (${currentFTPct.toFixed(1)}%)`, `${prevTotals.fullTankCount} (${prevFTPct.toFixed(1)}%)`, momFullTankChange, `${avg3Totals.fullTankCount.toFixed(0)} (${avg3FTPct.toFixed(1)}%)`, [250, 84, 28], false],
        ['Fuel Consumed (L)', `${formatNum(currentTotals.totalFuelConsumed)}${totalFuelConsumedRs > 0 ? `\n(Rs. ${formatRs(totalFuelConsumedRs)})` : ''}`, formatNum(prevTotals.totalFuelConsumed), momFuelConsumedChange, formatNum(avg3Totals.totalFuelConsumed), [114, 46, 209], true],
        ['Net Fuel Impact (L)', `${formatNum(currentImpact.net)}${netImpactRs !== 0 ? `\n(Rs. ${formatRs(netImpactRs)})` : ''}`, formatNum(prevImpact.net), prevImpact.net !== 0 ? ((currentImpact.net - prevImpact.net) / Math.abs(prevImpact.net)) * 100 : (currentImpact.net !== 0 ? 100 : 0), formatNum(avg3Impact.net), currentImpact.net >= 0 ? [255, 77, 79] : [82, 196, 26], true]
      ];

      if (portrait) {
        executiveCards.forEach((card, index) => {
          const col = index % 2;
          const row = Math.floor(index / 2);
          drawTrendCard(startX + (cardWidth + cardSpacing) * col, yPosition + row * (cardHeight + 12), ...card);
        });
        yPosition += (cardHeight * 3) + (12 * 2) + 18;
      } else {
        executiveCards.forEach((card, index) => {
          drawTrendCard(startX + (cardWidth + cardSpacing) * index, yPosition, ...card);
        });
        yPosition += cardHeight + 18;
      }

      // Fuel Optimisation Opportunity Section — two cards
      // Card 1: Savings realised from last month's top 10 loss assets
      const savingsFromPrevTop10 = prevTopLossAssets.reduce((sum, row) => {
        if (!row.hasCurrentData) return sum;
        const reduction = (row.prevFuelLoss || 0) - (row.totalFuelLoss || 0);
        if (reduction <= 0) return sum;
        const rate = getFuelRate(row);
        return sum + (reduction * rate);
      }, 0);
      const savingsFromPrevTop10Litres = prevTopLossAssets.reduce((sum, row) => {
        if (!row.hasCurrentData) return sum;
        const reduction = (row.prevFuelLoss || 0) - (row.totalFuelLoss || 0);
        return reduction > 0 ? sum + reduction : sum;
      }, 0);

      // Card 2: Opportunity from current month's top 10 loss assets
      const currentTop10LossRs = topLossAssets.reduce((sum, row) => {
        const rate = getFuelRate(row);
        return sum + ((row.totalFuelLoss || 0) * rate);
      }, 0);
      const currentTop10LossLitres = topLossAssets.reduce((sum, row) => sum + (row.totalFuelLoss || 0), 0);

      yPosition = drawSectionHeader('Fuel Optimisation Opportunity', yPosition, 'Impact of corrective action on high-loss assets.');

      const fooCardGap = portrait ? 14 : 12;
      const fooCardWidth = ((pageWidth - (pageMargin * 2)) - fooCardGap) / 2;
      const fooCardHeight = portrait ? 96 : 84;
      const fooCard1X = pageMargin;
      const fooCard2X = pageMargin + fooCardWidth + fooCardGap;

      // --- Card 1: Savings Realised ---
      doc.setFillColor(240, 253, 244); // light green bg
      doc.setDrawColor(200, 230, 200);
      doc.setLineWidth(0.5);
      doc.roundedRect(fooCard1X, yPosition, fooCardWidth, fooCardHeight, 4, 4, 'FD');
      // Green top accent bar
      doc.setFillColor(...palette.green);
      doc.roundedRect(fooCard1X, yPosition, fooCardWidth, 4, 4, 4, 'F');
      doc.rect(fooCard1X, yPosition + 2, fooCardWidth, 2, 'F');

      // --- Card 1: Savings Realised ---
      let cy = yPosition + (portrait ? 18 : 16);
      doc.setFontSize(portrait ? 8.5 : 7.5);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...palette.mutedInk);
      doc.text('SAVINGS REALISED', fooCard1X + 12, cy);
      doc.setFontSize(portrait ? 7 : 6.2);
      doc.setFont(undefined, 'normal');
      doc.text('(from last month\'s Top 10 loss assets)', fooCard1X + 12, cy + (portrait ? 11 : 9));

      cy += portrait ? 28 : 24;
      doc.setFontSize(portrait ? 18 : 15);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...palette.green);
      doc.text(`Rs. ${formatRs(savingsFromPrevTop10)}`, fooCard1X + 12, cy);

      cy += portrait ? 16 : 14;
      doc.setFontSize(portrait ? 8 : 7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...palette.mutedInk);
      doc.text(`${formatNum(savingsFromPrevTop10Litres)} litres saved this month`, fooCard1X + 12, cy);

      cy += portrait ? 11 : 9;
      doc.setFontSize(portrait ? 9 : 7.5);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...palette.green);
      doc.text(`Annualised: Rs. ${formatRs(savingsFromPrevTop10 * 12)}`, fooCard1X + 12, cy);

      // --- Card 2: Potential Savings ---
      doc.setFillColor(255, 245, 240);
      doc.setDrawColor(235, 210, 200);
      doc.setLineWidth(0.5);
      doc.roundedRect(fooCard2X, yPosition, fooCardWidth, fooCardHeight, 4, 4, 'FD');
      doc.setFillColor(...palette.red);
      doc.roundedRect(fooCard2X, yPosition, fooCardWidth, 4, 4, 4, 'F');
      doc.rect(fooCard2X, yPosition + 2, fooCardWidth, 2, 'F');

      cy = yPosition + (portrait ? 18 : 16);
      doc.setFontSize(portrait ? 8.5 : 7.5);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...palette.mutedInk);
      doc.text('POTENTIAL SAVINGS', fooCard2X + 12, cy);
      doc.setFontSize(portrait ? 7 : 6.2);
      doc.setFont(undefined, 'normal');
      doc.text('(if action taken on this month\'s Top 10 loss assets)', fooCard2X + 12, cy + (portrait ? 11 : 9));

      cy += portrait ? 28 : 24;
      doc.setFontSize(portrait ? 18 : 15);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...palette.red);
      doc.text(`Rs. ${formatRs(currentTop10LossRs)} /month`, fooCard2X + 12, cy);

      cy += portrait ? 16 : 14;
      doc.setFontSize(portrait ? 8 : 7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...palette.mutedInk);
      doc.text(`${formatNum(currentTop10LossLitres)} litres loss per month`, fooCard2X + 12, cy);

      cy += portrait ? 11 : 9;
      doc.setFontSize(portrait ? 9 : 7.5);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...palette.red);
      doc.text(`Annualised: Rs. ${formatRs(currentTop10LossRs * 12)}`, fooCard2X + 12, cy);

      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      yPosition += fooCardHeight + 18;

      const buildAssetIdentity = (row) => {
        const parts = [row.assetCode, row.altname, row.ownercode].filter(Boolean).filter(v => v && v !== '-');
        return parts.length > 0 ? parts.join(' / ') : '-';
      };

      const compactAssetIdentity = (row) => {
        const fullLabel = buildAssetIdentity(row);
        return fullLabel.length > 34 ? `${fullLabel.slice(0, 31)}...` : fullLabel;
      };

      const formatLossGainValue = (value) => {
        if (value === null || value === undefined) return '-';
        return value >= 0 ? `${formatNum(Math.abs(value))} L` : `${formatNum(Math.abs(value))} G`;
      };

      const formatCompactRate = (value, unit) => {
        if (value === null || value === undefined || value === 0) return '-';
        return `${value.toFixed(2)} ${unit || ''}`.trim();
      };
      const formatSignedLossGain = (value) => {
        if (value === null || value === undefined) return '-';
        return `${value > 0 ? '+' : value < 0 ? '-' : ''}${formatNum(Math.abs(value))}`;
      };
      const formatMetricTriplet = (current, previous, avg3, signed = false) => {
        const currentText = signed ? formatSignedLossGain(current) : formatNum(current || 0);
        const previousText = signed ? formatSignedLossGain(previous) : formatNum(previous || 0);
        const avg3Text = signed ? formatSignedLossGain(avg3) : formatNum(avg3 || 0);
        return `${currentText}\nPrev: ${previousText}\n3M: ${avg3Text}`;
      };
      const ensureExecutiveSpace = (requiredHeight) => {
        if (yPosition + requiredHeight > pageHeight - 45) {
          doc.addPage();
          yPosition = 30;
        }
      };
      const drawExecutiveTable = ({ title, subtitle = '', head, body, headFill, headTextColor, columnStyles, minRemaining = 160 }) => {
        if (yPosition > pageHeight - minRemaining) {
          doc.addPage();
          yPosition = 30;
        }
        yPosition = drawSectionHeader(title, yPosition, subtitle);
        autoTable(doc, {
          startY: yPosition,
          head: [head],
          body,
          theme: 'grid',
          tableWidth: pageWidth - (pageMargin * 2),
          margin: { left: pageMargin, right: pageMargin },
          styles: {
            fontSize: 8.4,
            cellPadding: { top: 5, bottom: 5, left: 5, right: 5 },
            overflow: 'linebreak',
            valign: 'middle',
            lineColor: palette.line,
            lineWidth: 0.5
          },
          headStyles: { fillColor: headFill, textColor: headTextColor, fontStyle: 'bold', fontSize: 8.4 },
          alternateRowStyles: { fillColor: palette.panel },
          columnStyles
        });
        yPosition = doc.lastAutoTable.finalY + 18;
      };

      // 2. Last Month's Top Loss Assets — Current Status
      if (prevTopLossAssets.length > 0) {
        ensureExecutiveSpace(160);
        yPosition = drawSectionHeader("Last Month's Top Loss Assets — Current Status", yPosition, 'Tracking whether action was taken on previously flagged high-loss assets.');
        const fmtRate = (val, unit) => (val && val > 0) ? `${val.toFixed(2)} ${unit || ''}`.trim() : '-';
        const formatRateCell = (row) => {
          const currRate = row.hasCurrentData ? fmtRate(row.currAvgRate, row.currRateUnit) : '-';
          const prevRate = `Prev: ${fmtRate(row.prevAvgRate, row.prevRateUnit)}`;
          const stdRate = `Std: ${fmtRate(row.stdRate, row.stdRateUnit)}`;
          return `${currRate}\n${prevRate}\n${stdRate}`;
        };
        const prevLossRateCellStore = {};
        autoTable(doc, {
          startY: yPosition,
          head: [['Asset', 'Last Month Loss (L)', 'This Month Loss (L)', 'Change (L)', 'Consumption Rate', 'Status']],
          body: prevTopLossAssets.map(row => {
            const rate = getFuelRate(row);
            const prevRs = toRs(row.prevFuelLoss || 0, rate);
            if (!row.hasCurrentData) {
              return [
                buildAssetIdentity(row),
                `${formatNum(row.prevFuelLoss || 0)}${prevRs ? `\n(${prevRs})` : ''}`,
                'No Activity',
                '-',
                formatRateCell(row),
                row.status
              ];
            }
            const currRs = toRs(row.totalFuelLoss || 0, rate);
            const changeRs = toRs(Math.abs(row.lossChange || 0), rate);
            return [
              buildAssetIdentity(row),
              `${formatNum(row.prevFuelLoss || 0)}${prevRs ? `\n(${prevRs})` : ''}`,
              `${formatNum(row.totalFuelLoss || 0)}${currRs ? `\n(${currRs})` : ''}`,
              `${(row.lossChange || 0) >= 0 ? '+' : ''}${formatNum(row.lossChange || 0)}${changeRs ? `\n(${changeRs})` : ''}`,
              formatRateCell(row),
              row.status
            ];
          }),
          theme: 'grid',
          tableWidth: pageWidth - (pageMargin * 2),
          margin: { left: pageMargin, right: pageMargin },
          styles: {
            fontSize: 8.4,
            cellPadding: { top: 5, bottom: 5, left: 5, right: 5 },
            overflow: 'linebreak',
            valign: 'middle',
            lineColor: palette.line,
            lineWidth: 0.5
          },
          headStyles: { fillColor: [224, 237, 255], textColor: [24, 100, 200], fontStyle: 'bold', fontSize: 8.4 },
          alternateRowStyles: { fillColor: palette.panel },
          columnStyles: portrait
            ? { 0: { cellWidth: 130 }, 1: { cellWidth: 80, halign: 'right' }, 2: { cellWidth: 80, halign: 'right' }, 3: { cellWidth: 70, halign: 'right' }, 4: { cellWidth: 100, halign: 'right' }, 5: { cellWidth: 85, halign: 'center' } }
            : { 0: { cellWidth: 170 }, 1: { cellWidth: 85, halign: 'right' }, 2: { cellWidth: 85, halign: 'right' }, 3: { cellWidth: 75, halign: 'right' }, 4: { cellWidth: 110, halign: 'right' }, 5: { cellWidth: 70, halign: 'center' } },
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            if (data.column.index === 5) {
              const status = prevTopLossAssets[data.row.index]?.status;
              if (status === 'Improved' || status === 'Resolved') {
                data.cell.styles.textColor = palette.green;
                data.cell.styles.fontStyle = 'bold';
              } else if (status === 'Worsened') {
                data.cell.styles.textColor = palette.red;
                data.cell.styles.fontStyle = 'bold';
              } else if (status === 'No Data') {
                data.cell.styles.textColor = [150, 150, 150];
                data.cell.styles.fontStyle = 'italic';
              }
            }
            if (data.column.index === 3) {
              const rowData = prevTopLossAssets[data.row.index];
              if (!rowData?.hasCurrentData) {
                data.cell.styles.textColor = [150, 150, 150];
              } else {
                const change = rowData?.lossChange || 0;
                if (change < 0) data.cell.styles.textColor = palette.green;
                else if (change > 0) data.cell.styles.textColor = palette.red;
              }
            }
            if (data.column.index === 2 && !prevTopLossAssets[data.row.index]?.hasCurrentData) {
              data.cell.styles.textColor = [150, 150, 150];
              data.cell.styles.fontStyle = 'italic';
              data.cell.styles.halign = 'center';
            }
            // Store consumption rate cell text for custom rendering
            if (data.column.index === 4) {
              const cellText = data.cell.text;
              if (Array.isArray(cellText) && cellText.length >= 2) {
                prevLossRateCellStore[data.row.index] = [...cellText];
                data.cell.text = [];
              }
            }
          },
          didDrawCell: (data) => {
            if (data.section !== 'body' || data.column.index !== 4) return;
            const storedText = prevLossRateCellStore[data.row.index];
            if (!storedText || storedText.length < 2) return;

            const cell = data.cell;
            const paddingObj = cell.styles.cellPadding;
            const paddingRight = typeof paddingObj === 'object' ? (paddingObj.right || 5) : (paddingObj || 5);
            const paddingTop = typeof paddingObj === 'object' ? (paddingObj.top || 5) : (paddingObj || 5);
            const textX = cell.x + cell.width - paddingRight;

            // Line 1: Current rate — normal size, regular weight
            const fontSize = cell.styles.fontSize || 8.4;
            let textY = cell.y + paddingTop + 8;
            doc.setFontSize(fontSize);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
            doc.text(storedText[0], textX, textY, { align: 'right' });

            // Lines 2+: Prev & Std — smaller, italic, muted
            doc.setFontSize(fontSize - 2);
            doc.setFont(undefined, 'italic');
            doc.setTextColor(...palette.mutedInk);
            for (let i = 1; i < storedText.length; i++) {
              textY += 7;
              doc.text(storedText[i], textX, textY, { align: 'right' });
            }
            doc.setFont(undefined, 'normal');
          }
        });
        yPosition = doc.lastAutoTable.finalY + 18;
      }

      // 3. Top Fuel Loss
      if (portrait) {
        drawExecutiveTable({
          title: 'Top Fuel Loss',
          subtitle: 'Highest current-month fuel loss impact.',
          head: ['Asset', 'Qty (L)', 'Amount', 'Avg Rate', 'Std Rate'],
          body: topLossAssets.length > 0
            ? topLossAssets.map(row => [buildAssetIdentity(row), formatNum(row.totalFuelLoss || 0), toRs(row.totalFuelLoss || 0, getFuelRate(row)) || '-', formatCompactRate(row.avgConsumptionRate, row.consumptionRateUnit), formatCompactRate(row.standardRate, row.standardRateUnit)])
            : [['No loss assets', '-', '-', '-', '-']],
          headFill: [255, 242, 232],
          headTextColor: [250, 84, 28],
          columnStyles: { 0: { cellWidth: 210 }, 1: { cellWidth: 65, halign: 'right' }, 2: { cellWidth: 80, halign: 'right' }, 3: { cellWidth: 95, halign: 'right' }, 4: { cellWidth: 95, halign: 'right' } }
        });
      } else {
        drawExecutiveTable({
          title: 'Top Fuel Loss',
          subtitle: 'Highest current-month fuel loss impact.',
          head: ['Asset', 'Qty (L)', 'Amount', 'Avg Rate', 'Std Rate'],
          body: topLossAssets.length > 0
            ? topLossAssets.map(row => [buildAssetIdentity(row), formatNum(row.totalFuelLoss || 0), toRs(row.totalFuelLoss || 0, getFuelRate(row)) || '-', formatCompactRate(row.avgConsumptionRate, row.consumptionRateUnit), formatCompactRate(row.standardRate, row.standardRateUnit)])
            : [['No loss assets', '-', '-', '-', '-']],
          headFill: [255, 242, 232],
          headTextColor: [250, 84, 28],
          columnStyles: { 0: { cellWidth: 270 }, 1: { cellWidth: 75, halign: 'right' }, 2: { cellWidth: 80, halign: 'right' }, 3: { cellWidth: 85, halign: 'right' }, 4: { cellWidth: 85, halign: 'right' } },
          minRemaining: 170
        });
      }

      // MoM Consumption Deterioration (after Top Fuel Loss)
      // Shared rate cell formatter for MoM tables
      const formatMomRateCell = (row) => {
        const fmtR = (val, unit) => (val && val > 0) ? `${val.toFixed(2)} ${unit || ''}`.trim() : '-';
        const curr = fmtR(row.avgConsumptionRate, row.consumptionRateUnit);
        const prev = `Prev: ${fmtR(row.prevAvgRate, row.consumptionRateUnit)}`;
        const std = `Std: ${fmtR(row.standardRate, row.standardRateUnit)}`;
        return `${curr}\n${prev}\n${std}`;
      };
      const drawMomRateCells = (cellStore, data) => {
        if (data.section !== 'body' || data.column.index !== 4) return;
        const storedText = cellStore[data.row.index];
        if (!storedText || storedText.length < 2) return;
        const cell = data.cell;
        const paddingObj = cell.styles.cellPadding;
        const paddingRight = typeof paddingObj === 'object' ? (paddingObj.right || 5) : (paddingObj || 5);
        const paddingTop = typeof paddingObj === 'object' ? (paddingObj.top || 5) : (paddingObj || 5);
        const textX = cell.x + cell.width - paddingRight;
        const fontSize = cell.styles.fontSize || 8.4;
        let textY = cell.y + paddingTop + 6;
        doc.setFontSize(fontSize - 1);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(storedText[0], textX, textY, { align: 'right' });
        doc.setFontSize(fontSize - 3);
        doc.setFont(undefined, 'italic');
        doc.setTextColor(...palette.mutedInk);
        for (let i = 1; i < storedText.length; i++) {
          textY += 6;
          doc.text(storedText[i], textX, textY, { align: 'right' });
        }
        doc.setFont(undefined, 'normal');
      };

      {
        ensureExecutiveSpace(160);
        yPosition = drawSectionHeader('MoM Consumption Deterioration', yPosition, 'Largest increase in fuel loss versus last month.');
        const detRateCellStore = {};
        autoTable(doc, {
          startY: yPosition,
          head: [['Asset', 'Prev Loss', 'Curr Loss', 'Delta', 'Consumption Rate']],
          body: deteriorationAssets.length > 0
            ? deteriorationAssets.map(row => [
                buildAssetIdentity(row),
                formatLossGainValue(row.prevFuelLoss || 0),
                formatLossGainValue(row.totalFuelLoss || 0),
                formatNum(row.fuelLossDelta || 0),
                formatMomRateCell(row)
              ])
            : [['No deterioration', '-', '-', '-', '-']],
          theme: 'grid',
          tableWidth: pageWidth - (pageMargin * 2),
          margin: { left: pageMargin, right: pageMargin },
          styles: { fontSize: 8.4, cellPadding: { top: 4, bottom: 3, left: 5, right: 5 }, overflow: 'linebreak', valign: 'top', lineColor: palette.line, lineWidth: 0.5, minCellHeight: 28 },
          headStyles: { fillColor: [255, 245, 230], textColor: [212, 90, 20], fontStyle: 'bold', fontSize: 8.4 },
          alternateRowStyles: { fillColor: palette.panel },
          columnStyles: portrait
            ? { 0: { cellWidth: 175 }, 1: { cellWidth: 75, halign: 'right' }, 2: { cellWidth: 75, halign: 'right' }, 3: { cellWidth: 70, halign: 'right' }, 4: { cellWidth: 150, halign: 'right' } }
            : { 0: { cellWidth: 220 }, 1: { cellWidth: 75, halign: 'right' }, 2: { cellWidth: 75, halign: 'right' }, 3: { cellWidth: 70, halign: 'right' }, 4: { cellWidth: 155, halign: 'right' } },
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            if (data.column.index === 4) {
              const cellText = data.cell.text;
              if (Array.isArray(cellText) && cellText.length >= 2) {
                detRateCellStore[data.row.index] = [...cellText];
                data.cell.text = [];
              }
            }
          },
          didDrawCell: (data) => drawMomRateCells(detRateCellStore, data)
        });
        yPosition = doc.lastAutoTable.finalY + 18;
      }

      // 4. Suggested Focus Assets
      const focusSectionMinHeight = portrait ? 70 : 42;
      if (yPosition + focusSectionMinHeight > pageHeight - 45) {
        doc.addPage();
        yPosition = 30;
      }

      yPosition = drawSectionHeader('Suggested Focus Assets', yPosition, 'Prioritized using fuel impact first, then severity and variance.');

      autoTable(doc, {
        startY: yPosition,
        head: [portrait
          ? ['Severity', 'Asset', 'Fuel Loss / Gain (L)', 'Avg / Std', 'Variance %', 'Action Signal']
          : ['Severity', 'Asset Code', 'Location / Sub Location', 'Fuel Loss / Gain (L)', 'Avg Rate', 'Std Rate', 'Variance %', 'Action Signal']],
        body: focusAssets.length > 0 ? focusAssets.map(row => (
          portrait
            ? [
                row.severity?.level ? `${row.severity.level}${row.severity?.isBetter ? ' (-)' : row.severity?.level !== 'OK' ? ' (+)' : ''}` : '-',
                `${buildAssetIdentity(row)}\n${[row.location || '-', row.subLocation || '-'].join(' / ')}`,
                row.totalFuelLoss != null ? `${row.totalFuelLoss > 0 ? '+' : '-'}${formatNum(Math.abs(row.totalFuelLoss))}${(() => { const rs = toRs(row.totalFuelLoss, getFuelRate(row)); return rs ? `\n(${rs})` : ''; })()}` : '-',
                `${row.avgConsumptionRate ? `${row.avgConsumptionRate.toFixed(2)} ${row.consumptionRateUnit || ''}` : '-'}\n${row.standardRate ? `${row.standardRate.toFixed(2)} ${row.standardRateUnit || ''}` : '-'}`,
                row.variancePercent != null ? `${row.variancePercent.toFixed(1)}%` : '-',
                row.actionSignal
              ]
            : [
                row.severity?.level ? `${row.severity.level}${row.severity?.isBetter ? ' (-)' : row.severity?.level !== 'OK' ? ' (+)' : ''}` : '-',
                buildAssetIdentity(row),
                [row.location || '-', row.subLocation || '-'].join(' / '),
                row.totalFuelLoss != null ? `${row.totalFuelLoss > 0 ? '+' : '-'}${formatNum(Math.abs(row.totalFuelLoss))}${(() => { const rs = toRs(row.totalFuelLoss, getFuelRate(row)); return rs ? `\n(${rs})` : ''; })()}` : '-',
                row.avgConsumptionRate ? `${row.avgConsumptionRate.toFixed(2)} ${row.consumptionRateUnit || ''}` : '-',
                row.standardRate ? `${row.standardRate.toFixed(2)} ${row.standardRateUnit || ''}` : '-',
                row.variancePercent != null ? `${row.variancePercent.toFixed(1)}%` : '-',
                row.actionSignal
              ]
        )) : [portrait ? ['-', 'No focus assets', '-', '-', '-', '-'] : ['-', 'No focus assets', '-', '-', '-', '-', '-', '-']],
        theme: 'striped',
        margin: { left: pageMargin, right: pageMargin },
        styles: { fontSize: portrait ? 8.3 : 7.3, cellPadding: { top: portrait ? 5 : 4, bottom: portrait ? 5 : 4, left: 4, right: 4 }, valign: 'middle', lineColor: palette.line, lineWidth: 0.5 },
        headStyles: { fillColor: palette.navy, textColor: 255, fontStyle: 'bold', fontSize: portrait ? 8.4 : 7.4 },
        alternateRowStyles: { fillColor: palette.panel },
        columnStyles: portrait ? {
          0: { cellWidth: 50, halign: 'center' },
          1: { cellWidth: 145 },
          2: { cellWidth: 78, halign: 'right' },
          3: { cellWidth: 82, halign: 'right' },
          4: { cellWidth: 55, halign: 'right' },
          5: { cellWidth: 135 }
        } : {
          0: { cellWidth: 55, halign: 'center' },
          1: { cellWidth: 110 },
          2: { cellWidth: 120 },
          3: { cellWidth: 85, halign: 'right' },
          4: { cellWidth: 75, halign: 'right' },
          5: { cellWidth: 75, halign: 'right' },
          6: { cellWidth: 60, halign: 'right' },
          7: { cellWidth: 150 }
        }
      });

      yPosition = doc.lastAutoTable.finalY + 24;
      if (false && !portrait) {
        if (yPosition > pageHeight - 120) {
          doc.addPage();
          yPosition = 30;
        }

        yPosition = drawSectionHeader('Asset-wise Trend Analysis Detail', yPosition, 'Detailed asset-level trend view with month-on-month and 3M comparison.');
      }

      if (false && !portrait) {
      const detailTableColumns = [
        'SN', 'Severity', 'Asset Code', 'Type', 'Sub Type', 'Model', 'Location', 'Sub Location',
        'Refills', 'Fuel Added (L)', 'Full Tank', 'Meter Run', 'Meter',
        'Consumed (L)', 'Avg Rate', 'Std Rate', 'Variance', 'Fuel Loss (L)'
      ];

      const formatTrendSeverity = (severity) => {
        if (!severity || !severity.level) return '-';
        if (severity.level === 'OK') return 'OK';
        const indicator = severity.isBetter ? ' (-)' : ' (+)';
        return severity.level + indicator;
      };

      const formatAssetCodeForTrendPdf = (row) => {
        const parts = [row.assetCode || '-'];
        if (row.altname) parts.push(row.altname);
        if (row.ownercode) parts.push(row.ownercode);
        return parts.join('\n');
      };

      const detailTableData = trendData.map((row, idx) => {
        const formatWithTrend = (curr, prev, mom, avg3, useThousands = false, decimals = 2, suffix = '') => {
          const fmt = (v) => useThousands ? formatNum(v) : (typeof v === 'number' ? (decimals === 0 ? Math.round(v).toString() : v.toFixed(decimals)) : String(v));
          const currStr = fmt(curr) + suffix;
          const prevStr = fmt(prev);
          const avg3Str = fmt(avg3);
          const momSign = mom >= 0 ? '+' : '';
          return `${currStr}\nPrev: ${prevStr}\nMoM: ${momSign}${mom.toFixed(0)}%\n3M Avg: ${avg3Str}`;
        };

        const meterRunVal = row.totalMeterRun > 0 ? row.totalMeterRun : 0;
        const meterRunPrev = row.prevMeterRun || 0;
        const avgRateVal = row.avgConsumptionRate || 0;
        const avgRatePrev = row.prevAvgRate || 0;
        const rateUnit = row.consumptionRateUnit || '';

        return [
          idx + 1,
          formatTrendSeverity(row.severity),
          formatAssetCodeForTrendPdf(row),
          row.assetType || '-',
          row.assetSubType || '-',
          row.assetModel || '-',
          row.location || '-',
          row.subLocation || '-',
          formatWithTrend(row.totalRefills, row.prevRefills, row.momRefills, row.avg3Refills, false, 0),
          formatWithTrend(row.totalFuelAdded, row.prevFuelAdded, row.momFuelAdded, row.avg3FuelAdded, true),
          row.fullTankCount,
          meterRunVal > 0 ? formatWithTrend(meterRunVal, meterRunPrev, row.momMeterRun, row.avg3MeterRun, false, 2) : '-',
          row.meterName || '-',
          formatWithTrend(row.totalFuelConsumed, row.prevFuelConsumed, row.momFuelConsumed, row.avg3FuelConsumed, true),
          avgRateVal > 0 ? formatWithTrend(avgRateVal, avgRatePrev, row.momAvgRate, row.avg3AvgRate, false, 2, rateUnit ? ` ${rateUnit}` : '') : '-',
          row.standardRate ? `${row.standardRate.toFixed(2)} ${row.standardRateUnit}` : '-',
          row.variancePercent !== null ? `${row.variancePercent.toFixed(1)}%` : '-',
          row.totalFuelLoss !== null ? formatWithTrend(row.totalFuelLoss, row.prevFuelLoss, row.momFuelLoss, row.avg3FuelLoss, true) : '-'
        ];
      });

      const detailTrendColumns = [8, 9, 11, 13, 14, 17];
      const cellTextStore = {};

      autoTable(doc, {
        startY: yPosition,
        head: [detailTableColumns],
        body: detailTableData,
        theme: 'striped',
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        styles: {
          fontSize: 5.2,
          cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
          overflow: 'linebreak',
          halign: 'left',
          valign: 'top',
          lineWidth: 0.5,
          lineColor: palette.line
        },
        headStyles: {
          fillColor: palette.navy,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 5.8,
          halign: 'center',
          valign: 'middle',
          minCellHeight: 15,
          cellPadding: { top: 4, bottom: 4, left: 3, right: 3 }
        },
        bodyStyles: { fontSize: 5.2, minCellHeight: 38, valign: 'top' },
        alternateRowStyles: { fillColor: palette.panel },
        margin: { left: 15, right: 15, top: 20, bottom: 20 },
        tableWidth: availableWidth,
        columnStyles: {
          0: { cellWidth: 18, halign: 'center' },
          1: { cellWidth: 48, halign: 'center' },
          2: { cellWidth: 48, halign: 'left' },
          3: { cellWidth: 38, halign: 'left' },
          4: { cellWidth: 42, halign: 'left' },
          5: { cellWidth: 45, halign: 'left' },
          6: { cellWidth: 42, halign: 'left' },
          7: { cellWidth: 42, halign: 'left' },
          8: { cellWidth: 52, halign: 'right' },
          9: { cellWidth: 55, halign: 'right' },
          10: { cellWidth: 32, halign: 'right' },
          11: { cellWidth: 38, halign: 'right' },
          12: { cellWidth: 32, halign: 'left' },
          13: { cellWidth: 55, halign: 'right' },
          14: { cellWidth: 48, halign: 'right' },
          15: { cellWidth: 48, halign: 'right' },
          16: { cellWidth: 38, halign: 'right' },
          17: { cellWidth: 42, halign: 'right' }
        },
        didParseCell: function(data) {
          const columnIndex = data.column.index;
          if (columnIndex === 1) {
            data.cell.styles.halign = 'center';
            data.cell.styles.valign = 'top';
          }
          if (data.row.section !== 'body') return;

          const rowIndex = data.row.index;
          if (columnIndex === 1 && rowIndex < trendData.length) {
            const severity = trendData[rowIndex].severity;
            if (severity && severity.level) {
              data.cell.styles.fontStyle = 'bold';
              if (severity.level === 'CRITICAL') data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [255, 77, 79];
              else if (severity.level === 'HIGH') data.cell.styles.textColor = severity.isBetter ? [114, 46, 209] : [250, 84, 28];
              else if (severity.level === 'MEDIUM') data.cell.styles.textColor = severity.isBetter ? [19, 194, 194] : [250, 173, 20];
              else if (severity.level === 'LOW') data.cell.styles.textColor = severity.isBetter ? [82, 196, 26] : [24, 144, 255];
              else if (severity.level === 'OK') data.cell.styles.textColor = [82, 196, 26];
            }
          }
          if (columnIndex === 16 && rowIndex < trendData.length) {
            const variance = trendData[rowIndex].variancePercent;
            if (variance > 0) data.cell.styles.textColor = [255, 77, 79];
            else if (variance < 0) data.cell.styles.textColor = [82, 196, 26];
          }
          if (columnIndex === 17 && rowIndex < trendData.length) {
            const fuelLoss = trendData[rowIndex].totalFuelLoss;
            if (fuelLoss > 0) data.cell.styles.textColor = [255, 77, 79];
            else if (fuelLoss < 0) data.cell.styles.textColor = [82, 196, 26];
          }
          if (detailTrendColumns.includes(columnIndex)) {
            const cellText = data.cell.text;
            if (Array.isArray(cellText) && cellText.length >= 2) {
              const key = `${rowIndex}_${columnIndex}`;
              cellTextStore[key] = [...cellText];
              data.cell.text = [];
            }
          }
        },
        didDrawCell: function(data) {
          if (data.row.section !== 'body') return;
          const columnIndex = data.column.index;
          if (!detailTrendColumns.includes(columnIndex)) return;

          const rowIndex = data.row.index;
          const key = `${rowIndex}_${columnIndex}`;
          const storedText = cellTextStore[key];
          if (!storedText || storedText.length < 2) return;

          const cell = data.cell;
          const x = cell.x;
          const y = cell.y;
          const cellWidth = cell.width;
          const paddingObj = cell.styles.cellPadding;
          const paddingLeft = typeof paddingObj === 'object' ? (paddingObj.left || 3) : (paddingObj || 3);
          const paddingRight = typeof paddingObj === 'object' ? (paddingObj.right || 3) : (paddingObj || 3);
          const paddingTop = typeof paddingObj === 'object' ? (paddingObj.top || 2) : (paddingObj || 2);
          const halign = cell.styles.halign || 'left';
          let textX;
          if (halign === 'right') textX = x + cellWidth - paddingRight;
          else if (halign === 'center') textX = x + cellWidth / 2;
          else textX = x + paddingLeft;

          let textY = y + paddingTop + 5;
          const baseColor = cell.styles.textColor;
          let textColorArr = [0, 0, 0];
          if (Array.isArray(baseColor)) textColorArr = baseColor;
          else if (typeof baseColor === 'number') textColorArr = [baseColor, baseColor, baseColor];

          doc.setFontSize((cell.styles.fontSize || 5) + 0.5);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(textColorArr[0], textColorArr[1], textColorArr[2]);
          doc.text(storedText[0], textX, textY, { align: halign });
          textY += 8;

          doc.setFontSize((cell.styles.fontSize || 5) - 0.5);
          doc.setFont(undefined, 'italic');
          for (let i = 1; i < storedText.length; i++) {
            const lineText = storedText[i];
            if (columnIndex === 14 && lineText.startsWith('MoM:')) {
              const momMatch = lineText.match(/MoM:\s*([+-]?\d+)/);
              if (momMatch) {
                const momValue = parseFloat(momMatch[1]);
                if (momValue < 0) doc.setTextColor(82, 196, 26);
                else if (momValue > 0) doc.setTextColor(255, 77, 79);
                else doc.setTextColor(100, 100, 100);
              } else {
                doc.setTextColor(100, 100, 100);
              }
            } else {
              doc.setTextColor(100, 100, 100);
            }
            doc.text(lineText, textX, textY, { align: halign });
            textY += 5;
          }
          doc.setFont(undefined, 'normal');
        }
      });
      }

      // 5. Lowest Full Tank Compliance Table
      drawExecutiveTable({
        title: 'Lowest Full Tank Compliance',
        subtitle: 'Assets with lowest proportion of full tank events this month.',
        head: ['Asset', 'Total Refills', 'Full Tanks', 'Full Tank %'],
        body: lowestFullTankAssets.length > 0
          ? lowestFullTankAssets.map(row => [
              buildAssetIdentity(row),
              (row.totalRefills || 0).toString(),
              (row.fullTankCount || 0).toString(),
              row.fullTankPct.toFixed(1) + '%'
            ])
          : [['No data', '-', '-', '-']],
        headFill: [255, 243, 224],
        headTextColor: [230, 126, 34],
        columnStyles: portrait
          ? { 0: { cellWidth: 275 }, 1: { cellWidth: 80, halign: 'right' }, 2: { cellWidth: 80, halign: 'right' }, 3: { cellWidth: 110, halign: 'right' } }
          : { 0: { cellWidth: 335 }, 1: { cellWidth: 80, halign: 'right' }, 2: { cellWidth: 80, halign: 'right' }, 3: { cellWidth: 100, halign: 'right' } },
        minRemaining: 170
      });

      // 6. Asset Type Wise Summary Table
      ensureExecutiveSpace(portrait ? 220 : 170);
      yPosition = drawSectionHeader('Asset Type Wise Summary', yPosition, 'Category-level totals to identify which asset types need more focus.');
      const assetTypeMetricColumns = [2, 3, 5];
      const assetTypeCellTextStore = {};
      const assetTypeBody = assetTypeSummary.length > 0
        ? [
            ...assetTypeSummary.map(item => [
              item.assetType || 'Unspecified',
              item.assetCount,
              formatMetricTriplet(item.totalFuelAdded, item.prevFuelAdded, item.avg3FuelAdded),
              formatMetricTriplet(item.totalFuelConsumed, item.prevFuelConsumed, item.avg3FuelConsumed),
              totalFuelConsumedAll > 0 ? (item.totalFuelConsumed / totalFuelConsumedAll * 100).toFixed(1) + '%' : '-',
              formatMetricTriplet(item.totalFuelLoss, item.prevFuelLoss, item.avg3FuelLoss, true)
            ]),
            [
              'Total',
              totalAssetCountAll,
              formatMetricTriplet(totalFuelAddedAll, totalPrevFuelAddedAll, totalAvg3FuelAddedAll),
              formatMetricTriplet(totalFuelConsumedAll, totalPrevFuelConsumedAll, totalAvg3FuelConsumedAll),
              '100%',
              formatMetricTriplet(totalFuelLossAll, totalPrevFuelLossAll, totalAvg3FuelLossAll, true)
            ]
          ]
        : [['No asset type data', '-', '-', '-', '-', '-']];
      autoTable(doc, {
        startY: yPosition,
        head: [['Asset Type', 'Assets', 'Total Fuel Added (L)', 'Fuel Consumed (L)', '% Consumption', 'Total Loss / Gain (L)']],
        body: assetTypeBody,
        theme: 'striped',
        tableWidth: pageWidth - (pageMargin * 2),
        margin: { left: pageMargin, right: pageMargin },
        styles: {
          fontSize: portrait ? 8.6 : 7.8,
          cellPadding: { top: portrait ? 5 : 4, bottom: portrait ? 5 : 4, left: 5, right: 5 },
          valign: 'middle',
          lineColor: palette.line,
          lineWidth: 0.5
        },
        bodyStyles: {
          minCellHeight: portrait ? 36 : 30,
          valign: 'middle'
        },
        headStyles: {
          fillColor: palette.navy,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: portrait ? 8.8 : 8
        },
        alternateRowStyles: { fillColor: palette.panel },
        columnStyles: portrait ? {
          0: { cellWidth: 120 },
          1: { cellWidth: 40, halign: 'center' },
          2: { cellWidth: 105, halign: 'right' },
          3: { cellWidth: 105, halign: 'right' },
          4: { cellWidth: 55, halign: 'center' },
          5: { cellWidth: 120, halign: 'right' }
        } : {
          0: { cellWidth: 120 },
          1: { cellWidth: 38, halign: 'center' },
          2: { cellWidth: 110, halign: 'right' },
          3: { cellWidth: 110, halign: 'right' },
          4: { cellWidth: 52, halign: 'center' },
          5: { cellWidth: 125, halign: 'right' }
        },
        didParseCell: (data) => {
          if (data.section !== 'body') return;
          const isTotalsRow = data.row.index === assetTypeSummary.length && assetTypeSummary.length > 0;
          if (isTotalsRow) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [230, 237, 250];
          }
          if (data.column.index === 5) {
            const value = isTotalsRow ? totalFuelLossAll : assetTypeSummary[data.row.index]?.totalFuelLoss;
            if (value > 0) data.cell.styles.textColor = palette.red;
            else if (value < 0) data.cell.styles.textColor = palette.green;
          }
          if (assetTypeMetricColumns.includes(data.column.index)) {
            const cellText = data.cell.text;
            if (Array.isArray(cellText) && cellText.length >= 2) {
              const key = `${data.row.index}_${data.column.index}`;
              assetTypeCellTextStore[key] = [...cellText];
              data.cell.text = [];
            }
          }
        },
        didDrawCell: (data) => {
          if (data.section !== 'body') return;
          const columnIndex = data.column.index;
          if (!assetTypeMetricColumns.includes(columnIndex)) return;

          const key = `${data.row.index}_${columnIndex}`;
          const storedText = assetTypeCellTextStore[key];
          if (!storedText || storedText.length < 2) return;

          const cell = data.cell;
          const x = cell.x;
          const y = cell.y;
          const cellWidth = cell.width;
          const paddingObj = cell.styles.cellPadding;
          const paddingLeft = typeof paddingObj === 'object' ? (paddingObj.left || 5) : (paddingObj || 5);
          const paddingRight = typeof paddingObj === 'object' ? (paddingObj.right || 5) : (paddingObj || 5);
          const paddingTop = typeof paddingObj === 'object' ? (paddingObj.top || 4) : (paddingObj || 4);
          const halign = cell.styles.halign || 'right';
          let textX;
          if (halign === 'right') textX = x + cellWidth - paddingRight;
          else if (halign === 'center') textX = x + cellWidth / 2;
          else textX = x + paddingLeft;

          let textY = y + paddingTop + (portrait ? 8 : 7);
          const baseColor = cell.styles.textColor;
          let primaryColor = [0, 0, 0];
          if (Array.isArray(baseColor)) primaryColor = baseColor;
          else if (typeof baseColor === 'number') primaryColor = [baseColor, baseColor, baseColor];

          doc.setFontSize((cell.styles.fontSize || (portrait ? 8.6 : 7.8)) + 0.8);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.text(storedText[0], textX, textY, { align: halign });

          textY += portrait ? 10 : 9;
          doc.setFontSize((cell.styles.fontSize || (portrait ? 8.6 : 7.8)) - 1.2);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(...palette.mutedInk);
          for (let i = 1; i < storedText.length; i++) {
            doc.text(storedText[i], textX, textY, { align: halign });
            textY += portrait ? 7 : 6;
          }
          doc.setFont(undefined, 'normal');
        }
      });
      yPosition = doc.lastAutoTable.finalY + 22;

      // Top Fuel Gain
      if (portrait) {
        drawExecutiveTable({
          title: 'Top Fuel Gain',
          subtitle: 'Highest current-month fuel gain impact.',
          head: ['Asset', 'Qty (L)', 'Amount', 'Avg Rate', 'Std Rate'],
          body: topGainAssets.length > 0
            ? topGainAssets.map(row => [buildAssetIdentity(row), formatNum(Math.abs(row.totalFuelLoss || 0)), toRs(row.totalFuelLoss || 0, getFuelRate(row)) || '-', formatCompactRate(row.avgConsumptionRate, row.consumptionRateUnit), formatCompactRate(row.standardRate, row.standardRateUnit)])
            : [['No gain assets', '-', '-', '-', '-']],
          headFill: [246, 255, 237],
          headTextColor: [82, 196, 26],
          columnStyles: { 0: { cellWidth: 210 }, 1: { cellWidth: 65, halign: 'right' }, 2: { cellWidth: 80, halign: 'right' }, 3: { cellWidth: 95, halign: 'right' }, 4: { cellWidth: 95, halign: 'right' } }
        });
      } else {
        drawExecutiveTable({
          title: 'Top Fuel Gain',
          subtitle: 'Highest current-month fuel gain impact.',
          head: ['Asset', 'Qty (L)', 'Amount', 'Avg Rate', 'Std Rate'],
          body: topGainAssets.length > 0
            ? topGainAssets.map(row => [buildAssetIdentity(row), formatNum(Math.abs(row.totalFuelLoss || 0)), toRs(row.totalFuelLoss || 0, getFuelRate(row)) || '-', formatCompactRate(row.avgConsumptionRate, row.consumptionRateUnit), formatCompactRate(row.standardRate, row.standardRateUnit)])
            : [['No gain assets', '-', '-', '-', '-']],
          headFill: [246, 255, 237],
          headTextColor: [82, 196, 26],
          columnStyles: { 0: { cellWidth: 270 }, 1: { cellWidth: 75, halign: 'right' }, 2: { cellWidth: 80, halign: 'right' }, 3: { cellWidth: 85, halign: 'right' }, 4: { cellWidth: 85, halign: 'right' } },
          minRemaining: 170
        });
      }

      // MoM Consumption Improvement
      {
        ensureExecutiveSpace(160);
        yPosition = drawSectionHeader('MoM Consumption Improvement', yPosition, 'Largest reduction in fuel loss versus last month.');
        const impRateCellStore = {};
        autoTable(doc, {
          startY: yPosition,
          head: [['Asset', 'Prev Loss', 'Curr Loss', 'Delta', 'Consumption Rate']],
          body: improvementAssets.length > 0
            ? improvementAssets.map(row => [
                buildAssetIdentity(row),
                formatLossGainValue(row.prevFuelLoss || 0),
                formatLossGainValue(row.totalFuelLoss || 0),
                formatNum(Math.abs(row.fuelLossDelta || 0)),
                formatMomRateCell(row)
              ])
            : [['No improvement', '-', '-', '-', '-']],
          theme: 'grid',
          tableWidth: pageWidth - (pageMargin * 2),
          margin: { left: pageMargin, right: pageMargin },
          styles: { fontSize: 8.4, cellPadding: { top: 4, bottom: 3, left: 5, right: 5 }, overflow: 'linebreak', valign: 'top', lineColor: palette.line, lineWidth: 0.5, minCellHeight: 28 },
          headStyles: { fillColor: [240, 255, 245], textColor: [46, 125, 50], fontStyle: 'bold', fontSize: 8.4 },
          alternateRowStyles: { fillColor: palette.panel },
          columnStyles: portrait
            ? { 0: { cellWidth: 175 }, 1: { cellWidth: 75, halign: 'right' }, 2: { cellWidth: 75, halign: 'right' }, 3: { cellWidth: 70, halign: 'right' }, 4: { cellWidth: 150, halign: 'right' } }
            : { 0: { cellWidth: 220 }, 1: { cellWidth: 75, halign: 'right' }, 2: { cellWidth: 75, halign: 'right' }, 3: { cellWidth: 70, halign: 'right' }, 4: { cellWidth: 155, halign: 'right' } },
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            if (data.column.index === 4) {
              const cellText = data.cell.text;
              if (Array.isArray(cellText) && cellText.length >= 2) {
                impRateCellStore[data.row.index] = [...cellText];
                data.cell.text = [];
              }
            }
          },
          didDrawCell: (data) => drawMomRateCells(impRateCellStore, data)
        });
        yPosition = doc.lastAutoTable.finalY + 18;
      }

      // Location Wise Summary Table
      ensureExecutiveSpace(portrait ? 220 : 170);
      yPosition = drawSectionHeader('Location Wise Summary', yPosition, 'Fuel added and consumed aggregated by location.');
      const locationMetricColumns = [2, 3];
      const locationCellTextStore = {};
      autoTable(doc, {
        startY: yPosition,
        head: [['Location', 'Assets', 'Total Fuel Added (L)', 'Fuel Consumed (L)']],
        body: locationSummary.length > 0
          ? locationSummary.map(item => [
              item.location || '-',
              item.assetCount,
              formatMetricTriplet(item.totalFuelAdded, item.prevFuelAdded, item.avg3FuelAdded),
              formatMetricTriplet(item.totalFuelConsumed, item.prevFuelConsumed, item.avg3FuelConsumed)
            ])
          : [['No location data', '-', '-', '-']],
        theme: 'striped',
        tableWidth: pageWidth - (pageMargin * 2),
        margin: { left: pageMargin, right: pageMargin },
        styles: {
          fontSize: portrait ? 8.6 : 7.8,
          cellPadding: { top: portrait ? 5 : 4, bottom: portrait ? 5 : 4, left: 5, right: 5 },
          valign: 'middle',
          lineColor: palette.line,
          lineWidth: 0.5
        },
        bodyStyles: {
          minCellHeight: portrait ? 36 : 30,
          valign: 'middle'
        },
        headStyles: {
          fillColor: palette.navy,
          textColor: 255,
          fontStyle: 'bold',
          fontSize: portrait ? 8.8 : 8
        },
        alternateRowStyles: { fillColor: palette.panel },
        columnStyles: portrait ? {
          0: { cellWidth: 160 },
          1: { cellWidth: 55, halign: 'center' },
          2: { cellWidth: 165, halign: 'right' },
          3: { cellWidth: 165, halign: 'right' }
        } : {
          0: { cellWidth: 160 },
          1: { cellWidth: 50, halign: 'center' },
          2: { cellWidth: 175, halign: 'right' },
          3: { cellWidth: 175, halign: 'right' }
        },
        didParseCell: (data) => {
          if (data.section !== 'body') return;
          if (locationMetricColumns.includes(data.column.index)) {
            const cellText = data.cell.text;
            if (Array.isArray(cellText) && cellText.length >= 2) {
              const key = `${data.row.index}_${data.column.index}`;
              locationCellTextStore[key] = [...cellText];
              data.cell.text = [];
            }
          }
        },
        didDrawCell: (data) => {
          if (data.section !== 'body') return;
          const columnIndex = data.column.index;
          if (!locationMetricColumns.includes(columnIndex)) return;

          const key = `${data.row.index}_${columnIndex}`;
          const storedText = locationCellTextStore[key];
          if (!storedText || storedText.length < 2) return;

          const cell = data.cell;
          const x = cell.x;
          const y = cell.y;
          const cellWidth = cell.width;
          const paddingObj = cell.styles.cellPadding;
          const paddingLeft = typeof paddingObj === 'object' ? (paddingObj.left || 5) : (paddingObj || 5);
          const paddingRight = typeof paddingObj === 'object' ? (paddingObj.right || 5) : (paddingObj || 5);
          const paddingTop = typeof paddingObj === 'object' ? (paddingObj.top || 4) : (paddingObj || 4);
          const halign = cell.styles.halign || 'right';
          let textX;
          if (halign === 'right') textX = x + cellWidth - paddingRight;
          else if (halign === 'center') textX = x + cellWidth / 2;
          else textX = x + paddingLeft;

          let textY = y + paddingTop + (portrait ? 8 : 7);
          doc.setFontSize((cell.styles.fontSize || (portrait ? 8.6 : 7.8)) + 0.8);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text(storedText[0], textX, textY, { align: halign });

          textY += portrait ? 10 : 9;
          doc.setFontSize((cell.styles.fontSize || (portrait ? 8.6 : 7.8)) - 1.2);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(...palette.mutedInk);
          for (let i = 1; i < storedText.length; i++) {
            doc.text(storedText[i], textX, textY, { align: halign });
            textY += portrait ? 7 : 6;
          }
          doc.setFont(undefined, 'normal');
        }
      });
      yPosition = doc.lastAutoTable.finalY + 22;

      const fuelRateFooter = fuelRateEntries.length > 0
        ? 'Fuel Rates: ' + fuelRateEntries.map(e => `${e.name}: Rs. ${e.rate.toFixed(2)}/L`).join('  |  ')
        : '';

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 50, pageHeight - 15);
        doc.text(`Executive Fuel Trend Report - ${selectedMonth.format('MMMM YYYY')}`, 20, pageHeight - 15);
        if (fuelRateFooter) {
          doc.setFontSize(6.5);
          doc.text(fuelRateFooter, pageWidth / 2, pageHeight - 7, { align: 'center' });
        }
      }

      doc.save(`Executive_Fuel_Trend_Report_${selectedMonth.format('YYYY-MM')}.pdf`);
      message.destroy('execTrendPdf');
      message.success('Executive Trend Report downloaded successfully');
    } catch (error) {
      console.error('Error generating Executive Trend PDF:', error);
      message.destroy('execTrendPdf');
      message.error('Failed to generate Executive Trend Report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExecutiveTrendPDF = async () => generateExecutiveTrendPDF();
  const handleDownloadAssetWiseDetailedPDF = async () => generateExecutiveTrendAnnexurePDF();

  // Email Report Functions
  const handleEmailReport = async () => {
    // Reset state when opening modal
    setSelectedUsers([]);
    setUserSearchText('');
    
    // Check if email server is online
    const isOnline = await checkEmailServerHealth();
    setEmailServerOnline(isOnline);
    
    // Fetch organization users
    setLoadingUsers(true);
    setEmailModalVisible(true);
    
    try {
      const result = await fetchOrganizationUsers();
      if (result.success) {
        setOrganizationUsers(result.data || []);
      } else {
        message.error(result.error || 'Failed to fetch users');
        setOrganizationUsers([]);
      }
    } catch (error) {
      message.error('Failed to fetch organization users');
      setOrganizationUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = (userId, checked) => {
    if (checked) {
      // Prevent duplicates
      setSelectedUsers(prev => prev.includes(userId) ? prev : [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAllUsers = (checked) => {
    if (checked) {
      const filteredUserIds = getFilteredUsers().map(u => u._id);
      setSelectedUsers(filteredUserIds);
    } else {
      setSelectedUsers([]);
    }
  };

  const getFilteredUsers = () => {
    if (!userSearchText) return organizationUsers;
    const search = userSearchText.toLowerCase();
    return organizationUsers.filter(user => 
      user.name.toLowerCase().includes(search) ||
      user.email.toLowerCase().includes(search) ||
      (user.phoneNumber && user.phoneNumber.toLowerCase().includes(search))
    );
  };

  const generateTrendPDFBlob = async () => {
    // Generate PDF similar to handleDownloadTrendPDF but return blob instead of downloading
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Try to load logo
    let logoImage = null;
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = process.env.PUBLIC_URL + '/Logo-svg copy.png';
      });
      logoImage = img;
    } catch (err) {
      console.warn('Could not load logo for PDF');
    }

    // Header
    let headerY = 15;
    if (logoImage) {
      const maxLogoWidth = 40;
      const maxLogoHeight = 15;
      const logoAspect = logoImage.width / logoImage.height;
      let logoW = maxLogoWidth;
      let logoH = logoW / logoAspect;
      if (logoH > maxLogoHeight) {
        logoH = maxLogoHeight;
        logoW = logoH * logoAspect;
      }
      doc.addImage(logoImage, 'PNG', 20, headerY - 5, logoW, logoH);
    }

    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text('Monthly Fuel Trend Analysis Report', pageWidth - 20, headerY, { align: 'right' });

    const userData = getUserData();
    const orgName = userData?.organisation?.name || 'Organization';
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(orgName, pageWidth - 20, headerY + 6, { align: 'right' });

    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${moment().format('DD MMM YYYY HH:mm')}`, pageWidth - 20, headerY + 11, { align: 'right' });

    let yPosition = headerY + 20;

    // Active filters
    const trendPdfActiveFilters = getActiveFilters();
    if (trendPdfActiveFilters.length > 0) {
      doc.setFontSize(9);
      doc.setTextColor(80);
      const filterText = 'Filters: ' + trendPdfActiveFilters.map(f => `${f.label}: ${f.value}`).join(' | ');
      doc.text(filterText, 20, yPosition);
      yPosition += 8;
    }

    // Month info
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text(`Report Period: ${selectedMonth.format('MMMM YYYY')}`, 20, yPosition);
    yPosition += 10;

    // Summary stats
    const totalAssets = filteredData.length;
    const criticalHighCount = filteredData.filter(d => 
      d.severity && (d.severity.level === 'CRITICAL' || d.severity.level === 'HIGH')
    ).length;

    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text(`Total Assets: ${totalAssets}`, 20, yPosition);
    doc.text(`Critical/High Severity: ${criticalHighCount}`, 80, yPosition);
    yPosition += 10;

    // Simplified table for email PDF
    const tableData = filteredData.slice(0, 50).map((item, idx) => [
      idx + 1,
      item.severity?.level || '-',
      item.assetCode || '-',
      item.assetType || '-',
      item.location || '-',
      item.totalRefills || 0,
      item.totalFuelAdded?.toFixed(1) || '0',
      item.avgConsumptionRate?.toFixed(2) || '-',
      item.standardRate?.toFixed(2) || '-',
      item.variance !== null && item.variance !== undefined ? item.variance.toFixed(1) + '%' : '-'
    ]);

    autoTable(doc, {
      head: [['#', 'Severity', 'Asset Code', 'Type', 'Location', 'Refills', 'Fuel Added', 'Avg Rate', 'Std Rate', 'Variance']],
      body: tableData,
      startY: yPosition,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [66, 66, 66], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 20 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 15 },
        6: { cellWidth: 20 },
        7: { cellWidth: 20 },
        8: { cellWidth: 20 },
        9: { cellWidth: 20 }
      }
    });

    drawSeverityGuidePdfAtEnd(doc, pageWidth, pageHeight);

    // Return as blob
    return doc.output('blob');
  };

  // Generate plain text email body for mailto
  const generatePlainTextEmailBody = () => {
    const activeFiltersList = getActiveFilters();
    const filtersDescription = formatFiltersForEmail(activeFiltersList);
    const totalAssets = filteredData.length;
    const totalFuelConsumed = aggregatedTotals.totalFuelConsumed || 0;
    const totalRefills = aggregatedTotals.totalRefills || 0;
    const criticalHighCount = filteredData.filter(d => 
      d.severity && (d.severity.level === 'CRITICAL' || d.severity.level === 'HIGH')
    ).length;

    let body = `Dear Team,

Please find attached the Fuel Trend Analysis Report for ${selectedMonth.format('MMMM YYYY')}.

REPORT SUMMARY
==============
• Report Period: ${selectedMonth.format('MMMM YYYY')}
• Total Assets Analysed: ${totalAssets}
• Total Fuel Consumed: ${totalFuelConsumed.toLocaleString(undefined, { maximumFractionDigits: 1 })} L
• Total Refills: ${totalRefills}
• Critical/High Severity Assets: ${criticalHighCount}
`;

    if (filtersDescription) {
      body += `
FILTERS APPLIED
===============
${filtersDescription}
`;
    }

    body += `
KEY INSIGHTS
============
• Start with Critical and High severity assets for fastest operational correction
• Track month-on-month improvement against consistent benchmarks
• Prioritise interventions based on highest variance and impact

Please review the attached PDF for detailed asset-wise analysis.

Note: Please attach the Trend Analysis PDF to this email before sending.

Warm regards,
AssetPro Reports
`;

    return body;
  };

  // Open mailto link with template email
  const openMailtoWithTemplate = () => {
    if (selectedUsers.length === 0) {
      message.warning('Please select at least one recipient');
      return;
    }

    // Get recipient emails
    const recipientEmails = selectedUsers.map(userId => {
      const user = organizationUsers.find(u => u._id === userId);
      return user?.email;
    }).filter(Boolean);

    // Generate subject and body
    const activeFiltersList = getActiveFilters();
    const filtersDescription = formatFiltersForEmail(activeFiltersList);
    const subject = generateEmailSubject(selectedMonth.format('MMMM YYYY'), filtersDescription);
    const body = generatePlainTextEmailBody();

    // Create mailto link
    const mailtoLink = `mailto:${recipientEmails.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // Open in default mail app
    window.location.href = mailtoLink;

    message.success('Opening your email app. Please attach the Trend Analysis PDF before sending.');
    setEmailModalVisible(false);
    setSelectedUsers([]);
  };

  // Download PDF and open mailto (combined action)
  const handleEmailWithDownload = async () => {
    if (selectedUsers.length === 0) {
      message.warning('Please select at least one recipient');
      return;
    }

    setSendingEmail(true);
    
    try {
      // First download the PDF
      await handleDownloadTrendPDF();
      
      // Then open mailto
      setTimeout(() => {
        openMailtoWithTemplate();
        setSendingEmail(false);
      }, 500);
    } catch (error) {
      console.error('Error:', error);
      message.error('Failed to prepare email');
      setSendingEmail(false);
    }
  };

  // Copy HTML email content to clipboard
  const copyHtmlToClipboard = async () => {
    const activeFiltersList = getActiveFilters();
    const filtersDescription = formatFiltersForEmail(activeFiltersList);
    const totalAssets = filteredData.length;
    const totalFuelConsumed = aggregatedTotals.totalFuelConsumed || 0;
    const totalRefills = aggregatedTotals.totalRefills || 0;
    const criticalHighCount = filteredData.filter(d => 
      d.severity && (d.severity.level === 'CRITICAL' || d.severity.level === 'HIGH')
    ).length;

    // Generate HTML body (without CID images - use inline base64 or placeholders)
    const htmlContent = generateEmailHTML({
      monthYear: selectedMonth.format('MMMM YYYY'),
      clientName: 'Team', 
      totalAssets: totalAssets,
      fuelConsumedL: totalFuelConsumed.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      fuelConsumedMoM: 'N/A',
      totalRefills: totalRefills,
      totalRefillsMoM: 'N/A',
      criticalHighCount: criticalHighCount,
      filtersDescription: filtersDescription
    });

    try {
      // Try to copy as HTML (rich text)
      if (navigator.clipboard && navigator.clipboard.write) {
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const clipboardItem = new ClipboardItem({ 'text/html': blob });
        await navigator.clipboard.write([clipboardItem]);
        message.success('HTML email copied! Paste into your email client (Ctrl+V / Cmd+V)');
      } else {
        // Fallback to plain text copy
        await navigator.clipboard.writeText(htmlContent);
        message.success('HTML code copied! Paste into your email client\'s HTML editor');
      }
    } catch (error) {
      console.error('Copy failed:', error);
      // Final fallback - select and copy
      const textarea = document.createElement('textarea');
      textarea.value = htmlContent;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      message.success('HTML code copied to clipboard');
    }
  };

  // Get email subject for display
  const getEmailSubject = () => {
    const activeFiltersList = getActiveFilters();
    const filtersDescription = formatFiltersForEmail(activeFiltersList);
    return generateEmailSubject(selectedMonth.format('MMMM YYYY'), filtersDescription);
  };

  // Get recipient emails as string
  const getRecipientEmails = () => {
    return selectedUsers.map(userId => {
      const user = organizationUsers.find(u => u._id === userId);
      return user?.email;
    }).filter(Boolean).join(', ');
  };

  const sendEmailReport = async () => {
    if (selectedUsers.length === 0) {
      message.warning('Please select at least one recipient');
      return;
    }

    if (!emailServerOnline) {
      message.error('Email server is not available. Please ensure the email server is running.');
      return;
    }

    setSendingEmail(true);
    message.loading({ content: 'Preparing and sending emails...', key: 'sendEmail', duration: 0 });

    try {
      // Get recipients
      const recipients = selectedUsers.map(userId => {
        const user = organizationUsers.find(u => u._id === userId);
        return { email: user.email, name: user.name };
      });

      // Get active filters for email
      const activeFiltersList = getActiveFilters();
      const filtersDescription = formatFiltersForEmail(activeFiltersList);

      // Generate subject
      const subject = generateEmailSubject(selectedMonth.format('MMMM YYYY'), filtersDescription);

      // Calculate summary stats for email
      const totalAssets = filteredData.length;
      const totalFuelConsumed = aggregatedTotals.totalFuelConsumed || 0;
      const totalRefills = aggregatedTotals.totalRefills || 0;
      const criticalHighCount = filteredData.filter(d => 
        d.severity && (d.severity.level === 'CRITICAL' || d.severity.level === 'HIGH')
      ).length;

      // Generate HTML body
      const htmlBody = generateEmailHTML({
        monthYear: selectedMonth.format('MMMM YYYY'),
        clientName: '{{CLIENT_NAME}}', // Will be replaced per-recipient by server
        totalAssets: totalAssets,
        fuelConsumedL: totalFuelConsumed.toLocaleString(undefined, { maximumFractionDigits: 1 }),
        fuelConsumedMoM: 'N/A', // Would need previous month data to calculate
        totalRefills: totalRefills,
        totalRefillsMoM: 'N/A', // Would need previous month data to calculate
        criticalHighCount: criticalHighCount,
        filtersDescription: filtersDescription
      });

      // Generate PDF blob
      const pdfBlob = await generateTrendPDFBlob();
      const pdfBase64 = await pdfBlobToBase64(pdfBlob);

      // Load images as base64
      const logoBase64 = await imageToBase64(process.env.PUBLIC_URL + '/Logo-svg copy.png');
      const bannerBase64 = await imageToBase64(process.env.PUBLIC_URL + '/monthy_fuel_trend_analysis.png');

      // Send email
      const result = await sendEmailWithAttachment({
        recipients,
        subject,
        htmlBody,
        pdfBase64,
        pdfFilename: `Fuel_Trend_Analysis_${selectedMonth.format('YYYY-MM')}.pdf`,
        logoBase64,
        bannerBase64
      });

      message.destroy('sendEmail');

      if (result.success) {
        message.success(result.message || `Successfully sent ${recipients.length} email(s)`);
        setEmailModalVisible(false);
        setSelectedUsers([]);
      } else {
        if (result.errors && result.errors.length > 0) {
          message.warning(`Sent ${result.results?.length || 0} emails, ${result.errors.length} failed`);
        } else {
          message.error(result.error || 'Failed to send emails');
        }
      }
    } catch (error) {
      message.destroy('sendEmail');
      console.error('Error sending email report:', error);
      message.error('Failed to send email report: ' + (error.message || 'Unknown error'));
    } finally {
      setSendingEmail(false);
    }
  };

  const columns = [
    {
      title: 'SN',
      dataIndex: 'snNo',
      key: 'snNo',
      width: 60,
      fixed: 'left',
      sorter: (a, b) => a.snNo - b.snNo
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      fixed: 'left',
      sorter: (a, b) => (a.severity?.order || 999) - (b.severity?.order || 999),
      render: (severity) => {
        if (!severity || !severity.level) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const { level, bgColor, color, textColor, isBetter } = severity;
        return (
          <Tag 
            style={{ 
              backgroundColor: bgColor, 
              color: textColor || color, 
              border: `1px solid ${color}`,
              fontWeight: 600,
              fontSize: '11px',
              padding: '2px 8px',
              margin: 0
            }}
          >
            {level}{isBetter ? ' ↓' : level !== 'OK' ? ' ↑' : ''}
          </Tag>
        );
      }
    },
    {
      title: 'Asset Code',
      dataIndex: 'assetCode',
      key: 'assetCode',
      width: 160,
      fixed: 'left',
      sorter: (a, b) => (a.assetCode || '').localeCompare(b.assetCode || ''),
      render: (value, record) => {
        // Asterisk marks assets where a meter change occurred during the month; the first
        // full-tank reading after the change is omitted from the averaged consumption.
        // Traceable to the footnote below the table.
        const marker = record.hasMeterChange ? (
          <Tooltip title="Meter changed during the month. The first full-tank reading after the change is omitted from the month's averaged consumption (see note below the table).">
            <span style={{ color: '#fa8c16', fontWeight: 700, marginLeft: 4, cursor: 'help' }}>*</span>
          </Tooltip>
        ) : null;
        return (
          <div style={{ lineHeight: '1.4' }}>
            <div style={{ fontWeight: 500 }}>{value}{marker}</div>
            {(record.altname || record.ownercode) && (
              <div style={{ fontSize: '11px', color: '#666' }}>
                {[record.altname, record.ownercode].filter(Boolean).join(' / ')}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Asset Type',
      dataIndex: 'assetType',
      key: 'assetType',
      width: 120,
      sorter: (a, b) => (a.assetType || '').localeCompare(b.assetType || '')
    },
    {
      title: 'Asset Sub Type',
      dataIndex: 'assetSubType',
      key: 'assetSubType',
      width: 140,
      sorter: (a, b) => (a.assetSubType || '').localeCompare(b.assetSubType || '')
    },
    {
      title: 'Asset Model',
      dataIndex: 'assetModel',
      key: 'assetModel',
      width: 150,
      sorter: (a, b) => (a.assetModel || '').localeCompare(b.assetModel || '')
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 120,
      sorter: (a, b) => (a.location || '').localeCompare(b.location || '')
    },
    {
      title: 'Sub Location',
      dataIndex: 'subLocation',
      key: 'subLocation',
      width: 120,
      sorter: (a, b) => (a.subLocation || '').localeCompare(b.subLocation || '')
    },
    {
      title: 'Total Refills',
      dataIndex: 'totalRefills',
      key: 'totalRefills',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.totalRefills - b.totalRefills,
      render: (value, record) => {
        const displayVal = value != null ? Math.round(value) : 0;
        if (!value || !record.allFuelEntries || record.allFuelEntries.length === 0) {
          return displayVal;
        }
        return (
          <span
            style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
            onClick={() => {
              setRefuelBreakdownData(record.allFuelEntries);
              setRefuelBreakdownTitle(`Fuel Entries - ${record.assetCode} at ${record.location}`);
              setRefuelBreakdownModalVisible(true);
            }}
            title="Click to view all fuel entries"
          >
            {displayVal}
          </span>
        );
      }
    },
    {
      title: 'Total Fuel Added (L)',
      dataIndex: 'totalFuelAdded',
      key: 'totalFuelAdded',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.totalFuelAdded - b.totalFuelAdded,
      render: (value) => value.toFixed(2)
    },
    {
      title: 'Full Tank Events',
      dataIndex: 'fullTankCount',
      key: 'fullTankCount',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.fullTankCount - b.fullTankCount,
      render: (value, record) => (
        <span 
          style={{ cursor: value > 0 ? 'pointer' : 'default', color: value > 0 ? '#1890ff' : 'inherit', textDecoration: value > 0 ? 'underline' : 'none' }}
          onClick={() => value > 0 && handleViewDetails(record)}
          title={value > 0 ? 'Click to view full tank event details' : ''}
        >
          {value}
        </span>
      )
    },
    {
      title: 'Total Meter Run',
      dataIndex: 'totalMeterRun',
      key: 'totalMeterRun',
      width: 130,
      align: 'right',
      sorter: (a, b) => a.totalMeterRun - b.totalMeterRun,
      render: (value, record) => value > 0 ? `${value.toFixed(2)} ${record.meterName !== '-' ? record.meterName : ''}` : '-'
    },
    {
      title: 'Meter Run (after meter change)',
      dataIndex: 'adjustedMeterRun',
      key: 'adjustedMeterRun',
      width: 160,
      align: 'right',
      sorter: (a, b) => (a.adjustedMeterRun || 0) - (b.adjustedMeterRun || 0),
      // Meter run used for averaging = gross minus the meter-change first-reading cycle(s).
      // Equals the gross figure when no meter change occurred.
      render: (value, record) => {
        if (!(value > 0)) return '-';
        const meterStr = record.meterName !== '-' ? record.meterName : '';
        return record.hasMeterChange
          ? <span style={{ color: '#fa8c16' }}>{value.toFixed(2)} {meterStr}</span>
          : `${value.toFixed(2)} ${meterStr}`;
      }
    },
    {
      title: 'Total Fuel Consumed (L)',
      dataIndex: 'totalFuelConsumed',
      key: 'totalFuelConsumed',
      width: 160,
      align: 'right',
      sorter: (a, b) => a.totalFuelConsumed - b.totalFuelConsumed,
      render: (value) => value > 0 ? value.toFixed(2) : '-'
    },
    {
      title: 'Fuel Consumed after meter change (L)',
      dataIndex: 'adjustedFuelConsumed',
      key: 'adjustedFuelConsumed',
      width: 200,
      align: 'right',
      sorter: (a, b) => (a.adjustedFuelConsumed || 0) - (b.adjustedFuelConsumed || 0),
      // Fuel consumed used for averaging = gross minus the meter-change first-reading cycle(s).
      render: (value, record) => {
        if (!(value > 0)) return '-';
        return record.hasMeterChange
          ? <span style={{ color: '#fa8c16' }}>{value.toFixed(2)}</span>
          : value.toFixed(2);
      }
    },
    {
      title: 'Avg Consumption Rate',
      dataIndex: 'avgConsumptionRate',
      key: 'avgConsumptionRate',
      width: 170,
      align: 'right',
      sorter: (a, b) => (a.avgConsumptionRate || 0) - (b.avgConsumptionRate || 0),
      // Averaged on the AFTER-meter-change basis. When a meter change leaves no complete
      // cycle, show no rate + a warning rather than a distorted figure.
      render: (value, record) => {
        if (!value) {
          if (record.meterChangeNote) {
            return (
              <Tooltip title={record.meterChangeNote}>
                <Tag color="gold">No complete cycle</Tag>
              </Tooltip>
            );
          }
          return '-';
        }
        return `${value.toFixed(2)} ${record.consumptionRateUnit}`;
      }
    },
    {
      title: 'Standard Rate',
      dataIndex: 'standardRate',
      key: 'standardRate',
      width: 140,
      align: 'right',
      sorter: (a, b) => (a.standardRate || 0) - (b.standardRate || 0),
      render: (value, record) => {
        if (!value) return '-';
        return `${value.toFixed(2)} ${record.standardRateUnit}`;
      }
    },
    {
      title: 'Variance %',
      dataIndex: 'variancePercent',
      key: 'variancePercent',
      width: 120,
      align: 'right',
      sorter: (a, b) => (a.variancePercent || 0) - (b.variancePercent || 0),
      render: (value, record) => {
        if (value === null || value === undefined) {
          if (record.varianceNote) {
            return <Tag color="gold">{record.varianceNote}</Tag>;
          }
          return '-';
        }
        const color = value >= 0 ? '#ff4d4f' : '#52c41a';
        return <Tag color={color}>{value.toFixed(2)}%</Tag>;
      }
    },
    {
      title: 'Total Fuel Loss (L)',
      dataIndex: 'totalFuelLoss',
      key: 'totalFuelLoss',
      width: 140,
      align: 'right',
      sorter: (a, b) => (a.totalFuelLoss || 0) - (b.totalFuelLoss || 0),
      render: (value) => {
        if (value === null || value === undefined) return '-';
        const color = value > 0 ? '#ff4d4f' : value < 0 ? '#52c41a' : '#1890ff';
        const sign = value >= 0 ? '+' : '';
        const label = value > 0 ? 'Loss' : value < 0 ? 'Saved' : '';
        return <span style={{ color, fontWeight: value !== 0 ? 'bold' : 'normal' }}>
          {sign}{value.toFixed(2)} {label}
        </span>;
      }
    },
    // IoT-based columns (conditionally included)
    ...(showIoTData ? [
      {
        title: 'IoT Fuel Added (L)',
        dataIndex: 'iotFuelAdded',
        key: 'iotFuelAdded',
        width: 140,
        align: 'right',
        sorter: (a, b) => (a.iotFuelAdded || 0) - (b.iotFuelAdded || 0),
        render: (value, record) => {
          if (!record.hasIoTData || !value) return <span style={{ color: '#999' }}>-</span>;
          const period = record.periodMoment || null;
          return (
            <span
              style={{ cursor: 'pointer', color: '#722ed1', textDecoration: 'underline' }}
              onClick={() => handleIotBreakdownClick(record.assetCode, record.location || record._location, record.subLocation || record._subLocation, 'IoT Fuel Added Breakdown', period)}
              title="Click for daily breakdown"
            >
              {value.toFixed(2)}
            </span>
          );
        }
      },
      {
        title: 'Fuel Added Diff',
        dataIndex: 'fuelAddedDiff',
        key: 'fuelAddedDiff',
        width: 140,
        align: 'right',
        sorter: (a, b) => (a.fuelAddedDiff || 0) - (b.fuelAddedDiff || 0),
        render: (value, record) => {
          if (!record.hasIoTData || value === null || value === undefined) return <span style={{ color: '#999' }}>-</span>;
          const percent = record.fuelAddedDiffPercent;
          const color = value > 0 ? '#52c41a' : value < 0 ? '#ff4d4f' : '#1890ff';
          return (
            <span style={{ color }}>
              {value >= 0 ? '+' : ''}{value.toFixed(2)}
              <br />
              <small>({percent >= 0 ? '+' : ''}{percent?.toFixed(1)}%)</small>
            </span>
          );
        }
      },
      {
        title: 'IoT Fuel Consumed (L)',
        dataIndex: 'iotFuelConsumed',
        key: 'iotFuelConsumed',
        width: 160,
        align: 'right',
        sorter: (a, b) => (a.iotFuelConsumed || 0) - (b.iotFuelConsumed || 0),
        render: (value, record) => {
          if (!record.hasIoTData || !value) return <span style={{ color: '#999' }}>-</span>;
          const period = record.periodMoment || null;
          return (
            <span
              style={{ cursor: 'pointer', color: '#722ed1', textDecoration: 'underline' }}
              onClick={() => handleIotBreakdownClick(record.assetCode, record.location || record._location, record.subLocation || record._subLocation, 'IoT Fuel Consumed Breakdown', period)}
              title="Click for daily breakdown"
            >
              {value.toFixed(2)}
            </span>
          );
        }
      },
      {
        title: 'Consumed Diff',
        dataIndex: 'fuelConsumedDiff',
        key: 'fuelConsumedDiff',
        width: 140,
        align: 'right',
        sorter: (a, b) => (a.fuelConsumedDiff || 0) - (b.fuelConsumedDiff || 0),
        render: (value, record) => {
          if (!record.hasIoTData || value === null || value === undefined) return <span style={{ color: '#999' }}>-</span>;
          const percent = record.fuelConsumedDiffPercent;
          const color = value > 0 ? '#ff4d4f' : value < 0 ? '#52c41a' : '#1890ff';
          return (
            <span style={{ color }}>
              {value >= 0 ? '+' : ''}{value.toFixed(2)}
              <br />
              <small>({percent >= 0 ? '+' : ''}{percent?.toFixed(1)}%)</small>
            </span>
          );
        }
      },
      {
        title: 'IoT Units Run',
        dataIndex: 'iotUnitsRun',
        key: 'iotUnitsRun',
        width: 130,
        align: 'right',
        sorter: (a, b) => (a.iotUnitsRun || 0) - (b.iotUnitsRun || 0),
        render: (value, record) => {
          if (!record.hasIoTData || !value) return <span style={{ color: '#999' }}>-</span>;
          const unit = record.iotUnitLabel || (record.iotUnitOfMeasurement === 'HOURS' ? 'Hrs' : 'KM');
          const period = record.periodMoment || null;
          return (
            <span
              style={{ cursor: 'pointer', color: '#722ed1', textDecoration: 'underline' }}
              onClick={() => handleIotBreakdownClick(record.assetCode, record.location || record._location, record.subLocation || record._subLocation, 'IoT Units Run Breakdown', period)}
              title="Click for daily breakdown"
            >
              {value.toFixed(2)} {unit}
            </span>
          );
        }
      },
      {
        title: 'Units Diff',
        dataIndex: 'unitsRunDiff',
        key: 'unitsRunDiff',
        width: 130,
        align: 'right',
        sorter: (a, b) => (a.unitsRunDiff || 0) - (b.unitsRunDiff || 0),
        render: (value, record) => {
          if (!record.hasIoTData || value === null || value === undefined) return <span style={{ color: '#999' }}>-</span>;
          const percent = record.unitsRunDiffPercent;
          const unit = record.iotUnitLabel || (record.iotUnitOfMeasurement === 'HOURS' ? 'Hrs' : 'KM');
          const color = value > 0 ? '#52c41a' : value < 0 ? '#ff4d4f' : '#1890ff';
          return (
            <span style={{ color }}>
              {value >= 0 ? '+' : ''}{value.toFixed(2)} {unit}
              <br />
              <small>({percent >= 0 ? '+' : ''}{percent?.toFixed(1)}%)</small>
            </span>
          );
        }
      },
      {
        title: 'IoT Consumption Rate',
        dataIndex: 'iotConsumptionRate',
        key: 'iotConsumptionRate',
        width: 160,
        align: 'right',
        sorter: (a, b) => (a.iotConsumptionRate || 0) - (b.iotConsumptionRate || 0),
        render: (value, record) => {
          if (!record.hasIoTData || !value) return <span style={{ color: '#999' }}>-</span>;
          const period = record.periodMoment || null;
          return (
            <span
              style={{ cursor: 'pointer', color: '#722ed1', textDecoration: 'underline' }}
              onClick={() => handleIotBreakdownClick(record.assetCode, record.location || record._location, record.subLocation || record._subLocation, 'IoT Consumption Rate Breakdown', period)}
              title="Click for daily breakdown"
            >
              {value.toFixed(2)} {record.iotConsumptionRateUnit}
            </span>
          );
        }
      },
      {
        title: 'Rate Diff',
        dataIndex: 'consumptionRateDiff',
        key: 'consumptionRateDiff',
        width: 140,
        align: 'right',
        sorter: (a, b) => (a.consumptionRateDiff || 0) - (b.consumptionRateDiff || 0),
        render: (value, record) => {
          if (!record.hasIoTData || value === null || value === undefined) return <span style={{ color: '#999' }}>-</span>;
          const percent = record.consumptionRateDiffPercent;
          // For consumption rate: positive diff means IoT shows higher rate
          // For KM/L: higher is better (more efficient) - green
          // For L/Hr: higher is worse (more fuel) - red
          const isKmBased = record.iotConsumptionRateUnit?.includes('KM');
          const color = isKmBased 
            ? (value > 0 ? '#52c41a' : value < 0 ? '#ff4d4f' : '#1890ff')
            : (value > 0 ? '#ff4d4f' : value < 0 ? '#52c41a' : '#1890ff');
          return (
            <span style={{ color }}>
              {value >= 0 ? '+' : ''}{value.toFixed(2)}
              <br />
              <small>({percent >= 0 ? '+' : ''}{percent?.toFixed(1)}%)</small>
            </span>
          );
        }
      }
    ] : [])
  ];

  // Calculate totals from filtered raw entries (same logic as day-wise breakdown for accuracy)
  const calculateTotalsFromRawEntries = () => {
    // Use IST timezone (UTC+5:30) for date range
    const startOfMonth = selectedMonth.clone().startOf('month').utcOffset('+05:30', true);
    const endOfMonth = selectedMonth.clone().endOf('month').utcOffset('+05:30', true);
    
    // Apply the same filters as the day-wise breakdown
    let filteredEntries = rawFuelEntries;
    
    // Filter by asset code (search across assetCode, altname, ownercode)
    if (selectedAssetCode) {
      const searchTerm = selectedAssetCode.toLowerCase();
      filteredEntries = filteredEntries.filter(entry => {
        let assetCode = null;
        let altname = null;
        let ownercode = null;
        if (entry.asset && typeof entry.asset === 'object') {
          assetCode = entry.asset.assetCode;
          altname = entry.asset.altname || entry.asset.altName;
          ownercode = entry.asset.ownercode || entry.asset.ownerCode;
        }
        if (!assetCode && entry.assetCode) {
          assetCode = entry.assetCode;
        }
        const matchCode = assetCode && assetCode.toLowerCase().includes(searchTerm);
        const matchAltname = altname && altname.toLowerCase().includes(searchTerm);
        const matchOwnercode = ownercode && ownercode.toLowerCase().includes(searchTerm);
        return matchCode || matchAltname || matchOwnercode;
      });
    }

    if (excludedAssetCodes.length > 0) {
      const excludeSet = buildExcludeSet();
      filteredEntries = filteredEntries.filter(entry => !isExcludedRawEntry(entry, excludeSet));
    }

    // Filter by asset type
    if (selectedAssetType) {
      filteredEntries = filteredEntries.filter(entry => {
        const assetType = extractName(entry.assetType);
        return assetType === selectedAssetType;
      });
    }

    // Filter by asset sub type
    if (selectedAssetSubType) {
      filteredEntries = filteredEntries.filter(entry => {
        const assetSubType = extractName(entry.assetSubType);
        return assetSubType === selectedAssetSubType;
      });
    }

    // Filter by asset model
    if (selectedAssetModel) {
      filteredEntries = filteredEntries.filter(entry => {
        const assetModel = extractName(entry.assetModel);
        return assetModel === selectedAssetModel;
      });
    }

    // Filter by location (use same helper as aggregation for consistency)
    if (selectedLocation) {
      filteredEntries = filteredEntries.filter(entry => {
        const location = getLocationFromEntry(entry);
        return location === selectedLocation;
      });
    }

    // Filter by sub location
    if (selectedSubLocation) {
      filteredEntries = filteredEntries.filter(entry => {
        const subLocation = getSubLocationFromEntry(entry);
        return subLocation === selectedSubLocation;
      });
    }

    // Filter by date range
    filteredEntries = filteredEntries.filter(entry => {
      const entryDate = moment(entry.date || entry.datetime);
      return entryDate.isBetween(startOfMonth, endOfMonth, null, '[]');
    });
    
    // Calculate totals
    let totalRefills = filteredEntries.length;
    let totalFuelAdded = filteredEntries.reduce((sum, e) => sum + (e.quantity || 0), 0);
    let fullTankCount = filteredEntries.filter(e => e.isFullTank === true).length;
    
    return { totalRefills, totalFuelAdded, fullTankCount };
  };
  
  // Get totals from raw entries (for refills, fuel added, and full tank events)
  const rawTotals = rawFuelEntries.length > 0 ? calculateTotalsFromRawEntries() : { totalRefills: 0, totalFuelAdded: 0, fullTankCount: 0 };
  
  // Calculate other totals from filtered aggregated data (for consumption-related metrics)
  const aggregatedTotals = filteredData.reduce((acc, item) => {
    acc.totalMeterRun += item.totalMeterRun;
    acc.totalFuelConsumed += item.totalFuelConsumed;
    if (item.totalFuelLoss !== null) {
      acc.totalFuelLoss += item.totalFuelLoss;
    }
    return acc;
  }, {
    totalMeterRun: 0,
    totalFuelConsumed: 0,
    totalFuelLoss: 0
  });
  
  // Combine totals - use raw entries for refills/fuel added (accurate), aggregated for consumption metrics
  const totals = {
    totalRefills: rawTotals.totalRefills,
    totalFuelAdded: rawTotals.totalFuelAdded,
    fullTankCount: rawTotals.fullTankCount,
    totalMeterRun: aggregatedTotals.totalMeterRun,
    totalFuelConsumed: aggregatedTotals.totalFuelConsumed,
    totalFuelLoss: aggregatedTotals.totalFuelLoss
  };

  // Helper function to get active filters for display
  const getActiveFilters = () => {
    const filters = [];
    if (selectedLocation) filters.push({ label: 'Location', value: selectedLocation });
    if (selectedSubLocation) filters.push({ label: 'Sub Location', value: selectedSubLocation });
    if (selectedAssetType) filters.push({ label: 'Asset Type', value: selectedAssetType });
    if (selectedAssetSubType) filters.push({ label: 'Asset Sub Type', value: selectedAssetSubType });
    if (selectedAssetModel) filters.push({ label: 'Asset Model', value: selectedAssetModel });
    if (selectedAssetCode) filters.push({ label: 'Asset Code', value: selectedAssetCode });
    if (excludedAssetCodes.length > 0) filters.push({ label: 'Excluded', value: `${excludedAssetCodes.length} asset(s)` });
    if (hideZeroRefills) filters.push({ label: 'Showing', value: 'Assets with refills only' });
    return filters;
  };

  const activeFilters = getActiveFilters();

  // Per-period totals for comparison mode (apply same filters as ComparisonTable)
  const getFilteredComparisonData = () => {
    let filtered = [...comparisonData];
    if (selectedAssetCode) {
      const searchTerm = selectedAssetCode.toLowerCase();
      filtered = filtered.filter(item => {
        const matchCode = item.assetCode && item.assetCode.toLowerCase().includes(searchTerm);
        const matchAltname = item.altname && item.altname.toLowerCase().includes(searchTerm);
        const matchOwnercode = item.ownercode && item.ownercode.toLowerCase().includes(searchTerm);
        return matchCode || matchAltname || matchOwnercode;
      });
    }
    if (excludedAssetCodes.length > 0) {
      const excludeSet = buildExcludeSet();
      filtered = filtered.filter(item => !isExcludedAggregatedItem(item, excludeSet));
    }
    if (selectedAssetType) {
      filtered = filtered.filter(item => item.assetType === selectedAssetType);
    }
    if (selectedAssetSubType) {
      filtered = filtered.filter(item => item.assetSubType === selectedAssetSubType);
    }
    if (selectedAssetModel) {
      filtered = filtered.filter(item => item.assetModel === selectedAssetModel);
    }
    if (selectedLocation) {
      filtered = filtered.filter(item => item.location === selectedLocation);
    }
    if (selectedSubLocation) {
      filtered = filtered.filter(item => item.subLocation === selectedSubLocation);
    }
    return filtered;
  };

  const calculateTotalsForPeriod = (period, entries) => {
    // Use IST timezone (UTC+5:30) for date range
    const startOfMonth = period.clone().startOf('month').utcOffset('+05:30', true);
    const endOfMonth = period.clone().endOf('month').utcOffset('+05:30', true);
    let filteredEntries = entries || [];
    
    if (selectedAssetCode) {
      const searchTerm = selectedAssetCode.toLowerCase();
      filteredEntries = filteredEntries.filter(entry => {
        let assetCode = null;
        let altname = null;
        let ownercode = null;
        if (entry.asset && typeof entry.asset === 'object') {
          assetCode = entry.asset.assetCode;
          altname = entry.asset.altname || entry.asset.altName;
          ownercode = entry.asset.ownercode || entry.asset.ownerCode;
        }
        if (!assetCode && entry.assetCode) assetCode = entry.assetCode;
        const matchCode = assetCode && assetCode.toLowerCase().includes(searchTerm);
        const matchAltname = altname && altname.toLowerCase().includes(searchTerm);
        const matchOwnercode = ownercode && ownercode.toLowerCase().includes(searchTerm);
        return matchCode || matchAltname || matchOwnercode;
      });
    }
    if (excludedAssetCodes.length > 0) {
      const excludeSet = buildExcludeSet();
      filteredEntries = filteredEntries.filter(entry => !isExcludedRawEntry(entry, excludeSet));
    }
    if (selectedAssetType) {
      filteredEntries = filteredEntries.filter(entry => extractName(entry.assetType) === selectedAssetType);
    }
    if (selectedAssetSubType) {
      filteredEntries = filteredEntries.filter(entry => extractName(entry.assetSubType) === selectedAssetSubType);
    }
    if (selectedAssetModel) {
      filteredEntries = filteredEntries.filter(entry => extractName(entry.assetModel) === selectedAssetModel);
    }
    if (selectedLocation) {
      filteredEntries = filteredEntries.filter(entry => getLocationFromEntry(entry) === selectedLocation);
    }
    if (selectedSubLocation) {
      filteredEntries = filteredEntries.filter(entry => getSubLocationFromEntry(entry) === selectedSubLocation);
    }
    filteredEntries = filteredEntries.filter(entry => {
      const entryDate = moment(entry.date || entry.datetime);
      return entryDate.isBetween(startOfMonth, endOfMonth, null, '[]');
    });
    
    return {
      totalRefills: filteredEntries.length,
      totalFuelAdded: filteredEntries.reduce((sum, e) => sum + (e.quantity || 0), 0),
      fullTankCount: filteredEntries.filter(e => e.isFullTank === true).length
    };
  };

  const comparisonTotalsPerPeriod = comparisonMode && selectedPeriods.length > 0 ? selectedPeriods.map(period => {
    const periodKey = period.format('YYYY-MM');
    const periodLabel = period.format('MMM YYYY');
    const rawEntries = rawFuelEntriesPerPeriod[periodKey] || [];
    const rawTotalsForPeriod = calculateTotalsForPeriod(period, rawEntries);
    
    const filteredComparison = getFilteredComparisonData();
    let totalFuelConsumed = 0;
    let totalFuelLoss = 0;
    let assetCount = 0;
    filteredComparison.forEach(item => {
      const periodData = item.periods?.[periodKey];
      if (periodData) {
        assetCount += 1;
        totalFuelConsumed += periodData.totalFuelConsumed || 0;
        if (periodData.totalFuelLoss != null) totalFuelLoss += periodData.totalFuelLoss;
      }
    });
    
    return {
      period,
      periodKey,
      periodLabel,
      totalAssets: assetCount,
      totalRefills: rawTotalsForPeriod.totalRefills,
      totalFuelAdded: rawTotalsForPeriod.totalFuelAdded,
      fullTankCount: rawTotalsForPeriod.fullTankCount,
      totalFuelConsumed,
      totalFuelLoss
    };
  }) : [];

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card
        title={
          <span>
            <Icon type="bar-chart" style={{ marginRight: 8 }} />
            Monthly Full Tank Report {comparisonMode ? '- Comparison' : `- ${selectedMonth.format('MMMM YYYY')}`}
          </span>
        }
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={comparisonMode}
              onChange={setComparisonMode}
              checkedChildren="Compare"
              unCheckedChildren="Single"
              style={{ marginRight: 16 }}
            />
            <Switch
              checked={showIoTData}
              onChange={setShowIoTData}
              checkedChildren="IoT Data"
              unCheckedChildren="No IoT"
              style={{ marginRight: 16 }}
            />
            <Switch
              checked={hideZeroRefills}
              onChange={setHideZeroRefills}
              checkedChildren="Hide 0 Refills"
              unCheckedChildren="Show All"
              style={{ marginRight: 16 }}
            />
            {!comparisonMode ? (
              <DatePicker.MonthPicker
                value={selectedMonth}
                onChange={(date) => {
                  if (date) {
                    setSelectedMonth(date);
                  }
                }}
                format="MMMM YYYY"
                style={{ marginRight: 8 }}
              />
            ) : (
              <Select
                mode="multiple"
                placeholder="Select periods to compare"
                value={selectedPeriods.map(p => p.format('YYYY-MM'))}
                onChange={(values) => {
                  setSelectedPeriods(values.map(v => moment(v, 'YYYY-MM')));
                }}
                style={{ width: 300, marginRight: 8 }}
                allowClear
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const month = moment().subtract(i, 'months');
                  return (
                    <Option key={month.format('YYYY-MM')} value={month.format('YYYY-MM')}>
                      {month.format('MMM YYYY')}
                    </Option>
                  );
                })}
              </Select>
            )}
            <Button
              icon="reload"
              onClick={() => comparisonMode ? loadComparisonData() : loadSummaryData()}
              style={{ marginRight: 8 }}
            >
              Reload
            </Button>
            <Button
              icon="download"
              onClick={() => convertTableToCSV(columns, filteredData, `monthly_full_tank_report_${selectedMonth.format('YYYY-MM')}`)}
              style={{ marginRight: 8 }}
            >
              Download CSV
            </Button>
            <Button
              type="default"
              icon="solution"
              onClick={handleDownloadExecutiveTrendPDF}
              disabled={comparisonMode || loading || filteredData.length === 0}
              title={comparisonMode ? 'Executive Trend Report is only available in single mode' : loading ? 'Please wait for data to load' : filteredData.length === 0 ? 'Load report data first' : 'Download Executive Trend Report'}
              style={{ marginRight: 8 }}
            >
              Executive Trend Report
            </Button>
            <Button
              type="default"
              icon="file-text"
              onClick={handleDownloadAssetWiseDetailedPDF}
              disabled={comparisonMode}
              title={comparisonMode ? 'AssetWise Detailed Report is only available in single mode' : 'Download AssetWise Detailed Report'}
              style={{ marginRight: 8 }}
            >
              AssetWise Detailed Report
            </Button>
            <Button
              type="primary"
              icon="mail"
              onClick={handleEmailReport}
              disabled={comparisonMode}
              title={comparisonMode ? 'Email Report is only available in single mode' : 'Email Trend Analysis Report'}
              style={{ marginLeft: 8, background: '#52c41a', borderColor: '#52c41a' }}
            >
              Email Report
            </Button>
          </div>
        }
      >
        {/* Filters */}
        <Card
          size="small"
          style={{ marginBottom: 16 }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Filters</span>
              <Button
                type="link"
                size="small"
                icon={filtersVisible ? "up" : "down"}
                onClick={() => setFiltersVisible(!filtersVisible)}
                style={{ padding: 0, height: 'auto' }}
              >
                {filtersVisible ? 'Hide' : 'Show'} Filters
              </Button>
            </div>
          }
        >
          {filtersVisible && (
            <>
              <Row gutter={16}>
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Asset Code:</label>
                  <Input
                    placeholder="Search code / altname / owner..."
                    allowClear
                    value={selectedAssetCode || ''}
                    onChange={(e) => setSelectedAssetCode(e.target.value || null)}
                  />
                </Col>
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Asset Type:</label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Types"
                    allowClear
                    value={selectedAssetType}
                    onChange={setSelectedAssetType}
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {assetTypes.map(type => (
                      <Option key={type} value={type}>{type}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Asset Sub Type:</label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Sub Types"
                    allowClear
                    value={selectedAssetSubType}
                    onChange={setSelectedAssetSubType}
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {assetSubTypes.map(subType => (
                      <Option key={subType} value={subType}>{subType}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Asset Model:</label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Models"
                    allowClear
                    value={selectedAssetModel}
                    onChange={setSelectedAssetModel}
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {assetModels.map(model => (
                      <Option key={model} value={model}>{model}</Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Location:</label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Locations"
                    allowClear
                    value={selectedLocation}
                    onChange={setSelectedLocation}
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {locations.map(loc => (
                      <Option key={loc} value={loc}>{loc}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Sub Location:</label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Sub Locations"
                    allowClear
                    value={selectedSubLocation}
                    onChange={setSelectedSubLocation}
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {subLocations.map(subLoc => (
                      <Option key={subLoc} value={subLoc}>{subLoc}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Exclude Asset Codes:</label>
                  <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder="Type code + Enter to exclude (exact match)"
                    value={excludedAssetCodes}
                    onChange={setExcludedAssetCodes}
                    tokenSeparators={[',', ' ']}
                    allowClear
                  />
                </Col>
              </Row>
            </>
          )}
        </Card>

        {/* Active Filters Display */}
        {activeFilters.length > 0 && (filteredData.length > 0 || comparisonData.length > 0) && (
          <div style={{ 
            marginBottom: 16, 
            padding: '12px 16px', 
            background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)', 
            borderRadius: 8,
            border: '1px solid #d6e4ff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ 
                fontWeight: 600, 
                color: '#1890ff', 
                marginRight: 8,
                fontSize: 13
              }}>
                Filtered Report:
              </span>
              {activeFilters.map((filter, index) => (
                <Tag 
                  key={index} 
                  color="blue" 
                  style={{ 
                    margin: 0, 
                    padding: '4px 10px', 
                    fontSize: 12,
                    borderRadius: 4
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{filter.label}:</span> {filter.value}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {/* Summary Statistics - Single mode */}
        {!comparisonMode && filteredData.length > 0 && (
          <Card size="small" style={{ marginBottom: 16 }} title="Summary (Click on any value for day-wise breakdown)">
            <Row gutter={16}>
              <Col span={4}>
                <Statistic title="Total Assets" value={filteredData.length} />
              </Col>
              <Col span={4}>
                <div 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleSummaryClick('refills', 'Day-wise Refills Breakdown')}
                  title="Click for day-wise breakdown"
                >
                  <Statistic 
                    title="Total Refills" 
                    value={Math.round(totals.totalRefills)} 
                    valueStyle={{ color: '#1890ff', textDecoration: 'underline' }}
                  />
                </div>
              </Col>
              <Col span={4}>
                <div 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleSummaryClick('fuelAdded', 'Day-wise Fuel Added Breakdown')}
                  title="Click for day-wise breakdown"
                >
                  <Statistic 
                    title="Fuel Added (L)" 
                    value={totals.totalFuelAdded.toFixed(2)} 
                    valueStyle={{ color: '#1890ff', textDecoration: 'underline' }}
                  />
                </div>
              </Col>
              <Col span={4}>
                <div 
                  style={{ cursor: 'pointer' }} 
                  onClick={() => handleSummaryClick('fullTank', 'Day-wise Full Tank Events Breakdown')}
                  title="Click for day-wise breakdown"
                >
                  <Statistic 
                    title="Full Tank Events" 
                    value={totals.fullTankCount} 
                    valueStyle={{ color: '#1890ff', textDecoration: 'underline' }}
                  />
                </div>
              </Col>
              <Col span={4}>
                <Statistic title="Fuel Consumed (L)" value={totals.totalFuelConsumed.toFixed(2)} />
              </Col>
              <Col span={4}>
                <Statistic 
                  title="Total Fuel Loss (L)" 
                  value={totals.totalFuelLoss.toFixed(2)} 
                  valueStyle={{ color: totals.totalFuelLoss > 0 ? '#ff4d4f' : totals.totalFuelLoss < 0 ? '#52c41a' : '#1890ff' }}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* Summary Statistics - Comparison mode (multiple periods) */}
        {comparisonMode && comparisonData.length > 0 && comparisonTotalsPerPeriod.length > 0 && (
          <Card size="small" style={{ marginBottom: 16 }} title="Summary by Period (Click on any value for day-wise breakdown)">
            <Table
              size="small"
              dataSource={comparisonTotalsPerPeriod}
              rowKey="periodKey"
              pagination={false}
              columns={[
                {
                  title: 'Period',
                  dataIndex: 'periodLabel',
                  key: 'periodLabel',
                  width: 120,
                  render: (text) => <Tag color="blue" style={{ fontWeight: 'bold' }}>{text}</Tag>
                },
                {
                  title: 'Total Assets',
                  dataIndex: 'totalAssets',
                  key: 'totalAssets',
                  width: 110,
                  align: 'right'
                },
                {
                  title: 'Total Refills',
                  dataIndex: 'totalRefills',
                  key: 'totalRefills',
                  width: 110,
                  align: 'right',
                  render: (val, row) => (
                    <span
                      style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                      onClick={() => handleSummaryClickForPeriod('refills', 'Day-wise Refills Breakdown', row.period, row.periodLabel)}
                      title="Click for day-wise breakdown"
                    >
                      {val != null ? Math.round(val) : 0}
                    </span>
                  )
                },
                {
                  title: 'Fuel Added (L)',
                  dataIndex: 'totalFuelAdded',
                  key: 'totalFuelAdded',
                  width: 120,
                  align: 'right',
                  render: (val, row) => (
                    <span
                      style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                      onClick={() => handleSummaryClickForPeriod('fuelAdded', 'Day-wise Fuel Added Breakdown', row.period, row.periodLabel)}
                      title="Click for day-wise breakdown"
                    >
                      {Number(val).toFixed(2)}
                    </span>
                  )
                },
                {
                  title: 'Full Tank Events',
                  dataIndex: 'fullTankCount',
                  key: 'fullTankCount',
                  width: 130,
                  align: 'right',
                  render: (val, row) => (
                    <span
                      style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                      onClick={() => handleSummaryClickForPeriod('fullTank', 'Day-wise Full Tank Events Breakdown', row.period, row.periodLabel)}
                      title="Click for day-wise breakdown"
                    >
                      {val}
                    </span>
                  )
                },
                {
                  title: 'Fuel Consumed (L)',
                  dataIndex: 'totalFuelConsumed',
                  key: 'totalFuelConsumed',
                  width: 140,
                  align: 'right',
                  render: (val) => Number(val).toFixed(2)
                },
                {
                  title: 'Total Fuel Loss (L)',
                  dataIndex: 'totalFuelLoss',
                  key: 'totalFuelLoss',
                  width: 130,
                  align: 'right',
                  render: (val) => (
                    <span style={{ color: val > 0 ? '#ff4d4f' : val < 0 ? '#52c41a' : '#1890ff' }}>
                      {Number(val).toFixed(2)}
                    </span>
                  )
                }
              ]}
            />
          </Card>
        )}

        <Alert
          type="info"
          showIcon
          style={{
            marginBottom: 16,
            border: '1px solid #d6e4ff',
            background: 'linear-gradient(180deg, #f7fbff 0%, #eef6ff 100%)',
            borderRadius: 8
          }}
          message={
            <div style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontWeight: 700, color: '#173b73', fontSize: 13 }}>Severity Logic</span>
              <span style={{ color: '#4f5f7a', fontSize: 12 }}>
                T = asset threshold, D = signed deviation, A = |D|. D &gt; 0 is worse, D &lt; 0 is better, OK when A &lt; T.
              </span>
            </div>
          }
          description={
            <div style={{ display: 'grid', gap: 10, color: '#334155', fontSize: 12, lineHeight: 1.6 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: '#1d4f91', minWidth: 88 }}>Worse bands</span>
                <Tag style={{ margin: 0, background: '#e6f7ff', color: '#1890ff', borderColor: '#91d5ff' }}>LOW: T to &lt; T+10</Tag>
                <Tag style={{ margin: 0, background: '#fffbe6', color: '#faad14', borderColor: '#ffe58f' }}>MEDIUM: T+10 to &lt; T+20</Tag>
                <Tag style={{ margin: 0, background: '#fff2e8', color: '#fa541c', borderColor: '#ffbb96' }}>HIGH: T+20 to &lt; T+30</Tag>
                <Tag style={{ margin: 0, background: '#ff4d4f', color: '#fff', borderColor: '#ff4d4f' }}>CRITICAL: ≥ T+30</Tag>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: '#1d4f91', minWidth: 88 }}>Better bands</span>
                <Tag style={{ margin: 0, background: '#f6ffed', color: '#52c41a', borderColor: '#b7eb8f' }}>LOW: T to &lt; T+20</Tag>
                <Tag style={{ margin: 0, background: '#e6fffb', color: '#13c2c2', borderColor: '#87e8de' }}>MEDIUM: T+20 to &lt; T+30</Tag>
                <Tag style={{ margin: 0, background: '#f9f0ff', color: '#722ed1', borderColor: '#d3adf7' }}>HIGH: T+30 to &lt; T+50</Tag>
                <Tag style={{ margin: 0, background: '#722ed1', color: '#fff', borderColor: '#722ed1' }}>CRITICAL: ≥ T+50</Tag>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <span><strong>Colors</strong>: warm = worse, cool/green-purple = better.</span>
                <span><strong>Sort</strong>: Severity first, then absolute variance, then Sub Location, Location, Model, Sub Type, Type.</span>
              </div>
            </div>
          }
        />

        {/* Data Table */}
        {comparisonMode ? (
          <ComparisonTable
            data={comparisonData}
            selectedPeriods={selectedPeriods}
            loading={loading}
            filters={{
              assetCode: selectedAssetCode,
              excludedAssetCodes: excludedAssetCodes,
              assetType: selectedAssetType,
              assetSubType: selectedAssetSubType,
              assetModel: selectedAssetModel,
              location: selectedLocation,
              subLocation: selectedSubLocation
            }}
            columns={columns}
            selectedMonth={selectedMonth}
            showIoTData={showIoTData}
            hideZeroRefills={hideZeroRefills}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            scroll={{ x: showIoTData ? 4000 : 2200, y: 600 }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} assets`
            }}
            size="small"
          />
        )}

        {/* Meter-change footnote: explains the consumption-averaging exclusion and lists
            the affected assets so the omission is documented, not silent. */}
        {filteredData.some(r => r.hasMeterChange) && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4, fontSize: 12, color: '#874d00' }}>
            <span style={{ color: '#fa8c16', fontWeight: 700 }}>* </span>
            A meter change occurred in this asset during the month. The first full-tank reading
            after the meter change has been omitted from the month's averaged consumption (both
            fuel consumed and meter run), because that reading does not include the running
            hours/distance accumulated while the meter was being changed. Gross totals are shown
            alongside the after-meter-change figures used for the average.
            <div style={{ marginTop: 4 }}>
              <strong>Affected asset(s):</strong>{' '}
              {filteredData.filter(r => r.hasMeterChange).map(r => r.assetCode).join(', ')}
            </div>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal
        title={`Full Tank Event Details - ${selectedRecordDetails?.assetCode || ''}`}
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>Close</Button>
        ]}
        width={1000}
      >
        {selectedRecordDetails && selectedRecordDetails.fullTankEventDetails && (
          <Table
            columns={[
              {
                title: 'Current Full Tank',
                dataIndex: 'date',
                key: 'date',
                width: 150,
                render: (value, record) => (
                  <span
                    style={{ cursor: record.currentEventEntry ? 'pointer' : 'default', color: record.currentEventEntry ? '#1890ff' : 'inherit', textDecoration: record.currentEventEntry ? 'underline' : 'none' }}
                    onClick={() => record.currentEventEntry && handleOpenFullTankEntryDetails(record, 'current')}
                    title={record.currentEventEntry ? 'Click to view fuel entry details' : ''}
                  >
                    {value}
                  </span>
                )
              },
              {
                title: 'Previous Full Tank',
                dataIndex: 'previousDate',
                key: 'previousDate',
                width: 150,
                render: (value, record) => (
                  <span
                    style={{ cursor: record.previousEventEntry ? 'pointer' : 'default', color: record.previousEventEntry ? '#1890ff' : 'inherit', textDecoration: record.previousEventEntry ? 'underline' : 'none' }}
                    onClick={() => record.previousEventEntry && handleOpenFullTankEntryDetails(record, 'previous')}
                    title={record.previousEventEntry ? 'Click to view fuel entry details' : ''}
                  >
                    {value}
                  </span>
                )
              },
              {
                title: 'Fuel Consumed (L)',
                dataIndex: 'fuelConsumed',
                key: 'fuelConsumed',
                width: 130,
                // Click to see the refuel events that contributed to this cycle's fuel consumed.
                render: (v, r) => {
                  const clickable = Array.isArray(r.cycleBreakdown) && r.cycleBreakdown.length > 0;
                  return (
                    <span
                      style={{ cursor: clickable ? 'pointer' : 'default', color: clickable ? '#1890ff' : 'inherit', textDecoration: clickable ? 'underline' : 'none' }}
                      onClick={() => clickable && handleOpenCycleBreakup(r)}
                      title={clickable ? 'Click to view the refuel events behind this calculation' : ''}
                    >
                      {v.toFixed(2)}
                    </span>
                  );
                }
              },
              {
                title: 'Meter Run',
                key: 'meterRun',
                width: 140,
                // Click to see the refuel events (with meter readings) behind this cycle's meter run.
                render: (_, r) => {
                  const calcs = r.meterCalculations && r.meterCalculations.length > 0
                    ? r.meterCalculations
                    : (r.meterRun != null ? [{ meterName: r.meterName, meterRun: r.meterRun }] : []);
                  if (calcs.length === 0) return '-';
                  const clickable = Array.isArray(r.cycleBreakdown) && r.cycleBreakdown.length > 0;
                  return (
                    <span
                      style={{ cursor: clickable ? 'pointer' : 'default', color: clickable ? '#1890ff' : 'inherit', textDecoration: clickable ? 'underline' : 'none' }}
                      onClick={() => clickable && handleOpenCycleBreakup(r)}
                      title={clickable ? 'Click to view the refuel events behind this calculation' : ''}
                    >
                      {calcs.map((c, i) => (
                        <div key={i}>{c.meterRun.toFixed(2)}</div>
                      ))}
                    </span>
                  );
                }
              },
              {
                title: 'Meter',
                key: 'meterName',
                width: 100,
                render: (_, r) => {
                  const calcs = r.meterCalculations && r.meterCalculations.length > 0
                    ? r.meterCalculations
                    : (r.meterName ? [{ meterName: r.meterName }] : []);
                  if (calcs.length === 0) return '-';
                  return (
                    <div>
                      {calcs.map((c, i) => (
                        <div key={i}>{c.meterName}</div>
                      ))}
                    </div>
                  );
                }
              },
              {
                title: 'Consumption Rate',
                key: 'rate',
                width: 170,
                render: (_, r) => {
                  const calcs = r.meterCalculations && r.meterCalculations.length > 0
                    ? r.meterCalculations
                    : (r.rate != null ? [{ rate: r.rate, rateUnit: r.rateUnit }] : []);
                  if (calcs.length === 0) return '-';
                  return (
                    <div>
                      {calcs.map((c, i) => (
                        <div key={i}>{c.rate != null ? `${c.rate.toFixed(2)} ${c.rateUnit}` : '-'}</div>
                      ))}
                    </div>
                  );
                }
              },
              { title: 'Voucher No', dataIndex: 'voucherNo', key: 'voucherNo', width: 120 },
              {
                title: 'Status',
                key: 'meterChangeStatus',
                width: 220,
                // Flags the first reading after a meter change. This cycle is excluded from
                // the month's averaged consumption (its run under-counts the faulty-meter period).
                render: (_, r) => r.isMeterChangeFirstReading
                  ? (
                    <Tooltip title="The meter was replaced before this reading. The running hours/distance during the meter change are not captured, so this cycle is excluded from the month's averaged consumption.">
                      <Tag color="orange">Meter changed — excluded from average</Tag>
                    </Tooltip>
                  )
                  : <span style={{ color: '#999' }}>Included</span>
              }
            ]}
            dataSource={selectedRecordDetails.fullTankEventDetails.map((d, i) => ({ ...d, key: i }))}
            onRow={(r) => (r.isMeterChangeFirstReading ? { style: { backgroundColor: '#fff7e6' } } : {})}
            pagination={false}
            size="small"
            scroll={{ y: 400 }}
          />
        )}
      </Modal>

      {/* Day-wise Breakdown Modal */}
      <Modal
        title={dayWiseModalTitle}
        visible={dayWiseModalVisible}
        onCancel={() => setDayWiseModalVisible(false)}
        footer={[
          <Button 
            key="download" 
            icon="download"
            onClick={() => {
              const csvContent = [
                ['Date', 'Refills', 'Fuel Added (L)', 'Full Tank Events', 'Running Refills', 'Running Fuel Added (L)', 'Running Full Tank'].join(','),
                ...dayWiseData.map(row => [
                  row.dateDisplay,
                  row.totalRefills,
                  row.totalFuelAdded.toFixed(2),
                  row.fullTankEvents,
                  row.runningRefills,
                  row.runningFuelAdded.toFixed(2),
                  row.runningFullTank
                ].join(','))
              ].join('\n');
              
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const link = document.createElement('a');
              link.href = URL.createObjectURL(blob);
              link.download = `day_wise_breakdown_${selectedMonth.format('YYYY-MM')}.csv`;
              link.click();
            }}
          >
            Download CSV
          </Button>,
          <Button key="close" onClick={() => setDayWiseModalVisible(false)}>Close</Button>
        ]}
        width={1100}
      >
        {dayWiseData.length > 0 && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic 
                    title="Days with Activity" 
                    value={dayWiseData.length} 
                    suffix={`/ ${selectedMonth.daysInMonth()}`}
                  />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="Total Refills" 
                    value={dayWiseData.reduce((sum, d) => sum + d.totalRefills, 0)} 
                  />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="Total Fuel Added (L)" 
                    value={dayWiseData.reduce((sum, d) => sum + d.totalFuelAdded, 0).toFixed(2)} 
                  />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="Total Full Tank Events" 
                    value={dayWiseData.reduce((sum, d) => sum + d.fullTankEvents, 0)} 
                  />
                </Col>
              </Row>
            </div>
            <Divider />
            <Table
              columns={[
                { 
                  title: 'Date', 
                  dataIndex: 'dateDisplay', 
                  key: 'dateDisplay', 
                  width: 180,
                  fixed: 'left'
                },
                { 
                  title: 'Refills', 
                  dataIndex: 'totalRefills', 
                  key: 'totalRefills', 
                  width: 100,
                  align: 'right',
                  sorter: (a, b) => a.totalRefills - b.totalRefills
                },
                { 
                  title: 'Fuel Added (L)', 
                  dataIndex: 'totalFuelAdded', 
                  key: 'totalFuelAdded', 
                  width: 130,
                  align: 'right',
                  render: v => v.toFixed(2),
                  sorter: (a, b) => a.totalFuelAdded - b.totalFuelAdded
                },
                { 
                  title: 'Full Tank Events', 
                  dataIndex: 'fullTankEvents', 
                  key: 'fullTankEvents', 
                  width: 130,
                  align: 'right',
                  sorter: (a, b) => a.fullTankEvents - b.fullTankEvents
                },
                { 
                  title: 'Running Refills', 
                  dataIndex: 'runningRefills', 
                  key: 'runningRefills', 
                  width: 130,
                  align: 'right',
                  render: v => <Tag color="blue">{v}</Tag>
                },
                { 
                  title: 'Running Fuel Added (L)', 
                  dataIndex: 'runningFuelAdded', 
                  key: 'runningFuelAdded', 
                  width: 160,
                  align: 'right',
                  render: v => <Tag color="green">{v.toFixed(2)}</Tag>
                },
                { 
                  title: 'Running Full Tank', 
                  dataIndex: 'runningFullTank', 
                  key: 'runningFullTank', 
                  width: 140,
                  align: 'right',
                  render: v => <Tag color="orange">{v}</Tag>
                }
              ]}
              dataSource={dayWiseData.map((d, i) => ({ ...d, key: i }))}
              pagination={false}
              size="small"
              scroll={{ y: 400, x: 900 }}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{dayWiseData.reduce((sum, d) => sum + d.totalRefills, 0)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <strong>{dayWiseData.reduce((sum, d) => sum + d.totalFuelAdded, 0).toFixed(2)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <strong>{dayWiseData.reduce((sum, d) => sum + d.fullTankEvents, 0)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">-</Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">-</Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">-</Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </>
        )}
        {dayWiseData.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <p>No data available for this period.</p>
          </div>
        )}
      </Modal>

      {/* IoT Daily Breakdown Modal */}
      <Modal
        title={<span><Icon type="dashboard" style={{ marginRight: 8, color: '#722ed1' }} />{iotBreakdownTitle}</span>}
        visible={iotBreakdownModalVisible}
        onCancel={() => setIotBreakdownModalVisible(false)}
        footer={[
          <Button 
            key="download" 
            icon="download"
            onClick={() => {
              const csvContent = [
                ['Date', 'Fuel Consumed (L)', 'Possible Refuel (L)', 'Possible Drain (L)', 'Ignition Time (Min)', 'Moving Time (Min)', 'Distance (KM)', 'Consumption Rate', 'Unit'].join(','),
                ...iotBreakdownData.map(row => {
                  // Calculate consumption rate for this row
                  let rate = '-';
                  const unitType = row.unitOfMeasurement;
                  if (unitType === 'HOURS' || unitType === 'KWH') {
                    const hours = (row.ignitionTime || 0) / 60;
                    if (hours > 0 && row.fuelConsumed > 0) {
                      rate = `${(row.fuelConsumed / hours).toFixed(2)} L/Hr`;
                    }
                  } else {
                    if (row.fuelConsumed > 0 && row.distanceTravelled > 0) {
                      rate = `${(row.distanceTravelled / row.fuelConsumed).toFixed(2)} KM/L`;
                    }
                  }
                  return [
                    row.dateFormatted,
                    (row.fuelConsumed || 0).toFixed(2),
                    (row.possibleRefuel || 0).toFixed(2),
                    (row.possibleDrain || 0).toFixed(2),
                    (row.ignitionTime || 0).toFixed(2),
                    (row.movingTime || 0).toFixed(2),
                    (row.distanceTravelled || 0).toFixed(2),
                    rate,
                    row.unitOfMeasurement || ''
                  ].join(',');
                })
              ].join('\n');
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `iot_breakdown_${iotBreakdownData[0]?.assetCode || 'asset'}.csv`;
              link.click();
              URL.revokeObjectURL(url);
            }}
          >
            Download CSV
          </Button>,
          <Button key="close" onClick={() => setIotBreakdownModalVisible(false)}>Close</Button>
        ]}
        width={1250}
      >
        {iotBreakdownData.length > 0 && (() => {
          // Pre-calculate totals for summary statistics
          const totalFuelConsumed = iotBreakdownData.reduce((sum, d) => sum + (d.fuelConsumed || 0), 0);
          const totalPossibleRefuel = iotBreakdownData.reduce((sum, d) => sum + (d.possibleRefuel || 0), 0);
          const totalIgnitionTime = iotBreakdownData.reduce((sum, d) => sum + (d.ignitionTime || 0), 0);
          const totalDistance = iotBreakdownData.reduce((sum, d) => sum + (d.distanceTravelled || 0), 0);
          const unitType = iotBreakdownData[0]?.unitOfMeasurement;
          
          // Calculate consumption rate from TOTALS (not average of daily rates)
          let consumptionRateValue = '-';
          if (unitType === 'HOURS' || unitType === 'KWH') {
            const totalHours = totalIgnitionTime / 60;
            if (totalHours > 0 && totalFuelConsumed > 0) {
              consumptionRateValue = `${(totalFuelConsumed / totalHours).toFixed(2)} L/Hr`;
            }
          } else {
            if (totalFuelConsumed > 0 && totalDistance > 0) {
              consumptionRateValue = `${(totalDistance / totalFuelConsumed).toFixed(2)} KM/L`;
            }
          }
          
          return (
          <>
            <div style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={4}>
                  <Statistic 
                    title="Days with Data" 
                    value={iotBreakdownData.length} 
                  />
                </Col>
                <Col span={4}>
                  <Statistic 
                    title="Total Fuel Consumed (L)" 
                    value={totalFuelConsumed.toFixed(2)} 
                  />
                </Col>
                <Col span={4}>
                  <Statistic 
                    title="Total Possible Refuel (L)" 
                    value={totalPossibleRefuel.toFixed(2)} 
                  />
                </Col>
                <Col span={4}>
                  <Statistic 
                    title="Total Ignition Time" 
                    value={`${(totalIgnitionTime / 60).toFixed(2)} Hrs`}
                  />
                </Col>
                <Col span={4}>
                  <Statistic 
                    title="Total Distance (KM)" 
                    value={totalDistance.toFixed(2)} 
                  />
                </Col>
                <Col span={4}>
                  <Statistic 
                    title="Consumption Rate" 
                    value={consumptionRateValue}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
            </div>
            <Divider />
            <Table
              columns={[
                { 
                  title: 'Date', 
                  dataIndex: 'dateFormatted', 
                  key: 'dateFormatted', 
                  width: 120,
                  fixed: 'left'
                },
                { 
                  title: 'Fuel Consumed (L)', 
                  dataIndex: 'fuelConsumed', 
                  key: 'fuelConsumed', 
                  width: 140,
                  align: 'right',
                  render: v => (v || 0).toFixed(2),
                  sorter: (a, b) => (a.fuelConsumed || 0) - (b.fuelConsumed || 0)
                },
                { 
                  title: 'Possible Refuel (L)', 
                  dataIndex: 'possibleRefuel', 
                  key: 'possibleRefuel', 
                  width: 140,
                  align: 'right',
                  render: v => (v || 0).toFixed(2),
                  sorter: (a, b) => (a.possibleRefuel || 0) - (b.possibleRefuel || 0)
                },
                { 
                  title: 'Possible Drain (L)', 
                  dataIndex: 'possibleDrain', 
                  key: 'possibleDrain', 
                  width: 130,
                  align: 'right',
                  render: v => (v || 0).toFixed(2),
                  sorter: (a, b) => (a.possibleDrain || 0) - (b.possibleDrain || 0)
                },
                { 
                  title: 'Ignition Time (Min)', 
                  dataIndex: 'ignitionTime', 
                  key: 'ignitionTime', 
                  width: 140,
                  align: 'right',
                  render: v => (v || 0).toFixed(2),
                  sorter: (a, b) => (a.ignitionTime || 0) - (b.ignitionTime || 0)
                },
                { 
                  title: 'Moving Time (Min)', 
                  dataIndex: 'movingTime', 
                  key: 'movingTime', 
                  width: 140,
                  align: 'right',
                  render: v => (v || 0).toFixed(2),
                  sorter: (a, b) => (a.movingTime || 0) - (b.movingTime || 0)
                },
                { 
                  title: 'Distance (KM)', 
                  dataIndex: 'distanceTravelled', 
                  key: 'distanceTravelled', 
                  width: 120,
                  align: 'right',
                  render: v => (v || 0).toFixed(2),
                  sorter: (a, b) => (a.distanceTravelled || 0) - (b.distanceTravelled || 0)
                },
                { 
                  title: 'Consumption Rate', 
                  key: 'consumptionRate', 
                  width: 140,
                  align: 'right',
                  render: (_, record) => {
                    const unitType = record.unitOfMeasurement;
                    if (unitType === 'HOURS' || unitType === 'KWH') {
                      // L/Hr calculation
                      const hours = (record.ignitionTime || 0) / 60;
                      if (hours > 0 && record.fuelConsumed > 0) {
                        return `${(record.fuelConsumed / hours).toFixed(2)} L/Hr`;
                      }
                    } else {
                      // KM/L calculation
                      if (record.fuelConsumed > 0 && record.distanceTravelled > 0) {
                        return `${(record.distanceTravelled / record.fuelConsumed).toFixed(2)} KM/L`;
                      }
                    }
                    return '-';
                  }
                },
                { 
                  title: 'Unit', 
                  dataIndex: 'unitOfMeasurement', 
                  key: 'unitOfMeasurement', 
                  width: 80,
                  align: 'center'
                }
              ]}
              dataSource={iotBreakdownData.map((d, i) => ({ ...d, key: i }))}
              pagination={false}
              size="small"
              scroll={{ y: 400, x: 1100 }}
              summary={() => {
                // Calculate totals
                const totalFuelConsumed = iotBreakdownData.reduce((sum, d) => sum + (d.fuelConsumed || 0), 0);
                const totalPossibleRefuel = iotBreakdownData.reduce((sum, d) => sum + (d.possibleRefuel || 0), 0);
                const totalPossibleDrain = iotBreakdownData.reduce((sum, d) => sum + (d.possibleDrain || 0), 0);
                const totalIgnitionTime = iotBreakdownData.reduce((sum, d) => sum + (d.ignitionTime || 0), 0);
                const totalMovingTime = iotBreakdownData.reduce((sum, d) => sum + (d.movingTime || 0), 0);
                const totalDistance = iotBreakdownData.reduce((sum, d) => sum + (d.distanceTravelled || 0), 0);
                
                // Calculate consumption rate from TOTALS (not average of daily rates)
                let totalConsumptionRate = '-';
                const unitType = iotBreakdownData[0]?.unitOfMeasurement;
                if (unitType === 'HOURS' || unitType === 'KWH') {
                  const totalHours = totalIgnitionTime / 60;
                  if (totalHours > 0 && totalFuelConsumed > 0) {
                    totalConsumptionRate = `${(totalFuelConsumed / totalHours).toFixed(2)} L/Hr`;
                  }
                } else {
                  if (totalFuelConsumed > 0 && totalDistance > 0) {
                    totalConsumptionRate = `${(totalDistance / totalFuelConsumed).toFixed(2)} KM/L`;
                  }
                }
                
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <strong>{totalFuelConsumed.toFixed(2)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong>{totalPossibleRefuel.toFixed(2)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <strong>{totalPossibleDrain.toFixed(2)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <strong>{totalIgnitionTime.toFixed(2)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        <strong>{totalMovingTime.toFixed(2)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">
                        <strong>{totalDistance.toFixed(2)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right">
                        <strong>{totalConsumptionRate}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="center">-</Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </>
          );
        })()}
        {iotBreakdownData.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <p>No IoT data available for this asset/period.</p>
          </div>
        )}
      </Modal>

      {/* Refuel Entries Breakdown Modal */}
      <Modal
        title={<span><Icon type="container" style={{ marginRight: 8, color: '#1890ff' }} />{refuelBreakdownTitle}</span>}
        visible={refuelBreakdownModalVisible}
        onCancel={() => setRefuelBreakdownModalVisible(false)}
        footer={[
          <Button 
            key="download" 
            icon="download" 
            onClick={() => {
              const csvContent = [
                ['Date', 'Voucher No', 'Quantity (L)', 'Full Tank', 'Location', 'Sub Location', 'Fuel Source', 'Meter Reading'].join(','),
                ...refuelBreakdownData.map(row => [
                  row.date,
                  row.voucherNo,
                  (row.quantity || 0).toFixed(2),
                  row.isFullTank ? 'Yes' : 'No',
                  row.location,
                  row.subLocation,
                  row.fuelSource,
                  `"${(row.meterReadingStr || '-').replace(/"/g, '""')}"`
                ].join(','))
              ].join('\n');
              
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const link = document.createElement('a');
              link.href = URL.createObjectURL(blob);
              link.download = `fuel_entries_breakdown_${refuelBreakdownTitle.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
              link.click();
              message.success('CSV downloaded');
            }}
          >
            Download CSV
          </Button>,
          <Button key="close" onClick={() => setRefuelBreakdownModalVisible(false)}>
            Close
          </Button>
        ]}
        width={1100}
      >
        {refuelBreakdownData.length > 0 && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Statistic 
                  title="Total Entries" 
                  value={refuelBreakdownData.length} 
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Total Fuel Added (L)" 
                  value={refuelBreakdownData.reduce((sum, d) => sum + (d.quantity || 0), 0).toFixed(2)} 
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Full Tank Events" 
                  value={refuelBreakdownData.filter(d => d.isFullTank).length} 
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Non-Full Tank Events" 
                  value={refuelBreakdownData.filter(d => !d.isFullTank).length} 
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
            </Row>
            <Divider />
            <Table
              columns={[
                { 
                  title: 'Date', 
                  dataIndex: 'date', 
                  key: 'date', 
                  width: 150,
                  sorter: (a, b) => a.date.localeCompare(b.date),
                  defaultSortOrder: 'ascend'
                },
                { 
                  title: 'Voucher No', 
                  dataIndex: 'voucherNo', 
                  key: 'voucherNo', 
                  width: 120
                },
                { 
                  title: 'Quantity (L)', 
                  dataIndex: 'quantity', 
                  key: 'quantity', 
                  width: 120,
                  align: 'right',
                  render: v => (v || 0).toFixed(2),
                  sorter: (a, b) => (a.quantity || 0) - (b.quantity || 0)
                },
                { 
                  title: 'Full Tank', 
                  dataIndex: 'isFullTank', 
                  key: 'isFullTank', 
                  width: 100,
                  align: 'center',
                  render: v => (
                    <Tag color={v ? 'green' : 'orange'}>
                      {v ? 'Yes' : 'No'}
                    </Tag>
                  ),
                  filters: [
                    { text: 'Full Tank', value: true },
                    { text: 'Non-Full Tank', value: false }
                  ],
                  onFilter: (value, record) => record.isFullTank === value
                },
                { 
                  title: 'Location', 
                  dataIndex: 'location', 
                  key: 'location', 
                  width: 130
                },
                { 
                  title: 'Sub Location', 
                  dataIndex: 'subLocation', 
                  key: 'subLocation', 
                  width: 130
                },
                { 
                  title: 'Fuel Source', 
                  dataIndex: 'fuelSource', 
                  key: 'fuelSource', 
                  width: 130
                },
                { 
                  title: 'Meter Reading', 
                  dataIndex: 'meterReadingStr', 
                  key: 'meterReadingStr', 
                  width: 200
                }
              ]}
              dataSource={refuelBreakdownData.map((d, i) => ({ ...d, key: i }))}
              pagination={false}
              size="small"
              scroll={{ y: 400, x: 1000 }}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>-</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <strong>{refuelBreakdownData.reduce((sum, d) => sum + (d.quantity || 0), 0).toFixed(2)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="center">
                      <Tag color="green">{refuelBreakdownData.filter(d => d.isFullTank).length}</Tag>
                      <Tag color="orange">{refuelBreakdownData.filter(d => !d.isFullTank).length}</Tag>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>-</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>-</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>-</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>-</Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </>
        )}
        {refuelBreakdownData.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <p>No fuel entries available for this asset.</p>
          </div>
        )}
      </Modal>

      {/* Per-cycle refuel breakup: the refuel events behind one full-tank cycle's
          Fuel Consumed and Meter Run. */}
      <Modal
        title={<span><Icon type="profile" style={{ marginRight: 8, color: '#1890ff' }} />Refuel Breakup{cycleBreakupInfo ? ` - ${cycleBreakupInfo.assetCode}` : ''}</span>}
        visible={cycleBreakupVisible}
        onCancel={() => setCycleBreakupVisible(false)}
        footer={[<Button key="close" onClick={() => setCycleBreakupVisible(false)}>Close</Button>]}
        width={900}
      >
        {cycleBreakupInfo && (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                Cycle: <strong>{cycleBreakupInfo.previousDate}</strong> &rarr; <strong>{cycleBreakupInfo.currentDate}</strong>
              </div>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic title="Fuel Consumed (this cycle)" value={(cycleBreakupInfo.fuelConsumed || 0).toFixed(2)} suffix="L" />
                </Col>
                <Col span={8}>
                  <Statistic title="Meter Run" value={cycleBreakupInfo.meterRun != null ? cycleBreakupInfo.meterRun.toFixed(2) : '-'} suffix={cycleBreakupInfo.meterName || ''} />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Meter (Calc) Opening → Closing"
                    value={`${cycleBreakupInfo.previousMeterCalcReading ?? '-'} → ${cycleBreakupInfo.currentMeterCalcReading ?? '-'}`}
                    valueStyle={{ fontSize: 14 }}
                  />
                </Col>
              </Row>
              <div style={{ marginTop: 8, fontSize: 12, color: '#874d00', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4, padding: '6px 10px' }}>
                Fuel Consumed = intermediate refuels + current full tank. The opening (previous full tank) row shows the
                starting meter only; its fuel belongs to the prior cycle and is <strong>not counted</strong> here.
              </div>
            </div>
            <Table
              columns={[
                {
                  title: 'Role', key: 'role', width: 150,
                  render: (_, r) => {
                    if (r.role === 'opening') return <Tag color="blue">Opening (prev full tank)</Tag>;
                    if (r.role === 'closing') return <Tag color="green">Closing (full tank)</Tag>;
                    return <Tag color="orange">Intermediate</Tag>;
                  }
                },
                { title: 'Date', dataIndex: 'date', key: 'date', width: 140, sorter: (a, b) => a.date.localeCompare(b.date), defaultSortOrder: 'ascend' },
                { title: 'Voucher No', dataIndex: 'voucherNo', key: 'voucherNo', width: 110 },
                {
                  title: 'Fuel Added (L)', dataIndex: 'quantity', key: 'quantity', width: 120, align: 'right',
                  render: (v, r) => r.countedInConsumption
                    ? (v || 0).toFixed(2)
                    : <span style={{ color: '#bbb' }}>{(v || 0).toFixed(2)} (prior cycle)</span>
                },
                {
                  title: 'Full Tank', dataIndex: 'isFullTank', key: 'isFullTank', width: 90, align: 'center',
                  render: v => <Tag color={v ? 'green' : 'orange'}>{v ? 'Yes' : 'No'}</Tag>
                },
                { title: 'Meter Reading', dataIndex: 'meterReadingStr', key: 'meterReadingStr', width: 220 },
              ]}
              dataSource={cycleBreakupData.map((d, i) => ({ ...d, key: i }))}
              pagination={false}
              size="small"
              scroll={{ y: 360, x: 800 }}
            />
          </>
        )}
      </Modal>

      {/* Email Report Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
              <Icon type="mail" style={{ marginRight: 8 }} />
              Email Trend Analysis Report
            </span>
            {emailServerOnline ? (
              <Tag color="green" style={{ marginLeft: 16 }}>Server Online</Tag>
            ) : (
              <Tag color="red" style={{ marginLeft: 16 }}>Server Offline</Tag>
            )}
          </div>
        }
        visible={emailModalVisible}
        onCancel={() => {
          setEmailModalVisible(false);
          setSelectedUsers([]);
          setUserSearchText('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setEmailModalVisible(false);
            setSelectedUsers([]);
            setUserSearchText('');
          }}>
            Cancel
          </Button>,
          <Button
            key="download"
            icon="download"
            onClick={handleDownloadTrendPDF}
            disabled={sendingEmail}
          >
            Download PDF
          </Button>,
          <Button
            key="copyHtml"
            icon="copy"
            onClick={copyHtmlToClipboard}
            disabled={sendingEmail}
          >
            Copy HTML Email
          </Button>,
          <Button
            key="mailto"
            type="primary"
            icon="mail"
            loading={sendingEmail}
            disabled={selectedUsers.length === 0}
            onClick={handleEmailWithDownload}
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
          >
            Open Mail App
          </Button>
        ]}
        width={700}
      >
        {/* Email Subject Preview */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>Email Subject</div>
          <div style={{ background: '#e6f7ff', padding: 10, borderRadius: 4, border: '1px solid #91d5ff' }}>
            <Icon type="mail" style={{ marginRight: 8, color: '#1890ff' }} />
            {getEmailSubject()}
          </div>
        </div>

        {/* Selected Recipients Preview */}
        {selectedUsers.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 500, marginBottom: 8 }}>Recipients ({selectedUsers.length})</div>
            <div style={{ background: '#f6ffed', padding: 10, borderRadius: 4, border: '1px solid #b7eb8f', fontSize: 13 }}>
              <Icon type="team" style={{ marginRight: 8, color: '#52c41a' }} />
              {getRecipientEmails()}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>Report Details</div>
          <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ color: '#666', fontSize: 12 }}>Period</div>
                <div style={{ fontWeight: 500 }}>{selectedMonth.format('MMMM YYYY')}</div>
              </Col>
              <Col span={8}>
                <div style={{ color: '#666', fontSize: 12 }}>Total Assets</div>
                <div style={{ fontWeight: 500 }}>{filteredData.length}</div>
              </Col>
              <Col span={8}>
                <div style={{ color: '#666', fontSize: 12 }}>Critical/High</div>
                <div style={{ fontWeight: 500, color: filteredData.filter(d => d.severity && (d.severity.level === 'CRITICAL' || d.severity.level === 'HIGH')).length > 0 ? '#f5222d' : '#52c41a' }}>
                  {filteredData.filter(d => d.severity && (d.severity.level === 'CRITICAL' || d.severity.level === 'HIGH')).length}
                </div>
              </Col>
            </Row>
            {getActiveFilters().length > 0 && (
              <div style={{ marginTop: 12, borderTop: '1px solid #e8e8e8', paddingTop: 12 }}>
                <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>Active Filters</div>
                <div>
                  {getActiveFilters().map((filter, idx) => (
                    <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>
                      {filter.label}: {filter.value}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 500 }}>Select Recipients</span>
            <div>
              <Checkbox
                checked={selectedUsers.length === getFilteredUsers().length && getFilteredUsers().length > 0}
                indeterminate={selectedUsers.length > 0 && selectedUsers.length < getFilteredUsers().length}
                onChange={(e) => handleSelectAllUsers(e.target.checked)}
              >
                Select All ({getFilteredUsers().length})
              </Checkbox>
            </div>
          </div>
          <Input
            placeholder="Search users by name, email, or phone..."
            prefix={<Icon type="search" style={{ color: '#999' }} />}
            value={userSearchText}
            onChange={(e) => setUserSearchText(e.target.value)}
            style={{ marginBottom: 12 }}
            allowClear
          />
        </div>

        {loadingUsers ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="Loading users..." />
          </div>
        ) : organizationUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            <Icon type="user" style={{ fontSize: 32, marginBottom: 8 }} />
            <p>No users found in your organization</p>
          </div>
        ) : (
          <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e8e8e8', borderRadius: 4 }}>
            <List
              dataSource={getFilteredUsers()}
              renderItem={(user) => (
                <List.Item
                  style={{ 
                    padding: '8px 12px',
                    cursor: 'pointer',
                    background: selectedUsers.includes(user._id) ? '#e6f7ff' : 'transparent'
                  }}
                  onClick={() => handleUserSelect(user._id, !selectedUsers.includes(user._id))}
                >
                  <Checkbox
                    checked={selectedUsers.includes(user._id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleUserSelect(user._id, e.target.checked);
                    }}
                    style={{ marginRight: 12 }}
                  />
                  <List.Item.Meta
                    title={
                      <span>
                        {user.name}
                        {user.roles && <Tag style={{ marginLeft: 8, fontSize: 10 }}>{user.roles}</Tag>}
                      </span>
                    }
                    description={
                      <div style={{ fontSize: 12 }}>
                        <Icon type="mail" style={{ marginRight: 4 }} />
                        {user.email}
                        {user.phoneNumber && (
                          <span style={{ marginLeft: 16 }}>
                            <Icon type="phone" style={{ marginRight: 4 }} />
                            {user.phoneNumber}
                          </span>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}

        {getFilteredUsers().length > 0 && getFilteredUsers().length !== organizationUsers.length && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            Showing {getFilteredUsers().length} of {organizationUsers.length} users
          </div>
        )}
      </Modal>
    </div>
  );
}

export default MonthlyFullTankReport;
